import { applyOfferPlaceholders, normalizeOfferTemplateGreeting, tokenizeOfferRecipient } from '../../utils/offerTemplate';
import { systemMessage } from '../../utils/localizedSystemMessages';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { ref, get, push, set, update, onValue, off } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { database } from '../../firebase';
import StageSection from './StageSection';
import AddSubstageArea from './AddSubstageArea';
import OffersHub from './OffersHub';
import BulkInterviewScheduler from '../BulkInterviewScheduler';
import GeneratingAssessmentModal from '../Modals/GeneratingAssessmentModal';
import LiveInterviewQuestionGateModal from './LiveInterviewQuestionGateModal';
import {
  canonicalInterviewGoals,
  classifyInterviewQuestions,
  interviewGoalHash
} from '../Assessments/liveInterviewQuestionUtils';
import { updateApplicantPositionOptimized, loadAllJobOptions, updateCustomList, deleteCustomList, parseConnectionKey } from './DatabaseOptimizer';
import { buildEmployeeRecordFromHire } from '../../utils/employeeRecords';
import {
  employeeMatchesOnboardingCandidate,
  selectOnboardingEmployeeId
} from '../../utils/onboardingEmployeeIdentity';
import nationalizationService, {
  addNationalizationLifecycleEventToUpdates,
  announceNationalizationWorkforceChange,
  startNationalizationLifecycleReplay
} from '../../services/nationalizationService';
import {
  buildActorAnalyticsContext,
  normalizeAnalyticsSource
} from '../../utils/analyticsEntityFields';
import { syncCommunityMember, triggerCommunityAutoSync } from '../../services/communityService';
import { emitAtsAnalyticsEvent } from '../../services/atsAnalyticsService';
import { offerApi } from '../../services/offerService';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import useApprovalSettings from '../../hooks/useApprovalSettings';
import useTeamAccessProfile from '../../hooks/useTeamAccessProfile';

const COMPARE_RATING_TO_VALUE = {
  Poor: 1,
  'Below Average': 2,
  Average: 3,
  Good: 4,
  Excellent: 5
};

const COMPARE_SKILL_ALIASES = {
  react: 'React',
  reactjs: 'React',
  'react js': 'React',
  javascript: 'JavaScript',
  js: 'JavaScript',
  typescript: 'TypeScript',
  ts: 'TypeScript',
  node: 'Node.js',
  nodejs: 'Node.js',
  'node js': 'Node.js',
  nextjs: 'Next.js',
  'next js': 'Next.js',
  vue: 'Vue.js',
  vuejs: 'Vue.js',
  angular: 'Angular',
  angularjs: 'Angular',
  python: 'Python',
  django: 'Django',
  flask: 'Flask',
  aws: 'AWS',
  azure: 'Azure',
  gcp: 'GCP',
  docker: 'Docker',
  kubernetes: 'Kubernetes',
  postgresql: 'PostgreSQL',
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  mongodb: 'MongoDB',
  graphql: 'GraphQL',
  figma: 'Figma',
  salesforce: 'Salesforce',
  playright: 'Playwright',
  playwright: 'Playwright',
  cypress: 'Cypress',
  jest: 'Jest',
  terraform: 'Terraform',
  sql: 'SQL',
  excel: 'Excel',
  tableau: 'Tableau',
  powerbi: 'Power BI'
};

// Keep stable English department values in Firebase while translating only the
// employer-facing labels. This preserves existing reporting and access scopes.
const ONBOARDING_DEPARTMENT_OPTIONS = [
  ['Administration', 'administration'],
  ['Customer Support', 'customerSupport'],
  ['Engineering', 'engineering'],
  ['Product Management', 'productManagement'],
  ['Design / UX', 'designUx'],
  ['Data & Analytics', 'dataAnalytics'],
  ['Marketing', 'marketing'],
  ['Sales', 'sales'],
  ['Finance & Accounting', 'financeAccounting'],
  ['Human Resources', 'hr'],
  ['Operations', 'operations'],
  ['IT / Systems', 'itSystems'],
  ['Security / InfoSec', 'security'],
  ['Legal', 'legal'],
  ['Compliance', 'compliance'],
  ['Procurement', 'procurement'],
  ['Supply Chain', 'supplyChain'],
  ['Logistics', 'logistics'],
  ['Manufacturing', 'manufacturing'],
  ['Quality Assurance', 'qualityAssurance']
];

const COMPARE_ANALYSIS_HEADERS = [
  'Summary of Experience:',
  'Strong Points:',
  'Weak Points:',
  'Concerns:',
  'Global Standards Assessment:',
  'Verdict:',
  'Recommendation:',
  'Interview Questions:'
];

const ASSESSMENT_STEP_TYPES = new Set(['assessment', 'ai-assessment', 'ai-interview']);
const AI_EDITABLE_ASSESSMENT_STEP_TYPES = new Set(['ai-assessment', 'ai-interview']);
const isAssessmentStepType = (type) => ASSESSMENT_STEP_TYPES.has(type);
const isAiEditableAssessmentStepType = (type) => AI_EDITABLE_ASSESSMENT_STEP_TYPES.has(type);

const DEFAULT_OFFER_TERMS = {
  baseSalary: '',
  currency: 'USD',
  payFrequency: 'annual',
  bonus: '',
  commission: '',
  equity: '',
  signOnBonus: '',
  employmentType: 'Full-time',
  workMode: 'On-site',
  startDate: '',
  expiresAt: ''
};

const createDefaultOfferTerms = () => ({ ...DEFAULT_OFFER_TERMS });

const stringifySalaryValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.map(stringifySalaryValue).filter(Boolean).join(' - ');
  }
  if (typeof value === 'object') {
    const directValue = value.listedSalary
      || value.salary
      || value.salaryRange
      || value.compensationRange
      || value.payRange
      || value.range
      || value.amount
      || value.value
      || value.label;
    if (directValue) return stringifySalaryValue(directValue);

    const minValue = value.min ?? value.minimum ?? value.from;
    const maxValue = value.max ?? value.maximum ?? value.to;
    const range = [stringifySalaryValue(minValue), stringifySalaryValue(maxValue)].filter(Boolean).join(' - ');
    const currency = stringifySalaryValue(value.currency || value.currencyCode);
    const frequency = stringifySalaryValue(value.frequency || value.payFrequency || value.period);
    return [currency, range, frequency].filter(Boolean).join(' ').trim();
  }
  return '';
};

const getJobListedSalary = (...sources) => {
  const salaryKeys = [
    'listedSalary',
    'salary',
    'salaryRange',
    'compensationRange',
    'payRange',
    'compensation'
  ];

  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    const candidates = [source, source.data, source.position, source.job].filter(Boolean);
    for (const candidate of candidates) {
      for (const key of salaryKeys) {
        const salary = stringifySalaryValue(candidate?.[key]);
        if (salary) return salary;
      }
    }
  }

  return '';
};

const DEFAULT_OFFER_FOLLOW_UP_DAYS = [3, 6];

const DEFAULT_OFFER_FOLLOW_UP_SUBJECT = 'Reminder {{sequence}}: offer for {{job_title}} at {{company_name}}';

const buildDefaultOfferFollowUpMessage = (sequence = 1) => [
  uiText('<p>Hi {{candidate_first_name}},</p>'),
  uiText(sequence === 1 ? '<p>I wanted to follow up on the offer for {{job_title}} at {{company_name}}.</p>' : '<p>Just checking in again on the offer for {{job_title}} at {{company_name}}.</p>'),
  uiText('<p>Please review the details and let us know if you have any questions.</p>'),
  uiText('<p>Best regards,<br/>The {{company_name}} team</p>')
].join('');

const createDefaultOfferFollowUps = () => DEFAULT_OFFER_FOLLOW_UP_DAYS.map((day, index) => ({
  id: `day_${day}`,
  daysAfterSend: day,
  subject: uiText(DEFAULT_OFFER_FOLLOW_UP_SUBJECT).replace('{{sequence}}', String(index + 1)),
  messageHTML: buildDefaultOfferFollowUpMessage(index + 1)
}));

const dedupeCompareValues = (values) => [...new Set((Array.isArray(values) ? values : []).filter(Boolean))];

const normalizeCompareText = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const compareTitleCase = (value) => String(value || '')
  .split(' ')
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
  .join(' ');

const splitCompareList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  return value
    .split(/\n|•|- |,|;/)
    .map((item) => item.replace(/^[\s•-]+/, '').trim())
    .filter(Boolean);
};

const normalizeCompareSkillLabel = (value) => {
  const normalized = normalizeCompareText(value)
    .replace(/\b(advanced|intermediate|beginner|basic|expert|experienced|proficient)\b/g, ' ')
    .replace(/\b\d+\+?\s*(years|yrs|year)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return '';
  return COMPARE_SKILL_ALIASES[normalized] || compareTitleCase(normalized);
};

const normalizeCompareSkillValues = (values) => dedupeCompareValues(
  (Array.isArray(values) ? values : [values]).flatMap((value) => splitCompareList(value)
    .map((item) => item.replace(/\([^)]*\)/g, ' ').trim())
    .map(normalizeCompareSkillLabel)
    .filter(Boolean))
);

const intersectCompareValues = (leftValues, rightValues, limit = 8) => {
  const rightKeys = new Set((rightValues || []).map((value) => normalizeCompareText(value)).filter(Boolean));
  return dedupeCompareValues((leftValues || []).filter((value) => rightKeys.has(normalizeCompareText(value)))).slice(0, limit);
};

const firstNonEmptyArray = (...values) => values.find((value) => Array.isArray(value) && value.length > 0) || [];

const readFirstNumber = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const match = String(value).match(/(\d+(?:\.\d+)?)/);
    if (match) return Number(match[1]);
  }
  return null;
};

const normalizePercentScore = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  if (number <= 1) return Math.round(number * 100);
  if (number <= 10) return Math.round(number * 10);
  return Math.round(number);
};

const normalizeTenPointScore = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  if (number <= 1) return Number((number * 10).toFixed(1));
  if (number <= 10) return Number(number.toFixed(1));
  return Number((number / 10).toFixed(1));
};

const buildTeamRatingSummary = (scores) => {
  const ratings = Object.values(scores || {})
    .map((entry) => COMPARE_RATING_TO_VALUE[entry?.rating])
    .filter((value) => Number.isFinite(value));

  if (!ratings.length) {
    return { average: null, count: 0 };
  }

  const total = ratings.reduce((sum, value) => sum + value, 0);
  return {
    average: total / ratings.length,
    count: ratings.length
  };
};

const parseAnalysisSection = (text, headers) => {
  const source = String(text || '');
  const startPattern = headers.map((header) => header.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const allPattern = COMPARE_ANALYSIS_HEADERS.map((header) => header.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const regex = new RegExp(`(?:${startPattern})\\s*([\\s\\S]*?)(?=(?:${allPattern})|$)`, 'i');
  const match = source.match(regex);
  return match ? match[1].trim() : '';
};

const parseApplicantAnalysis = (analysis) => {
  if (!analysis) {
    return { summary: '', strengths: [], concerns: [], verdict: '' };
  }

  if (typeof analysis === 'object') {
    return {
      summary: analysis.summary || analysis.overall_summary || analysis.overallSummary || '',
      strengths: dedupeCompareValues(splitCompareList(
        analysis.strengths || analysis.strongPoints || analysis.strong_points || analysis.topStrengths
      )),
      concerns: dedupeCompareValues(splitCompareList(
        analysis.concerns || analysis.weakPoints || analysis.weak_points || analysis.improvement_areas
      )),
      verdict: analysis.verdict || analysis.recommendation || ''
    };
  }

  return {
    summary: parseAnalysisSection(analysis, ['Summary of Experience:']),
    strengths: dedupeCompareValues(splitCompareList(parseAnalysisSection(analysis, ['Strong Points:']))),
    concerns: dedupeCompareValues(splitCompareList(parseAnalysisSection(analysis, ['Weak Points:', 'Concerns:']))),
    verdict: parseAnalysisSection(analysis, ['Verdict:', 'Recommendation:'])
  };
};

const getLatestAssessmentEntry = (assessments) => {
  const entries = Object.entries(assessments || {})
    .map(([id, value]) => ({ id, ...(value || {}) }))
    .filter((entry) => entry && typeof entry === 'object');

  if (!entries.length) return null;

  return entries.sort((left, right) => {
    const leftUpdated = left?.evaluation?.updatedAt || left?.response?.submittedAt || left?.updatedAt || 0;
    const rightUpdated = right?.evaluation?.updatedAt || right?.response?.submittedAt || right?.updatedAt || 0;
    return rightUpdated - leftUpdated;
  })[0];
};

const extractJobSignals = (selectedJobData, selectedJobName) => {
  const source = selectedJobData?.data || selectedJobData || {};
  const title = source.jobOpening || source.title || selectedJobName || '';
  const department = source.department || '';
  const yearsRequired = readFirstNumber(source.experienceYears, source.requiredExperience);
  const textParts = [
    title,
    source.jobDescription,
    source.description,
    source.requirements,
    source.details
  ].filter(Boolean);

  const normalizedText = normalizeCompareText(textParts.join(' '));
  const explicitSkills = normalizeCompareSkillValues([
    ...(Array.isArray(source.skills) ? source.skills : []),
    ...(Array.isArray(source.requiredSkills) ? source.requiredSkills : []),
    ...(Array.isArray(source.tags) ? source.tags : [])
  ]);
  const inferredSkills = dedupeCompareValues(
    Object.entries(COMPARE_SKILL_ALIASES)
      .filter(([alias]) => normalizedText.includes(normalizeCompareText(alias)))
      .map(([, label]) => label)
  );

  return {
    title,
    department,
    yearsRequired,
    requiredSkills: dedupeCompareValues([...explicitSkills, ...inferredSkills]).slice(0, 12)
  };
};

const buildCompareRankLookup = (profiles, selector) => {
  const rankedProfiles = [...profiles]
    .map((profile) => ({ id: profile.id, value: selector(profile) }))
    .filter(({ value }) => Number.isFinite(value))
    .sort((left, right) => right.value - left.value);

  const ranks = {};
  let currentRank = 0;
  let previousValue = null;

  rankedProfiles.forEach((entry, index) => {
    if (previousValue === null || entry.value !== previousValue) {
      currentRank = index + 1;
      previousValue = entry.value;
    }
    ranks[entry.id] = currentRank;
  });

  return ranks;
};

const getCompareRecommendationMeta = (recommendation, t) => {
  if (recommendation === 'advance') {
    return {
      tone: 'advance',
      label: t('workspace.compareRecommendationAdvance')
    };
  }
  if (recommendation === 'hold') {
    return {
      tone: 'hold',
      label: t('workspace.compareRecommendationHold')
    };
  }
  return {
    tone: 'validate',
    label: t('workspace.compareRecommendationValidate')
  };
};

const CompareTagList = ({ items, tone = '', empty }) => {
  if (!items?.length) {
    return <div className="compare-empty-mini">{empty}</div>;
  }

  return (
    <div className="compare-tag-row">
      {items.map((item) => (
        <span key={item} className={`compare-tag ${tone}`.trim()}>{item}</span>
      ))}
    </div>
  );
};

const CompareMissingEvidence = ({ children }) => (
  <span className="compare-missing-evidence">
    <i className="fa-solid fa-circle-exclamation" />
    {children}
  </span>
);

const CompareRecommendationPill = ({ recommendation, t }) => {
  const meta = getCompareRecommendationMeta(recommendation, t);
  return <span className={`compare-recommendation-pill ${meta.tone}`}>{uiText(meta.label)}</span>;
};

const CompareCandidateScoreCard = ({ candidate, t }) => {
  const recommendation = getCompareRecommendationMeta(candidate.hiringRecommendation, t);
  const hasContact = Boolean(candidate.email || candidate.phone);
  const metrics = [
    {
      key: 'coverage',
      label: t('workspace.compareRoleCoverage'),
      value: candidate.skillCoverage !== null ? `${candidate.skillCoverage}%` : null,
      missing: t('workspace.compareNoJobSignals')
    },
    {
      key: 'experience',
      label: t('workspace.compareExperience'),
      value: candidate.yearsExperience !== null && candidate.yearsExperience !== undefined ? `${candidate.yearsExperience}y` : null,
      missing: t('workspace.compareEvidenceMissing')
    },
    {
      key: 'assessment',
      label: t('workspace.compareAssessment'),
      value: candidate.assessmentScore !== null && candidate.assessmentScore !== undefined ? `${candidate.assessmentScore}/10` : null,
      missing: t('workspace.compareMissingAssessmentImpact')
    },
    {
      key: 'team',
      label: t('workspace.compareTeamSignal'),
      value: candidate.teamRatingAverage !== null && candidate.teamRatingAverage !== undefined ? `${candidate.teamRatingAverage.toFixed(1)}/5` : null,
      missing: t('workspace.compareMissingTeamImpact')
    }
  ];

  return (
    <article className={`compare-candidate-card ${recommendation.tone}`}>
      <div className="compare-candidate-card-top">
        <div>
          <span className="compare-candidate-stage">{candidate.stageName}</span>
          <h4>{candidate.name}</h4>
          <p>{candidate.currentRole || t('workspace.compareRoleMissing')}</p>
          <p>{candidate.currentCompany || t('workspace.compareCompanyMissing')}</p>
        </div>
        <div className="compare-fit-badge">
          <strong>{candidate.fitScorePercent !== null && candidate.fitScorePercent !== undefined ? `${candidate.fitScorePercent}%` : '-'}</strong>
          <span>{t('workspace.compareFitScore')}</span>
        </div>
      </div>

      <div className="compare-card-recommendation">
        <CompareRecommendationPill recommendation={candidate.hiringRecommendation} t={t} />
        <p>{candidate.decisionRationale || candidate.positioningSummary || t('workspace.compareDecisionRationaleFallback')}</p>
      </div>

      <div className="compare-card-metrics">
        {metrics.map((metric) => (
          <div key={metric.key} className="compare-card-metric">
            <span>{uiText(metric.label)}</span>
            {metric.value ? <strong>{metric.value}</strong> : <CompareMissingEvidence>{metric.missing}</CompareMissingEvidence>}
          </div>
        ))}
        <div className="compare-card-metric">
          <span>{t('workspace.compareContact')}</span>
          <strong className={hasContact ? 'compare-ready-text' : 'compare-risk-text'}>
            {hasContact ? t('workspace.compareContactReady') : t('workspace.compareContactMissing')}
          </strong>
        </div>
      </div>
    </article>
  );
};

const CompareDecisionMatrix = ({ candidates, rows, t }) => (
  <section className="compare-section-card">
    <div className="compare-section-heading">
      <span>{t('workspace.compareStructuredDecision')}</span>
      <h4>{t('workspace.compareDecisionMatrixHeader')}</h4>
      <p>{t('workspace.compareDecisionMatrixSubtitle')}</p>
    </div>

    <div className="compare-decision-matrix">
      <div className="compare-matrix-row compare-matrix-header-row">
        <div className="compare-matrix-label">
          <strong>{t('workspace.compareHiringRecommendation')}</strong>
          <span>{t('workspace.compareEvidenceQuality')}</span>
        </div>
        {candidates.map((candidate) => (
          <div key={`matrix-header-${candidate.id}`} className="compare-matrix-cell compare-matrix-candidate-head">
            <strong>{candidate.name}</strong>
            <span>{candidate.currentRole || t('workspace.compareRoleMissing')}</span>
          </div>
        ))}
      </div>
      {rows.map((row) => (
        <div key={row.key} className="compare-matrix-row">
          <div className="compare-matrix-label">
            <strong>{uiText(row.label)}</strong>
            <span>{row.description}</span>
          </div>
          {candidates.map((candidate) => (
            <div key={`${row.key}-${candidate.id}`} className="compare-matrix-cell">
              {row.render(candidate)}
            </div>
          ))}
        </div>
      ))}
    </div>
  </section>
);

const CompareEvidenceCard = ({ candidate, t }) => (
  <article className="compare-evidence-card">
    <div className="compare-evidence-card-head">
      <div>
        <span>{candidate.currentRole || t('workspace.compareRoleMissing')}</span>
        <h4>{candidate.name}</h4>
      </div>
      <span className={`compare-evidence-pill ${candidate.evidenceTone}`}>{candidate.evidenceLabel}</span>
    </div>

    <div className="compare-evidence-block">
      <h5>{t('workspace.compareMatchedRequirements')}</h5>
      <CompareTagList items={candidate.matchedSkills} tone="success" empty={t('workspace.compareNoMatchedSkills')} />
    </div>

    <div className="compare-evidence-block">
      <h5>{t('workspace.compareCoverageGaps')}</h5>
      <CompareTagList items={candidate.missingSkills} tone="warning" empty={t('workspace.compareNoMissingSkills')} />
    </div>

    <div className="compare-evidence-block">
      <h5>{t('workspace.compareUniqueEdge')}</h5>
      <CompareTagList items={candidate.uniqueEdge} empty={t('workspace.compareNoUniqueEdge')} />
    </div>

    <div className="compare-evidence-block">
      <h5>{t('workspace.compareWatchouts')}</h5>
      <CompareTagList items={candidate.businessRisks?.length ? candidate.businessRisks : candidate.watchouts} tone="warning" empty={t('workspace.compareNoWatchouts')} />
    </div>

    <div className="compare-evidence-copy">
      <span>{t('workspace.compareRecruiterRead')}</span>
      <p>{candidate.summary || t('workspace.compareNoSummary')}</p>
    </div>

    <div className="compare-evidence-copy emphasis">
      <span>{t('workspace.compareRecommendedNextStep')}</span>
      <p>{candidate.recommendedAction || t('workspace.compareRecommendedDefault')}</p>
    </div>

    <div className="compare-evidence-block">
      <h5>{t('workspace.compareInterviewFocus')}</h5>
      <CompareTagList items={candidate.interviewFocus} tone="info" empty={t('workspace.compareNoInterviewFocus')} />
    </div>
  </article>
);

const ApplicantsWorkspace = forwardRef((props, forwardedRef) => {
  const { t } = useTranslation('applicants');
  const translateSubstageName = (stepId, fallback) => {
    switch (stepId) {
      case 'all-applicants-1':
        return t('substages.allApplicants');
      case 'filter-applicants':
        return t('substages.fitApplicants');
      case 'offer-sent':
        return t('substages.offerSent');
      case 'hired-applicants':
        return t('substages.hired');
      case 'move-to-onboarding':
        return t('substages.moveToOnboarding');
      default:
        return fallback || stepId;
    }
  };
  const { isRTL } = useLanguage();
  const {
    jobs,
    selectedJobName,
    selectedJobData,
    isFilterJobArea,
    currActiveStage,
    onSelectJob,
    onToggleStage,
    onAddFilteringToJob,
    onOpenFilteringSubstagePopup,
    onOpenApplicantPopup,
    onOpenAddSubstagePopup,
    userUID,
    sortedApplicantsData,
    currActiveJob,
    exampleApplicants,
    dynamicSubstages,
    onRemoveFilterSubstage,
    onOpenSubstagePopup,
    onOpenGlobalSearch,
    onOpenNotifications,
    notificationCount,
    onCheckAndPromptForLink,
    onScheduleInterviewBeforeDrop,
    onOpenCVUpload,
    selectedApplicants = [],
    onClearSelection,
    pendingApprovals = [],
    canManageApplicants: canManageApplicantsProp,
    jobId,
    onSubstageUpdated,
    onSubstageDeleted,
    onMultipleApplicantsRemoved,
  } = props;

  // Get current user for approval submissions
  const { currentUser } = useAuth();
  const teamAccess = useTeamAccessProfile(userUID);

  // Get approval settings for offer approval interception
  const { approvalSettings, userPermissions } = useApprovalSettings(userUID);
  const canDraftOffer = teamAccess.isCompanyOwner || userPermissions.canDraftOffers || userPermissions.canApproveOffers;
  const canDirectSendOffer = teamAccess.isCompanyOwner || userPermissions.canApproveOffers;
  const canSendOfferToCandidate = !approvalSettings.offerLetters || canDirectSendOffer;
  const canReviewOffer = teamAccess.isCompanyOwner || userPermissions.canReviewOffers || userPermissions.canApprove;
  const canDeliverLifecycleEvents = teamAccess.isCompanyOwner
    || teamAccess.userPermissions.canManageEmployees
    || teamAccess.userPermissions.canManageOnboarding
    || teamAccess.userPermissions.canManageApplicants
    || teamAccess.userPermissions.canManageNationalization;

  useEffect(() => {
    if (!userUID || teamAccess.loading || !canDeliverLifecycleEvents) return undefined;
    return startNationalizationLifecycleReplay(userUID);
  }, [canDeliverLifecycleEvents, teamAccess.loading, userUID]);
  const mustSubmitForApproval = approvalSettings.offerLetters && !canDirectSendOffer;
  const canManageApplicants = canManageApplicantsProp ?? (teamAccess.isCompanyOwner || teamAccess.userPermissions.canManageApplicants);
  const canAccessOffersHub = teamAccess.isCompanyOwner
    || userPermissions.canDraftOffers
    || userPermissions.canApproveOffers
    || userPermissions.canReviewOffers
    || userPermissions.canApprove;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [applicantsData, setApplicantsData] = useState([]);
  const [applicantStatusMap, setApplicantStatusMap] = useState({});
  const [statusRefreshKey, setStatusRefreshKey] = useState(0);
  const statusFetchJobRef = useRef(null);
  const [jobOptions, setJobOptions] = useState([]);
  const [isJobOptionsLoading, setIsJobOptionsLoading] = useState(() => !(Array.isArray(jobs) && jobs.length > 0));
  const [showEditCustomList, setShowEditCustomList] = useState(false);
  const [editingListId, setEditingListId] = useState(null);
  const [editListTitle, setEditListTitle] = useState('');
  const [editListDescription, setEditListDescription] = useState('');
  const [editListJobDescription, setEditListJobDescription] = useState('');
  const [editListTargetHireDate, setEditListTargetHireDate] = useState('');

  const translateStaticStepName = useCallback((stepId) => {
    switch (stepId) {
      case 'all-applicants-1':
        return t('substages.allApplicants');
      case 'filter-applicants':
        return t('substages.fitApplicants');
      case 'offer-sent':
        return t('substages.offerSent');
      case 'hired-applicants':
        return t('substages.hired');
      case 'move-to-onboarding':
        return t('substages.moveToOnboarding');
      case 'background-checks':
        return t('substages.backgroundChecks');
      case 'orientation':
        return t('substages.orientation');
      default:
        return null;
    }
  }, [t]);

  const formatOptionSubtitle = (option) => {
    if (option.type === 'posted_job') {
      return t('workspace.postedJob');
    }
    if (option.type === 'custom_list') {
      const count = option.data?.applicantCount ?? option.applicantCount ?? 0;
      return t('workspace.customListWithCount', { count });
    }
    return option.subtitle || '';
  };

  // View mode state: 'board', 'list', 'gallery'
  const [viewMode, setViewMode] = useState('board');
  const MAX_COMPARE_CANDIDATES = 2;
  const [listSelectedApplicantIds, setListSelectedApplicantIds] = useState([]);
  const [compareApplicantIds, setCompareApplicantIds] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareApplicantDetails, setCompareApplicantDetails] = useState({});
  const [isCompareLoading, setIsCompareLoading] = useState(false);
  const [compareAIAnalysis, setCompareAIAnalysis] = useState(null);
  const [isCompareAILoading, setIsCompareAILoading] = useState(false);
  const compareAnalysisFetchedKeyRef = useRef(null);
  const compareRangeAnchorRef = useRef(null);
  const compareDragSelectionRef = useRef(null);
  const [selectionActionMenuPosition, setSelectionActionMenuPosition] = useState(null);
  const [isSelectionActionLoading, setIsSelectionActionLoading] = useState(false);
  const [showCompareSelectionPicker, setShowCompareSelectionPicker] = useState(false);
  const [compareDraftSelectionIds, setCompareDraftSelectionIds] = useState([]);
  const [comparePickerSearch, setComparePickerSearch] = useState('');

  // Gallery view sorting state
  const [gallerySortBy, setGallerySortBy] = useState(''); // '', 'date', 'updated', 'step'
  const [gallerySortOrder, setGallerySortOrder] = useState('desc'); // 'asc', 'desc'
  const [galleryGroupByStep, setGalleryGroupByStep] = useState(false); // Group by workflow step
  const [expandedStages, setExpandedStages] = useState({}); // Track which stages are expanded

  // Gallery filters
  const [galleryFilterBy, setGalleryFilterBy] = useState(''); // '', 'step', 'applied_last_7', 'applied_over_7', 'starred'
  const [galleryFilterStep, setGalleryFilterStep] = useState('');

  // Local overrides for substage titles when renaming inline in grouped gallery
  const [substageTitleOverrides, setSubstageTitleOverrides] = useState({}); // { substageId: newTitle }
  const [editingSubstageId, setEditingSubstageId] = useState('');
  const [editingSubstageTitle, setEditingSubstageTitle] = useState('');

  // Undo toast state
  const [showUndoToast, setShowUndoToast] = useState(false);
  const [undoData, setUndoData] = useState(null);
  const undoTimeoutRef = useRef(null);
  const boardRef = useRef(null);
  const dragScrollFrameRef = useRef(null);

  // Bulk Interview Scheduling state
  const [showBulkInterviewScheduler, setShowBulkInterviewScheduler] = useState(false);
  const [bulkInterviewApplicants, setBulkInterviewApplicants] = useState([]);
  const [bulkInterviewSubstageData, setBulkInterviewSubstageData] = useState(null);

  // Bulk Assessment Sending state
  const [isSendingBulkAssessments, setIsSendingBulkAssessments] = useState(false);
  const [bulkAssessmentCount, setBulkAssessmentCount] = useState(0);

  // On-demand job assessment generation state
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [generatingJobTitle, setGeneratingJobTitle] = useState('');
  const [liveQuestionGate, setLiveQuestionGate] = useState(null);
  const navigate = useNavigate();

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toastTimeoutRef = useRef(null);

  // Onboarding Transition state
  const [showOnboardingTransition, setShowOnboardingTransition] = useState(false);
  const [onboardingApplicant, setOnboardingApplicant] = useState(null);
  const [onboardingForm, setOnboardingForm] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
    startDate: '',
    manager: '',
    buddyMentor: ''
  });
  const [onboardingOfferContext, setOnboardingOfferContext] = useState(null);
  const [onboardingEstablishments, setOnboardingEstablishments] = useState([]);
  const [onboardingEstablishmentId, setOnboardingEstablishmentId] = useState('');
  const [companyName, setCompanyName] = useState(() => {
    try {
      return localStorage.getItem('companyName') || '';
    } catch (error) {
      return '';
    }
  });

  useEffect(() => {
    if (!showOnboardingTransition || !userUID) return undefined;
    const controller = new AbortController();
    nationalizationService.getOperationalContext(userUID, { signal: controller.signal })
      .then((result) => {
        const establishments = Array.isArray(result?.establishments) ? result.establishments : [];
        setOnboardingEstablishments(establishments);
        setOnboardingEstablishmentId((current) => (
          establishments.some(({ id }) => id === current)
            ? current
            : establishments.length === 1 ? establishments[0].id : ''
        ));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          // Never guess a branch. The lifecycle marker remains pending when the
          // operational context cannot be loaded.
          console.warn('Legal establishment choices could not be loaded:', error);
        }
      });
    return () => controller.abort();
  }, [showOnboardingTransition, userUID]);

  // Offer email modal state
  const [showOfferEmailModal, setShowOfferEmailModal] = useState(false);
  const [offerReviewMode, setOfferReviewMode] = useState(false);
  const [reviewApprovalData, setReviewApprovalData] = useState(null);
  const [offerModalApplicants, setOfferModalApplicants] = useState([]);
  const [offerTargetStage, setOfferTargetStage] = useState('stage-1');
  const [offerTargetSubstage, setOfferTargetSubstage] = useState('offer-sent');
  const [offerSubject, setOfferSubject] = useState('');
  const [offerSending, setOfferSending] = useState(false);
  const [offerActionMode, setOfferActionMode] = useState('');
  const [offerAttachments, setOfferAttachments] = useState([]);
  const [activeOfferId, setActiveOfferId] = useState('');
  const [offerTerms, setOfferTerms] = useState(createDefaultOfferTerms);
  const [offerContextListedSalary, setOfferContextListedSalary] = useState('');
  const [offerFollowUpsEnabled, setOfferFollowUpsEnabled] = useState(true);
  const [offerFollowUps, setOfferFollowUps] = useState(createDefaultOfferFollowUps);
  const [offerTemplate, setOfferTemplate] = useState(null);
  const [offerApprovalNote, setOfferApprovalNote] = useState('');
  const [offerRejectionReason, setOfferRejectionReason] = useState('');
  const [showOffersHub, setShowOffersHub] = useState(false);
  const [offersHubItems, setOffersHubItems] = useState([]);
  const [offersHubLoading, setOffersHubLoading] = useState(false);
  const [offersHubActionBusy, setOffersHubActionBusy] = useState('');
  const offerEditorRef = useRef(null);
  const offerQuillRef = useRef(null);
  const offerFollowUpEditorRefs = useRef({});
  const offerFollowUpQuillRefs = useRef({});
  const [offerMessageHTML, setOfferMessageHTML] = useState('');
  const offerResetRef = useRef(false);
  const offerBusy = offerSending || Boolean(offerActionMode);
  const offerFieldsReadOnly = offerReviewMode && !canReviewOffer;
  const offerPrimaryLabel = offerActionMode === 'submitting'
    ? t('workspace.offerModal.submitting', 'Submitting...')
    : offerActionMode === 'sending'
      ? t('workspace.offerModal.sending', 'Sending...')
      : mustSubmitForApproval
        ? t('workspace.offerModal.submitForApproval', 'Submit for approval')
        : approvalSettings.offerLetters && canDirectSendOffer
          ? t('workspace.offerModal.sendNow', 'Send offer now')
          : t('workspace.offerModal.sendOffer', 'Send offer');
  const offerReviewApproveLabel = offerActionMode === 'approving'
    ? t('workspace.offerModal.approving', 'Approving...')
    : t('workspace.offerModal.approveAndSend', 'Approve & send');
  const offerReviewRejectLabel = offerActionMode === 'rejecting'
    ? t('workspace.offerModal.rejecting', 'Rejecting...')
    : t('workspace.offerModal.reject', 'Reject');
  const offerFooterHelper = mustSubmitForApproval
    ? t('workspace.offerModal.submitApprovalHelper', 'This will not be sent to the candidate until an approver approves it.')
    : t('workspace.offerModal.directSendHelper', 'This sends the offer to the candidate now.');
  // Load all job options (posted jobs + custom lists)
  const loadJobOptions = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!userUID) {
      setJobOptions([]);
      setIsJobOptionsLoading(false);
      return;
    }

    setIsJobOptionsLoading(true);
    try {
      const options = await loadAllJobOptions(userUID, { forceRefresh });
      setJobOptions(options);
    } catch (error) {
      console.error('Error loading job options:', error);
      setJobOptions([]);
    } finally {
      setIsJobOptionsLoading(false);
    }
  }, [userUID]);

  useEffect(() => {
    if (jobs && jobs.length > 0) {
      setJobOptions(jobs);
      setIsJobOptionsLoading(false);
      return;
    }

    if (userUID) {
      loadJobOptions();
      return;
    }

    setJobOptions([]);
    setIsJobOptionsLoading(false);
  }, [jobs, userUID, loadJobOptions]);

  const visibleJobOptions = useMemo(
    () => teamAccess.filterByDepartmentAccess(jobOptions, (option) => option?.data?.department || option?.department),
    [jobOptions, teamAccess]
  );

  useEffect(() => {
    if (!userUID || !canAccessOffersHub) {
      setOffersHubItems([]);
      setOffersHubLoading(false);
      return undefined;
    }

    setOffersHubLoading(true);
    const offersRef = ref(database, `companies/${userUID}/offers`);
    const unsubscribe = onValue(
      offersRef,
      (snapshot) => {
        const offers = [];
        if (snapshot.exists()) {
          snapshot.forEach((child) => {
            const offer = child.val() || {};
            offers.push({
              ...offer,
              offerId: offer.offerId || child.key
            });
          });
        }
        setOffersHubItems(offers);
        setOffersHubLoading(false);
      },
      (error) => {
        console.error('Failed to load offers:', error);
        setOffersHubItems([]);
        setOffersHubLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userUID, canAccessOffersHub]);

  useEffect(() => {
    if (!userUID) {
      setOfferTemplate(null);
      return;
    }

    let cancelled = false;
    get(ref(database, `companies/${userUID}/settings/offerTemplate`))
      .then((snapshot) => {
        if (cancelled) return;
        setOfferTemplate(snapshot.exists() ? (snapshot.val() || null) : null);
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Failed to load offer template:', error);
          setOfferTemplate(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userUID]);

  const pendingApprovalByOfferId = useMemo(() => {
    const map = new Map();
    (Array.isArray(pendingApprovals) ? pendingApprovals : []).forEach((approval) => {
      const offerId = approval?.metadata?.offerId;
      if (offerId) {
        map.set(offerId, approval);
      }
    });
    return map;
  }, [pendingApprovals]);

  const visibleOffersHubItems = useMemo(
    () => teamAccess.filterByDepartmentAccess(
      offersHubItems,
      (offer) => offer?.job?.department || offer?.department
    ),
    [offersHubItems, teamAccess]
  );

  // Load company name for offer emails and defaults
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (!userUID) return;
      try {
        const cachedName = localStorage.getItem('companyName') || '';
        if (cachedName) {
          setCompanyName(cachedName);
        }

        const companySnap = await get(ref(database, `companies/${userUID}`));
        if (companySnap.exists()) {
          const companyData = companySnap.val() || {};
          const name = companyData.companyName || companyData.name || cachedName || '';
          if (name) {
            setCompanyName(name);
            localStorage.setItem('companyName', name);
          }
        }
      } catch (error) {
        console.error('Error loading company name:', error);
      }
    };
    fetchCompanyName();
  }, [userUID]);

  const showTransientToast = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);

    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    toastTimeoutRef.current = setTimeout(() => {
      setShowToast(false);
      setToastMessage('');
    }, 5000);
  }, []);

  const getApplicantStageName = useCallback((applicant) => {
    if (!applicant) return t('workspace.unknownStage');
    if (applicant.substageId === 'all-applicants-1') return t('substages.allApplicants');
    if (applicant.substageId === 'filter-applicants') return t('substages.fitApplicants');
    if (applicant.substageId === 'offer-sent') return t('substages.offerSent');
    if (applicant.substageId === 'hired-applicants') return t('substages.hired');
    if (applicant.substageId === 'move-to-onboarding') return t('substages.moveToOnboarding');
    if (applicant.substageId === 'background-checks') return translateStaticStepName('background-checks') || t('workspace.unknownStage');
    if (applicant.substageId === 'orientation') return translateStaticStepName('orientation') || t('workspace.unknownStage');

    const foundSubstage = Object.values(dynamicSubstages || {}).flat().find((substage) => substage.id === applicant.substageId);
    return foundSubstage ? (foundSubstage.title || foundSubstage.name || applicant.substageId) : (applicant.substageId || t('workspace.unknownStage'));
  }, [dynamicSubstages, t, translateStaticStepName]);

  const listViewApplicants = useMemo(() => {
    let filteredApplicants = [...applicantsData];

    if (galleryFilterBy === 'starred') {
      filteredApplicants = filteredApplicants.filter((applicant) => applicant.starred === true);
    } else if (galleryFilterBy === 'applied_last_7') {
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      filteredApplicants = filteredApplicants.filter((applicant) => (applicant.createdAt || 0) >= cutoff);
    } else if (galleryFilterBy === 'applied_over_7') {
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      filteredApplicants = filteredApplicants.filter((applicant) => (applicant.createdAt || 0) < cutoff);
    } else if (galleryFilterBy === 'step' && galleryFilterStep) {
      filteredApplicants = filteredApplicants.filter((applicant) => applicant.substageId === galleryFilterStep);
    }

    const getStepOrder = (substageId) => {
      const orderedSteps = ['all-applicants-1'];
      const filterSubstage = dynamicSubstages['stage-1']?.find((substage) => substage.id === 'filter-applicants');
      if (filterSubstage) {
        orderedSteps.push(filterSubstage.id);
      }

      const stage1Dynamic = dynamicSubstages['stage-1']?.filter((substage) => substage.id !== 'filter-applicants') || [];
      stage1Dynamic.forEach((substage) => orderedSteps.push(substage.id));
      orderedSteps.push('offer-sent', 'hired-applicants', 'move-to-onboarding');
      orderedSteps.push('background-checks');
      (dynamicSubstages['stage-2'] || []).forEach((substage) => orderedSteps.push(substage.id));
      orderedSteps.push('orientation');
      (dynamicSubstages['stage-3'] || []).forEach((substage) => orderedSteps.push(substage.id));
      return orderedSteps.indexOf(substageId);
    };

    return [...filteredApplicants].sort((left, right) => getStepOrder(left.substageId) - getStepOrder(right.substageId));
  }, [applicantsData, galleryFilterBy, galleryFilterStep, dynamicSubstages]);

  const compareJobSignals = useMemo(
    () => extractJobSignals(selectedJobData, selectedJobName),
    [selectedJobData, selectedJobName]
  );

  useEffect(() => {
    setListSelectedApplicantIds((prev) => prev.filter((id) => applicantsData.some((applicant) => applicant.id === id)));
    setCompareApplicantIds((prev) => prev.filter((id) => applicantsData.some((applicant) => applicant.id === id)));
    setCompareApplicantDetails((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!applicantsData.some((applicant) => applicant.id === id)) {
          delete next[id];
        }
      });
      return next;
    });
  }, [applicantsData]);

  // Fetch interview + assessment statuses for all visible applicants (non-blocking, background)
  useEffect(() => {
    if (!userUID || !applicantsData.length) return;

    const fetchStatuses = async () => {
      const map = {};

      await Promise.all(applicantsData.map(async (applicant) => {
        const status = {};

        // Interview status — use dateTime ISO field for reliable comparison
        try {
          const snap = await get(ref(database, `companies/${userUID}/interviews/${applicant.id}`));
          if (snap.exists()) {
            const all = Object.values(snap.val());
            const now = Date.now();
            const upcoming = all.find(iv => {
              if (iv.status === 'cancelled') return false;
              try {
                const ts = iv.dateTime ? new Date(iv.dateTime).getTime() : new Date(`${iv.date}T${iv.time}`).getTime();
                return ts > now;
              } catch { return false; }
            });
            const past = !upcoming && all.some(iv => {
              if (iv.status === 'cancelled') return false;
              try {
                const ts = iv.dateTime ? new Date(iv.dateTime).getTime() : new Date(`${iv.date}T${iv.time}`).getTime();
                return ts <= now;
              } catch { return false; }
            });
            if (upcoming) {
              status.interviewScheduled = { date: upcoming.date, time: upcoming.time };
            } else if (past) {
              status.interviewComplete = true;
            }
          }
        } catch (_) {}

        // Assessment status — keyed by assessmentId; presence of the entry = awaiting (no sentAt needed)
        if (applicant.connectionKey) {
          try {
            const snap = await get(ref(database, `connections/${applicant.connectionKey}/assessments`));
            if (snap.exists()) {
              const byId = {};
              for (const [assessmentId, entry] of Object.entries(snap.val())) {
                const rs = entry?.response?.status;
                if (rs === 'completed' || rs === 'evaluated' || rs === 'manual_review' || entry?.evaluation?.result) {
                  byId[assessmentId] = 'available';
                } else {
                  byId[assessmentId] = 'awaiting';
                }
              }
              status.assessmentsByStep = byId;
            }
          } catch (_) {}
        }

        map[applicant.id] = status;
      }));

      setApplicantStatusMap(map);
    };

    fetchStatuses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userUID, currActiveJob, applicantsData.length, statusRefreshKey]);

  // Reset status cache when job changes
  useEffect(() => {
    statusFetchJobRef.current = null;
    setApplicantStatusMap({});
  }, [currActiveJob]);

  useEffect(() => {
    if (compareRangeAnchorRef.current && !listViewApplicants.some((applicant) => applicant.id === compareRangeAnchorRef.current)) {
      compareRangeAnchorRef.current = null;
    }
  }, [listViewApplicants]);

  useEffect(() => {
    if (!listSelectedApplicantIds.length) {
      compareRangeAnchorRef.current = null;
      compareDragSelectionRef.current = null;
    }
  }, [listSelectedApplicantIds.length]);

  useEffect(() => {
    if (showCompareModal && compareApplicantIds.length < 2) {
      setShowCompareModal(false);
    }
  }, [compareApplicantIds.length, showCompareModal]);

  useEffect(() => {
    if (!showCompareModal) {
      setCompareAIAnalysis(null);
      setIsCompareAILoading(false);
      compareAnalysisFetchedKeyRef.current = null;
    }
  }, [showCompareModal]);

  useEffect(() => {
    const stopCompareDrag = () => {
      compareDragSelectionRef.current = null;
    };

    window.addEventListener('mouseup', stopCompareDrag);
    return () => {
      window.removeEventListener('mouseup', stopCompareDrag);
    };
  }, []);

  const toggleListApplicantSelection = useCallback((applicantId, options = {}) => {
    const { shiftKey = false, forceSelected } = options;

    setListSelectedApplicantIds((prev) => {
      const isSelected = prev.includes(applicantId);
      const shouldSelect = typeof forceSelected === 'boolean' ? forceSelected : !isSelected;
      const visibleApplicantIds = listViewApplicants.map((applicant) => applicant.id);
      const anchorId = compareRangeAnchorRef.current;

      if (shiftKey && anchorId && visibleApplicantIds.includes(anchorId) && visibleApplicantIds.includes(applicantId)) {
        const startIndex = visibleApplicantIds.indexOf(anchorId);
        const endIndex = visibleApplicantIds.indexOf(applicantId);
        const rangeIds = visibleApplicantIds.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1);

        compareRangeAnchorRef.current = applicantId;

        if (!shouldSelect) {
          return prev.filter((id) => !rangeIds.includes(id));
        }

        const next = [...prev];
        rangeIds.forEach((id) => {
          if (!next.includes(id)) {
            next.push(id);
          }
        });
        return next;
      }

      compareRangeAnchorRef.current = applicantId;

      if (!shouldSelect) {
        return isSelected ? prev.filter((id) => id !== applicantId) : prev;
      }

      return isSelected ? prev : [...prev, applicantId];
    });
  }, [listViewApplicants]);

  const handleApplicantSelectionMouseDown = useCallback((event, applicantId, isSelected) => {
    if (event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    const forceSelected = !isSelected;

    if (event.shiftKey) {
      toggleListApplicantSelection(applicantId, { shiftKey: true, forceSelected });
      return;
    }

    compareDragSelectionRef.current = {
      forceSelected,
      touchedIds: new Set([applicantId])
    };
    toggleListApplicantSelection(applicantId, { forceSelected });
  }, [toggleListApplicantSelection]);

  const handleApplicantSelectionDragEnter = useCallback((applicantId) => {
    const dragSelection = compareDragSelectionRef.current;
    if (!dragSelection || dragSelection.touchedIds.has(applicantId)) {
      return;
    }

    dragSelection.touchedIds.add(applicantId);
    toggleListApplicantSelection(applicantId, { forceSelected: dragSelection.forceSelected });
  }, [toggleListApplicantSelection]);

  const handleApplicantSelectionKeyDown = useCallback((event, applicantId, isSelected) => {
    if (event.key !== ' ' && event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    toggleListApplicantSelection(applicantId, {
      shiftKey: event.shiftKey,
      forceSelected: !isSelected
    });
  }, [toggleListApplicantSelection]);

  const comparedApplicants = useMemo(
    () => compareApplicantIds
      .map((id) => applicantsData.find((applicant) => applicant.id === id))
      .filter(Boolean),
    [compareApplicantIds, applicantsData]
  );

  const activeActionSelectionIds = useMemo(() => {
    if (viewMode === 'list') {
      return listSelectedApplicantIds;
    }
    if (viewMode === 'board') {
      return selectedApplicants;
    }
    return [];
  }, [listSelectedApplicantIds, selectedApplicants, viewMode]);

  const activeActionSelectionApplicants = useMemo(
    () => activeActionSelectionIds
      .map((id) => applicantsData.find((applicant) => applicant.id === id))
      .filter(Boolean),
    [activeActionSelectionIds, applicantsData]
  );

  const comparePickerApplicants = useMemo(() => {
    const selectedSet = new Set(activeActionSelectionIds);
    const visibleApplicants = viewMode === 'list' ? listViewApplicants : applicantsData;
    const seenApplicantIds = new Set();

    return visibleApplicants
      .filter((applicant) => {
        if (!applicant?.id || seenApplicantIds.has(applicant.id)) {
          return false;
        }

        seenApplicantIds.add(applicant.id);
        return true;
      })
      .sort((left, right) => {
        const leftSelected = selectedSet.has(left.id);
        const rightSelected = selectedSet.has(right.id);

        if (leftSelected !== rightSelected) {
          return leftSelected ? -1 : 1;
        }

        const leftName = left.name || left.username || '';
        const rightName = right.name || right.username || '';
        return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
      });
  }, [activeActionSelectionIds, applicantsData, listViewApplicants, viewMode]);

  useEffect(() => {
    if (!activeActionSelectionIds.length) {
      setShowCompareSelectionPicker(false);
      setCompareDraftSelectionIds([]);
      return;
    }

    const visibleApplicantIds = new Set(comparePickerApplicants.map((applicant) => applicant.id));

    setCompareDraftSelectionIds((prev) => {
      if (activeActionSelectionIds.length <= MAX_COMPARE_CANDIDATES) {
        return activeActionSelectionIds
          .filter((id) => visibleApplicantIds.has(id))
          .slice(0, MAX_COMPARE_CANDIDATES);
      }

      return prev
        .filter((id) => visibleApplicantIds.has(id))
        .slice(0, MAX_COMPARE_CANDIDATES);
    });
  }, [MAX_COMPARE_CANDIDATES, activeActionSelectionIds, comparePickerApplicants]);

  useEffect(() => {
    if (!(viewMode === 'list' || viewMode === 'board') || !activeActionSelectionIds.length) {
      setSelectionActionMenuPosition(null);
      setShowCompareSelectionPicker(false);
      return undefined;
    }

    const updateSelectionActionMenuPosition = () => {
      const selector = viewMode === 'list'
        ? '.list-view-row-selected'
        : '.applicant-cell.applicant-selected';
      const selectedElements = Array.from(document.querySelectorAll(selector));

      if (!selectedElements.length) {
        setSelectionActionMenuPosition(null);
        return;
      }

      const bounds = selectedElements.reduce((accumulator, element) => {
        const rect = element.getBoundingClientRect();
        return {
          top: Math.min(accumulator.top, rect.top),
          right: Math.max(accumulator.right, rect.right),
          bottom: Math.max(accumulator.bottom, rect.bottom),
          left: Math.min(accumulator.left, rect.left)
        };
      }, {
        top: Number.POSITIVE_INFINITY,
        right: Number.NEGATIVE_INFINITY,
        bottom: Number.NEGATIVE_INFINITY,
        left: Number.POSITIVE_INFINITY
      });

      const menuWidth = 280;
      const preferredLeft = bounds.right + 16;
      const fallbackLeft = bounds.left - menuWidth - 16;
      const left = preferredLeft + menuWidth < window.innerWidth - 16
        ? preferredLeft
        : Math.max(16, fallbackLeft);
      const top = Math.min(Math.max(bounds.top, 92), window.innerHeight - 112);

      setSelectionActionMenuPosition({ left, top });
    };

    updateSelectionActionMenuPosition();
    window.addEventListener('resize', updateSelectionActionMenuPosition);
    window.addEventListener('scroll', updateSelectionActionMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateSelectionActionMenuPosition);
      window.removeEventListener('scroll', updateSelectionActionMenuPosition, true);
    };
  }, [activeActionSelectionIds, viewMode]);

  const clearActiveActionSelection = useCallback(() => {
    if (viewMode === 'list') {
      setListSelectedApplicantIds([]);
      return;
    }

    if (onClearSelection) {
      onClearSelection();
    }
  }, [onClearSelection, viewMode]);

  useEffect(() => {
    if (!activeActionSelectionIds.length || showCompareModal) {
      return undefined;
    }

    const handleSelectionOutsideMouseDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const isSelectionInteraction = target.closest([
        '.applicant-cell.applicant-selected',
        '.list-view-row-selected',
        '.compare-select-cell',
        '.compare-select-trigger',
        '.selection-action-menu',
        '.compare-modal-overlay',
        '.compare-modal-card'
      ].join(', '));

      if (isSelectionInteraction) {
        return;
      }

      clearActiveActionSelection();
    };

    document.addEventListener('mousedown', handleSelectionOutsideMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleSelectionOutsideMouseDown);
    };
  }, [activeActionSelectionIds.length, clearActiveActionSelection, showCompareModal]);

  const handleOpenCompareFromSelection = useCallback(() => {
    if (!activeActionSelectionIds.length) {
      return;
    }

    if (activeActionSelectionIds.length === MAX_COMPARE_CANDIDATES) {
      setCompareApplicantIds(activeActionSelectionIds.slice(0, MAX_COMPARE_CANDIDATES));
      setShowCompareModal(true);
      setShowCompareSelectionPicker(false);
      return;
    }

    setShowCompareSelectionPicker(true);
  }, [MAX_COMPARE_CANDIDATES, activeActionSelectionIds]);

  const toggleCompareDraftSelection = useCallback((applicantId) => {
    let hitSelectionLimit = false;

    setCompareDraftSelectionIds((prev) => {
      if (prev.includes(applicantId)) {
        return prev.filter((id) => id !== applicantId);
      }

      if (prev.length >= MAX_COMPARE_CANDIDATES) {
        hitSelectionLimit = true;
        return prev;
      }

      return [...prev, applicantId];
    });

    if (hitSelectionLimit) {
      showTransientToast(t('workspace.toast.compareLimit', { limit: MAX_COMPARE_CANDIDATES }));
    }
  }, [MAX_COMPARE_CANDIDATES, showTransientToast, t]);

  const handleProceedCompareSelection = useCallback(() => {
    if (compareDraftSelectionIds.length !== MAX_COMPARE_CANDIDATES) {
      return;
    }

    setCompareApplicantIds(compareDraftSelectionIds);
    setShowCompareModal(true);
    setShowCompareSelectionPicker(false);
  }, [MAX_COMPARE_CANDIDATES, compareDraftSelectionIds]);

  const handleRejectSelectedCandidates = async () => {
    if (!activeActionSelectionApplicants.length || isSelectionActionLoading) {
      return;
    }

    // Status-aware delete warnings (single candidate)
    if (activeActionSelectionApplicants.length === 1) {
      const applicant = activeActionSelectionApplicants[0];
      const status = applicantStatusMap[applicant.id] || {};
      const isNeverOpened = !applicant.firstOpenedAt;
      const currentSubstage = dynamicSubstages[applicant.stageId]?.find(s => s.id === applicant.substageId);
      const currentSubstageType = currentSubstage?.type || 'static';
      const isInAssessmentStep = isAssessmentStepType(currentSubstageType);
      const isInInterviewStep = currentSubstageType === 'interview';

      const currentSubstageForDelete = dynamicSubstages[applicant.stageId]?.find(s => s.id === applicant.substageId);
      const deleteSubstageAssessmentId = currentSubstageForDelete?.link || null;
      const deleteAssessmentStepStatus = isInAssessmentStep && deleteSubstageAssessmentId
        ? (status.assessmentsByStep || {})[deleteSubstageAssessmentId]
        : null;

      if (isInAssessmentStep && deleteAssessmentStepStatus !== 'available') {
        if (!window.confirm(t('cellStatus.warnDeleteAwaitingResults'))) return;
      } else if (isInInterviewStep && status.interviewScheduled) {
        const dateLabel = status.interviewScheduled.date || '';
        if (!window.confirm(t('cellStatus.warnDeleteInterviewScheduled', { date: dateLabel }))) return;
      } else if (isNeverOpened) {
        if (!window.confirm(t('cellStatus.warnDeleteNeverOpened'))) return;
      }
    }

    setIsSelectionActionLoading(true);
    try {
      await Promise.all(activeActionSelectionApplicants.map((applicant) => (
        updateApplicantPositionInFirebase(
          applicant.id,
          applicant.stageId || 'stage-1',
          'rejected-applicants'
        )
      )));

      const rejectedIds = activeActionSelectionApplicants.map((applicant) => applicant.id);
      setApplicantsData((prev) => prev.filter((applicant) => !rejectedIds.includes(applicant.id)));
      setCompareApplicantIds((prev) => prev.filter((id) => !rejectedIds.includes(id)));
      clearActiveActionSelection();
      showTransientToast(t('editSubstage.rejected', { count: rejectedIds.length }));
    } catch (error) {
      console.error('Failed to reject selected candidates:', error);
      alert(t('alerts.savingError'));
    } finally {
      setIsSelectionActionLoading(false);
    }
  };

  const loadCompareApplicantDetail = useCallback(async (applicant) => {
    if (!applicant?.connectionKey) {
      return { applicantId: applicant?.id, connectionData: {}, profileData: {} };
    }

    let connectionData = {};
    try {
      const connectionSnapshot = await get(ref(database, `connections/${applicant.connectionKey}`));
      connectionData = connectionSnapshot.exists() ? (connectionSnapshot.val() || {}) : {};
    } catch (error) {
      console.error('Error loading compare connection data:', error);
    }

    let profileData = {};
    try {
      const parsedConnection = parseConnectionKey(applicant.connectionKey);
      if (parsedConnection.applicantType === 'authenticated') {
        const personSnapshot = await get(ref(database, `people/${parsedConnection.applicantId}`));
        profileData = personSnapshot.exists() ? (personSnapshot.val() || {}) : {};
      } else {
        const importedApplicantId = connectionData.tempProfileId || parsedConnection.applicantId;
        let profileSnapshot = await get(ref(database, `imported_profiles/${userUID}/${importedApplicantId}`));
        if (!profileSnapshot.exists()) {
          profileSnapshot = await get(ref(database, `imported_profiles/${importedApplicantId}`));
        }
        profileData = profileSnapshot.exists() ? (profileSnapshot.val() || {}) : {};
      }
    } catch (error) {
      console.error('Error loading compare profile data:', error);
    }

    return {
      applicantId: applicant.id,
      connectionData,
      profileData
    };
  }, [userUID]);

  useEffect(() => {
    if (!compareApplicantIds.length) return undefined;

    const applicantsToLoad = comparedApplicants.filter(
      (applicant) => applicant?.connectionKey && !compareApplicantDetails[applicant.id]
    );

    if (!applicantsToLoad.length) return undefined;

    let cancelled = false;
    setIsCompareLoading(true);

    (async () => {
      const loaded = await Promise.all(applicantsToLoad.map(loadCompareApplicantDetail));
      if (cancelled) return;

      setCompareApplicantDetails((prev) => {
        const next = { ...prev };
        loaded.forEach((entry) => {
          if (entry?.applicantId) {
            next[entry.applicantId] = entry;
          }
        });
        return next;
      });
      setIsCompareLoading(false);
    })().catch((error) => {
      console.error('Error hydrating compare candidates:', error);
      if (!cancelled) {
        setIsCompareLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [compareApplicantDetails, compareApplicantIds, comparedApplicants, loadCompareApplicantDetail]);

  // Fire AI analysis when the modal opens for a new pair+job combination.
  // Uses a stable string key so re-renders never re-trigger the fetch.
  useEffect(() => {
    if (!showCompareModal || compareApplicantIds.length < 2) return;
    const jobId = selectedJobData?.id;
    if (!userUID || !jobId) return;

    const fetchKey = `${userUID}__${jobId}__${[...compareApplicantIds].sort().join('_vs_')}`;
    // Already fetched (or fetching) this exact combination — do nothing.
    if (compareAnalysisFetchedKeyRef.current === fetchKey) return;
    compareAnalysisFetchedKeyRef.current = fetchKey;

    let cancelled = false;
    setCompareAIAnalysis(null);
    setIsCompareAILoading(true);

    const backendUrl = process.env.REACT_APP_BACKEND_URL_PROD || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    fetch(`${backendUrl}/compare-candidates-analysis`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_uid: userUID,
        job_id: jobId,
        applicant_ids: [...compareApplicantIds].sort(),
        force_refresh: false,
      }),
    })
      .then((res) => res.json())
      .then((data) => new Promise((resolve) => setTimeout(() => resolve(data), 3000)))
      .then((data) => {
        if (!cancelled && data?.success) {
          setCompareAIAnalysis(data);
        } else if (!cancelled) {
          compareAnalysisFetchedKeyRef.current = null;
        }
      })
      .catch(() => {
        if (!cancelled) compareAnalysisFetchedKeyRef.current = null;
      })
      .finally(() => { if (!cancelled) setIsCompareAILoading(false); });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCompareModal, compareApplicantIds.join(','), selectedJobData?.id, userUID]);

  const comparedApplicantProfiles = useMemo(() => comparedApplicants.map((applicant) => {
    const compareDetail = compareApplicantDetails[applicant.id] || {};
    const connectionData = compareDetail.connectionData || {};
    const profileData = compareDetail.profileData || {};
    const profileCv = profileData?.metadata?.full_cv || profileData?.cv || {};
    const latestAssessment = getLatestAssessmentEntry(connectionData.assessments || applicant.assessments || profileData.assessments);
    const assessmentResult = latestAssessment?.evaluation?.result || latestAssessment?.evaluation || null;
    const analysis = parseApplicantAnalysis(
      connectionData.applicantAnalysis
      || profileData.applicantAnalysis
      || applicant.applicantAnalysis
      || applicant.analysis
    );
    const teamRating = buildTeamRatingSummary(connectionData.scores || applicant.scores || {});
    const workEntries = firstNonEmptyArray(
      profileData?.metadata?.full_cv?.work_experience,
      profileCv?.work_experience,
      profileData?.work_experience,
      profileData?.parsedData?.experience,
      profileData?.extraction?.experience?.timeline
    );
    const educationEntries = firstNonEmptyArray(
      profileData?.metadata?.full_cv?.education,
      profileCv?.education,
      profileData?.education,
      profileData?.parsedData?.education,
      profileData?.extraction?.education
    );
    const certificationEntries = firstNonEmptyArray(
      profileData?.metadata?.full_cv?.certifications,
      profileCv?.certifications,
      profileData?.certifications,
      profileData?.parsedData?.certifications,
      profileData?.extraction?.skills?.certifications
    );
    const currentWorkEntry = workEntries[0] || {};
    const normalizedSkills = normalizeCompareSkillValues([
      ...(Array.isArray(applicant?.skills) ? applicant.skills : []),
      ...(Array.isArray(connectionData?.skills) ? connectionData.skills : []),
      ...(Array.isArray(connectionData?.applicantAnalysis?.topSkills) ? connectionData.applicantAnalysis.topSkills : []),
      ...(Array.isArray(connectionData?.applicantAnalysis?.top_skills) ? connectionData.applicantAnalysis.top_skills : []),
      ...(Array.isArray(profileData?.skills) ? profileData.skills : []),
      ...(Array.isArray(profileData?.tags) ? profileData.tags : []),
      ...(Array.isArray(profileData?.parsedData?.skills) ? profileData.parsedData.skills : []),
      ...(Array.isArray(profileData?.extraction?.skills?.normalized) ? profileData.extraction.skills.normalized : []),
      ...(Array.isArray(profileData?.metadata?.full_cv?.skills) ? profileData.metadata.full_cv.skills : []),
      ...(Array.isArray(profileCv?.skills) ? profileCv.skills : [])
    ]);

    const matchedSkills = intersectCompareValues(normalizedSkills, compareJobSignals.requiredSkills, 8);
    const missingSkills = compareJobSignals.requiredSkills
      .filter((skill) => !matchedSkills.some((matchedSkill) => normalizeCompareText(matchedSkill) === normalizeCompareText(skill)))
      .slice(0, 6);
    const skillCoverage = compareJobSignals.requiredSkills.length
      ? Math.round((matchedSkills.length / compareJobSignals.requiredSkills.length) * 100)
      : null;
    const yearsExperience = readFirstNumber(
      applicant?.yearsExperience,
      applicant?.experienceYears,
      applicant?.applicantAnalysis?.yearsExperience,
      applicant?.applicantAnalysis?.years_experience,
      connectionData?.applicantAnalysis?.yearsExperience,
      connectionData?.applicantAnalysis?.years_experience,
      profileData?.totalYearsExperience,
      profileData?.experience?.totalYears,
      profileData?.extraction?.experience?.totalYears,
      profileData?.parsedData?.totalYearsExperience,
      applicant?.experience,
      currentWorkEntry?.duration
    );
    const fitScorePercent = normalizePercentScore(
      connectionData?.candidateEvaluation?.current?.fitScore
      ?? applicant?.candidateEvaluation?.current?.fitScore
      ?? applicant?.fit_score
      ?? connectionData?.fit_score
      ?? profileData?.fit_score
      ?? applicant?.fitScore
      ?? connectionData?.applicantAnalysis?.fitScore
      ?? connectionData?.applicantAnalysis?.fit_score
      ?? applicant?.applicantAnalysis?.fitScore
      ?? applicant?.applicantAnalysis?.fit_score
    );
    const assessmentScore = normalizeTenPointScore(
      assessmentResult?.fit_score
      ?? assessmentResult?.overallScore
      ?? assessmentResult?.score
    );
    const teamRatingPercent = teamRating.average ? Math.round((teamRating.average / 5) * 100) : null;
    const summary = analysis.summary
      || assessmentResult?.overall_summary
      || profileData?.recruiterSummary
      || profileData?.summary
      || applicant?.fitReasoning
      || '';
    const strengths = dedupeCompareValues([
      ...analysis.strengths,
      ...splitCompareList(assessmentResult?.strengths)
    ]).slice(0, 6);
    const watchouts = dedupeCompareValues([
      ...analysis.concerns,
      ...splitCompareList(assessmentResult?.improvement_areas)
    ]).slice(0, 6);
    const evidenceSignals = [
      fitScorePercent,
      skillCoverage,
      assessmentScore !== null ? Math.round((assessmentScore / 10) * 100) : null,
      teamRatingPercent
    ].filter((value) => Number.isFinite(value));
    const readinessScore = Math.max(
      0,
      Math.round((evidenceSignals.length ? (evidenceSignals.reduce((sum, value) => sum + value, 0) / evidenceSignals.length) : 0)
        + ((applicant?.email || profileData?.email || applicant?.phone || profileData?.phone) ? 8 : -10)
        - Math.min(missingSkills.length * 5, 20))
    );

    let recommendedAction = t('workspace.compareRecommendedDefault');
    if (!(applicant?.email || profileData?.email || applicant?.phone || profileData?.phone)) {
      recommendedAction = t('workspace.compareRecommendedContact');
    } else if (fitScorePercent !== null && fitScorePercent >= 80 && (skillCoverage === null || skillCoverage >= 60) && watchouts.length <= 1) {
      recommendedAction = t('workspace.compareRecommendedAdvance');
    } else if (assessmentScore !== null && assessmentScore < 6) {
      recommendedAction = t('workspace.compareRecommendedProbe');
    } else if (missingSkills.length > 0 || watchouts.length > 0) {
      recommendedAction = t('workspace.compareRecommendedValidate', {
        focus: missingSkills[0] || watchouts[0] || t('workspace.compareFallbackFocus')
      });
    }

    return {
      id: applicant.id,
      name: applicant.name || applicant.username || t('workspace.candidate'),
      stageName: getApplicantStageName(applicant),
      fitScorePercent,
      yearsExperience,
      normalizedSkills,
      matchedSkills,
      missingSkills,
      skillCoverage,
      summary,
      strengths,
      watchouts,
      verdict: analysis.verdict || '',
      email: applicant?.email || profileData?.email || '',
      phone: applicant?.phone || profileData?.phone || '',
      location: profileData?.location || profileData?.parsedData?.location || profileData?.extraction?.identity?.location || '',
      currentRole: profileData?.jobTitle || profileData?.metadata?.jobTitle || profileData?.extraction?.experience?.currentTitle || currentWorkEntry?.title || '',
      currentCompany: profileData?.company || profileData?.extraction?.experience?.currentCompany || currentWorkEntry?.company || '',
      lastUpdated: applicant?.lastUpdated || connectionData?.lastUpdated || applicant?.createdAt || 0,
      createdAt: applicant?.createdAt || 0,
      educationCount: educationEntries.length,
      certificationCount: certificationEntries.length,
      teamRatingAverage: teamRating.average,
      teamRatingCount: teamRating.count,
      assessmentScore,
      assessmentSummary: assessmentResult?.overall_summary || assessmentResult?.recommendation || latestAssessment?.analysis || '',
      assessmentTitle: latestAssessment?.assessment?.title || latestAssessment?.title || '',
      readinessScore,
      recommendedAction
    };
  }), [compareApplicantDetails, compareJobSignals.requiredSkills, comparedApplicants, getApplicantStageName, t]);

  const comparedApplicantInsights = useMemo(() => {
    // When AI analysis is ready, merge its per-candidate data into the profiles
    const aiByid = {};
    if (compareAIAnalysis?.candidates) {
      for (const c of compareAIAnalysis.candidates) {
        aiByid[c.applicant_id] = c;
      }
    }

    const mergedProfiles = comparedApplicantProfiles.map((profile) => {
      const ai = aiByid[profile.id];
      if (!ai) return profile;
      return {
        ...profile,
        fitScorePercent: profile.fitScorePercent ?? ai.fit_score ?? null,
        yearsExperience: ai.years_experience ?? profile.yearsExperience,
        matchedSkills: ai.matched_skills?.length ? ai.matched_skills : profile.matchedSkills,
        missingSkills: ai.missing_skills?.length ? ai.missing_skills : profile.missingSkills,
        skillCoverage: ai.skill_coverage ?? profile.skillCoverage,
        strengths: ai.strengths?.length ? ai.strengths : profile.strengths,
        watchouts: ai.concerns?.length ? ai.concerns : profile.watchouts,
        summary: ai.summary || profile.summary,
        recommendedAction: ai.recommended_action || profile.recommendedAction,
        hiringRecommendation: ai.hiring_recommendation || profile.hiringRecommendation,
        decisionRationale: ai.decision_rationale || profile.decisionRationale,
        interviewFocus: ai.interview_focus?.length ? ai.interview_focus : profile.interviewFocus,
        businessRisks: ai.business_risks?.length ? ai.business_risks : profile.businessRisks,
      };
    });

    const fitRanks = buildCompareRankLookup(mergedProfiles, (profile) => profile.fitScorePercent);
    const coverageRanks = buildCompareRankLookup(mergedProfiles, (profile) => profile.skillCoverage);
    const readinessRanks = buildCompareRankLookup(mergedProfiles, (profile) => profile.readinessScore);

    return mergedProfiles.map((profile) => {
      const ai = aiByid[profile.id];

      const otherProfiles = mergedProfiles.filter((c) => c.id !== profile.id);
      const otherSkills = otherProfiles.flatMap((c) => c.normalizedSkills || []);

      // AI provides unique_edge directly; fall back to skill-diff heuristic
      const uniqueEdge = (ai?.unique_edge?.length ? ai.unique_edge :
        (profile.normalizedSkills || [])
          .filter((skill) => !otherSkills.some((s) => normalizeCompareText(s) === normalizeCompareText(skill)))
      ).slice(0, 4);

      const strengthSignals = dedupeCompareValues([
        ...profile.matchedSkills.slice(0, 4),
        ...profile.strengths.slice(0, 3),
        ...uniqueEdge
      ]).slice(0, 6);
      const validationSignals = dedupeCompareValues([
        ...profile.missingSkills.slice(0, 4),
        ...profile.watchouts.slice(0, 3),
        ...(!(profile.email || profile.phone) ? [t('workspace.compareContactMissing')] : [])
      ]).slice(0, 5);

      // AI provides evidence quality directly when available
      let evidenceTone = ai?.evidence_quality || 'thin';
      let evidenceLabel = t('workspace.compareEvidenceThin');
      let evidenceBody = t('workspace.compareEvidenceThinBody');
      if (evidenceTone === 'strong') {
        evidenceLabel = t('workspace.compareEvidenceStrong');
        evidenceBody = t('workspace.compareEvidenceStrongBody');
      } else if (evidenceTone === 'moderate') {
        evidenceLabel = t('workspace.compareEvidenceModerate');
        evidenceBody = t('workspace.compareEvidenceModerateBody');
      }
      const evidenceScore = evidenceTone === 'strong' ? 5 : evidenceTone === 'moderate' ? 3 : 1;

      // AI provides positioning summary directly; fall back to synthesised narrative
      let positioningSummary = ai?.positioning_summary || '';
      if (!positioningSummary) {
        const positioningSignals = [];
        if (fitRanks[profile.id] === 1 && profile.fitScorePercent !== null) {
          positioningSignals.push(t('workspace.compareNarrativeLeadFit', { score: profile.fitScorePercent }));
        }
        if (coverageRanks[profile.id] === 1 && profile.skillCoverage !== null && compareJobSignals.requiredSkills.length) {
          positioningSignals.push(t('workspace.compareNarrativeLeadCoverage', { coverage: profile.skillCoverage }));
        }
        if (readinessRanks[profile.id] === 1) {
          positioningSignals.push(t('workspace.compareNarrativeLeadReadiness'));
        }
        if (!positioningSignals.length) {
          positioningSignals.push(t('workspace.compareNarrativeCompetitive'));
        }
        const standoutSignal = uniqueEdge[0] || strengthSignals[0];
        if (standoutSignal) positioningSignals.push(t('workspace.compareNarrativeEdge', { edge: standoutSignal }));
        if (validationSignals[0]) {
          positioningSignals.push(t('workspace.compareNarrativeGap', { gap: validationSignals[0] }));
        } else {
          positioningSignals.push(t('workspace.compareNarrativeLowRisk'));
        }
        positioningSummary = positioningSignals.join(' ');
      }

      let hiringRecommendation = ai?.hiring_recommendation || profile.hiringRecommendation || '';
      if (!['advance', 'validate', 'hold'].includes(hiringRecommendation)) {
        if (profile.fitScorePercent !== null && profile.fitScorePercent < 55) {
          hiringRecommendation = 'hold';
        } else if (profile.skillCoverage !== null && compareJobSignals.requiredSkills.length && profile.skillCoverage < 40) {
          hiringRecommendation = 'hold';
        } else if (
          profile.fitScorePercent !== null
          && profile.fitScorePercent >= 80
          && (profile.skillCoverage === null || profile.skillCoverage >= 70)
          && evidenceTone !== 'thin'
          && validationSignals.length <= 1
        ) {
          hiringRecommendation = 'advance';
        } else {
          hiringRecommendation = 'validate';
        }
      }

      let decisionRationale = ai?.decision_rationale || profile.decisionRationale || '';
      if (!decisionRationale) {
        if (hiringRecommendation === 'advance') {
          decisionRationale = t('workspace.compareDecisionRationaleAdvance');
        } else if (hiringRecommendation === 'hold') {
          decisionRationale = t('workspace.compareDecisionRationaleHold');
        } else {
          decisionRationale = t('workspace.compareDecisionRationaleValidate');
        }
      }

      const interviewFocus = dedupeCompareValues(
        ai?.interview_focus?.length
          ? ai.interview_focus
          : validationSignals
      ).slice(0, 3);
      const businessRisks = dedupeCompareValues(
        ai?.business_risks?.length
          ? ai.business_risks
          : [
              ...validationSignals,
              ...(evidenceTone === 'thin' ? [t('workspace.compareEvidenceMissing')] : [])
            ]
      ).slice(0, 4);

      return {
        ...profile,
        uniqueEdge,
        strengthSignals,
        validationSignals,
        evidenceScore,
        evidenceTone,
        evidenceLabel,
        evidenceBody,
        hiringRecommendation,
        decisionRationale,
        interviewFocus,
        businessRisks,
        positioningSummary,
        fitRank: fitRanks[profile.id] || null,
        coverageRank: coverageRanks[profile.id] || null,
        readinessRank: readinessRanks[profile.id] || null
      };
    });
  }, [compareJobSignals.requiredSkills.length, comparedApplicantProfiles, compareAIAnalysis, t]);

  const compareInsightCards = useMemo(() => {
    // Use AI-generated insight cards when available
    if (compareAIAnalysis?.insight_cards?.length) {
      return compareAIAnalysis.insight_cards;
    }
    if (comparedApplicantInsights.length < 2) return [];

    const byFit = [...comparedApplicantInsights].sort((left, right) => (right.fitScorePercent || -1) - (left.fitScorePercent || -1))[0];
    const byCoverage = [...comparedApplicantInsights].sort((left, right) => (right.skillCoverage || -1) - (left.skillCoverage || -1))[0];
    const byEvidence = [...comparedApplicantInsights].sort((left, right) => (right.evidenceScore || -1) - (left.evidenceScore || -1))[0];
    const biggestGap = [...comparedApplicantInsights].sort((left, right) => (right.missingSkills.length || 0) - (left.missingSkills.length || 0))[0];

    return [
      byFit ? {
        key: 'fit',
        tone: 'good',
        label: t('workspace.compareBestFitLabel'),
        title: byFit.name,
        body: t('workspace.compareBestFitBody', { score: byFit.fitScorePercent ?? '—' })
      } : null,
      compareJobSignals.requiredSkills.length && byCoverage ? {
        key: 'coverage',
        tone: 'info',
        label: t('workspace.compareBestCoverageLabel'),
        title: byCoverage.name,
        body: t('workspace.compareBestCoverageBody', {
          matched: byCoverage.matchedSkills.length,
          total: compareJobSignals.requiredSkills.length
        })
      } : null,
      byEvidence ? {
        key: 'evidence',
        tone: 'good',
        label: t('workspace.compareEvidenceLeadLabel'),
        title: byEvidence.name,
        body: t('workspace.compareEvidenceLeadBody', { label: byEvidence.evidenceLabel.toLowerCase() })
      } : null,
      biggestGap && biggestGap.missingSkills.length ? {
        key: 'risk',
        tone: 'warning',
        label: t('workspace.compareCoverageRiskLabel'),
        title: biggestGap.name,
        body: t('workspace.compareCoverageRiskBody', {
          gap: biggestGap.missingSkills[0]
        })
      } : null
    ].filter(Boolean);
  }, [compareJobSignals.requiredSkills.length, comparedApplicantInsights, compareAIAnalysis, t]);

  const compareDecisionRows = useMemo(() => [
    {
      key: 'role',
      label: t('workspace.compareRoleCoverage'),
      description: t('workspace.compareDecisionRoleDescription'),
      render: (candidate) => (
        <>
          <strong>{candidate.skillCoverage !== null ? `${candidate.skillCoverage}%` : t('workspace.compareNoJobSignals')}</strong>
          <span>{candidate.matchedSkills.length ? candidate.matchedSkills.slice(0, 3).join(', ') : t('workspace.compareNoMatchedSkills')}</span>
        </>
      )
    },
    {
      key: 'experience',
      label: t('workspace.compareExperience'),
      description: t('workspace.compareDecisionExperienceDescription'),
      render: (candidate) => (
        <>
          <strong>{candidate.yearsExperience !== null && candidate.yearsExperience !== undefined ? `${candidate.yearsExperience}y` : t('workspace.compareEvidenceMissing')}</strong>
          <span>{candidate.currentRole || t('workspace.compareRoleMissing')}</span>
        </>
      )
    },
    {
      key: 'assessment',
      label: t('workspace.compareAssessment'),
      description: t('workspace.compareDecisionAssessmentDescription'),
      render: (candidate) => (
        candidate.assessmentScore !== null && candidate.assessmentScore !== undefined ? (
          <>
            <strong>{candidate.assessmentScore}/10</strong>
            <span>{candidate.assessmentTitle || t('workspace.compareAssessment')}</span>
          </>
        ) : (
          <CompareMissingEvidence>{t('workspace.compareMissingAssessmentImpact')}</CompareMissingEvidence>
        )
      )
    },
    {
      key: 'team',
      label: t('workspace.compareTeamSignal'),
      description: t('workspace.compareDecisionTeamDescription'),
      render: (candidate) => (
        candidate.teamRatingAverage !== null && candidate.teamRatingAverage !== undefined ? (
          <>
            <strong>{candidate.teamRatingAverage.toFixed(1)}/5</strong>
            <span>{t('workspace.compareTeamRatingCount', { count: candidate.teamRatingCount })}</span>
          </>
        ) : (
          <CompareMissingEvidence>{t('workspace.compareMissingTeamImpact')}</CompareMissingEvidence>
        )
      )
    },
    {
      key: 'evidence',
      label: t('workspace.compareEvidenceQuality'),
      description: t('workspace.compareDecisionEvidenceDescription'),
      render: (candidate) => (
        <>
          <span className={`compare-evidence-pill ${candidate.evidenceTone}`}>{candidate.evidenceLabel}</span>
          <span>{candidate.evidenceBody}</span>
        </>
      )
    }
  ], [t]);

  const openOfferReviewModal = (approval) => {
    if (!approval || !approval.metadata) return;

    setOfferReviewMode(true);
    setReviewApprovalData(approval);
    setOfferTargetStage(approval.metadata.targetStage || 'stage-1');
    setOfferTargetSubstage(approval.metadata.targetSubstage || 'offer-sent');

    setOfferModalApplicants(approval.metadata.applicants ? approval.metadata.applicants.map(a => ({
      ...a,
      // Ensure ID is set for UI keys
      id: a.id || `app_${Date.now()}_${Math.random()}`
    })) : []);

    setOfferSubject(approval.metadata.subject || '');
    setOfferMessageHTML(approval.metadata.messageHTML || '');
    setActiveOfferId(approval.metadata.offerId || '');
    setOfferTerms({
      ...createDefaultOfferTerms(),
      ...(approval.metadata.terms || {})
    });
    setOfferContextListedSalary(
      approval.metadata.listedSalary
      || approval.metadata.jobListedSalary
      || getJobListedSalary(approval.metadata.job, selectedJobData, currActiveJob)
    );
    const reviewFollowUps = approval.metadata.followUps || {};
    setOfferFollowUpsEnabled(reviewFollowUps.enabled !== false);
    setOfferFollowUps(normalizeOfferFollowUpItems(reviewFollowUps));
    setOfferApprovalNote(approval.metadata.approvalNote || '');
    setOfferRejectionReason('');

    const existingAttachments = approval.metadata.attachments || [];
    const mappedAttachments = existingAttachments.map(att => ({
      name: att.name,
      // Handle variations in field names
      content: att.content_base64 || att.content,
      type: att.mime_type || att.type,
      size: att.size || 0
    }));
    setOfferAttachments(mappedAttachments);

    // Trigger editor content set on next load
    offerResetRef.current = true;
    setShowOfferEmailModal(true);
  };

  useImperativeHandle(forwardedRef, () => ({
    refreshStatuses: () => {
      setStatusRefreshKey(k => k + 1);
    },
    openOnboardingModalForTutorial: (applicantId, fallbackApplicant = null) => {
      if (!applicantId) return;
      const pools = [
        Array.isArray(applicantsData) ? applicantsData : [],
        Array.isArray(exampleApplicants) ? exampleApplicants : [],
        fallbackApplicant ? [fallbackApplicant] : []
      ];
      const candidate = pools.flat().find(applicant => applicant?.id === applicantId);
      if (candidate) {
        openOnboardingTransitionModal(candidate);
      }
    },
    closeOnboardingModal: () => {
      setShowOnboardingTransition(false);
    },
    openOfferReview: openOfferReviewModal
  }));

  // Load applicants when active job changes
  useEffect(() => {
    if (currActiveJob) {
      console.log('Loading applicants for job:', currActiveJob);
      if (Array.isArray(sortedApplicantsData)) {
        setApplicantsData(sortedApplicantsData);
      } else {
        setApplicantsData([]);
      }
    }
  }, [currActiveJob, sortedApplicantsData]);

  // Real-time listener for applicant updates (for gallery/list views)
  useEffect(() => {
    if (!userUID || !selectedJobData?.id) return;

    const jobId = selectedJobData.id;
    const indexRef = ref(database, `companies/${userUID}/applicant_index/by_job/${jobId}`);

    const unsubscribe = onValue(indexRef, (snapshot) => {
      if (snapshot.exists()) {
        const indexData = snapshot.val();

        // Update applicantsData with the latest data from the index
        setApplicantsData(prev => {
          return prev.map(applicant => {
            const updatedData = indexData[applicant.connectionKey];
            if (updatedData) {
              // Merge updated data while preserving fields not in the index
              const updated = {
                ...applicant,
                stageId: updatedData.stageId,
                substageId: updatedData.substageId,
                lastUpdated: updatedData.lastUpdated,
                createdAt: updatedData.createdAt,
                name: updatedData.applicantName || applicant.name,
                email: updatedData.applicantEmail || applicant.email,
                starred: updatedData.starred === true,
                firstOpenedAt: updatedData.firstOpenedAt || applicant.firstOpenedAt || null,
                fit_score: Object.prototype.hasOwnProperty.call(updatedData, 'fit_score')
                  && updatedData.fit_score !== null
                  && updatedData.fit_score !== undefined
                  && updatedData.fit_score !== ''
                  && Number.isInteger(Number(updatedData.fit_score))
                  ? Number(updatedData.fit_score)
                  : null
              };

              // Debug log when starred changes
              if (applicant.starred !== updated.starred) {
                console.log(`⭐ Starred status changed for ${applicant.name}: ${applicant.starred} -> ${updated.starred}`);
              }

              return updated;
            }
            return applicant;
          });
        });
      }
    });

    // Cleanup listener on unmount or when job/user changes
    return () => {
      off(indexRef);
    };
  }, [userUID, selectedJobData?.id]);

  const handleJobSelect = (jobName) => {
    onSelectJob(jobName);
    setIsDropdownOpen(false);
  };

  // Handle custom list editing
  const handleEditCustomList = (listOption) => {
    setEditingListId(listOption.id);
    setEditListTitle(listOption.title);
    setEditListDescription(listOption.description || '');
    setEditListJobDescription(listOption.jobDescription || '');
    setEditListTargetHireDate(toOfferDateInput(listOption.data?.hiringPlan?.targetHireDate || listOption.hiringPlan?.targetHireDate || listOption.data?.targetHireDate || listOption.targetHireDate || ''));
    setShowEditCustomList(true);
    setIsDropdownOpen(false);
  };

  const handleSaveCustomListChanges = async () => {
    if (!editListTitle.trim()) {
      alert(uiText('Please enter a title for the custom list'));
      return;
    }

    try {
      // Get the old title before updating
      const currentList = jobOptions.find(option => option.id === editingListId);
      const oldTitle = currentList?.title;
      const newTitle = editListTitle.trim();
      const targetHireDate = toOfferDateInput(editListTargetHireDate);

      const updates = {
        title: newTitle,
        description: editListDescription.trim(),
        jobDescription: editListJobDescription.trim(),
        targetHireDate,
        hiringPlan: {
          ...(currentList?.data?.hiringPlan || currentList?.hiringPlan || {}),
          targetHireDate
        }
      };

      await updateCustomList(userUID, editingListId, updates);

      // Refresh job options
      await loadJobOptions({ forceRefresh: true });

      // If the currently selected job was renamed, update the selection
      if (oldTitle && newTitle && currActiveJob === oldTitle) {
        console.log('Current job was renamed, updating selection');
        onSelectJob(newTitle);
      }

      // Close edit modal
      setShowEditCustomList(false);
      setEditingListId(null);
      setEditListTargetHireDate('');

    } catch (error) {
      console.error('Error updating custom list:', error);
      alert(uiText('Error updating custom list. Please try again.'));
    }
  };

  const handleDeleteCustomList = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this custom list? This will remove all CVs in this list and cannot be undone.'
    );

    if (!confirmed) return;

    try {
      await deleteCustomList(userUID, editingListId);

      // Refresh job options
      await loadJobOptions({ forceRefresh: true });

      // Close edit modal
      setShowEditCustomList(false);
      setEditingListId(null);

      // If we're currently viewing the deleted list, reset the selection
      const deletedList = jobOptions.find(opt => opt.id === editingListId);
      if (deletedList && selectedJobName === deletedList.title) {
        onSelectJob(t('workspace.selectJob'));
      }

    } catch (error) {
      console.error('Error deleting custom list:', error);
      alert(uiText('Error deleting custom list. Please try again.'));
    }
  };

  // Undo functionality
  const handleUndo = async () => {
    if (!undoData) return;

    // Clear the timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }

    // Revert all moved applicants to their original positions
    for (const movedApp of undoData.movedApplicants) {
      await updateApplicantPositionInFirebase(
        movedApp.id,
        movedApp.originalStageId,
        movedApp.originalSubstageId
      );
    }

    // Update local state
    setApplicantsData(prev =>
      prev.map(app => {
        const movedApp = undoData.movedApplicants.find(m => m.id === app.id);
        if (movedApp) {
          return {
            ...app,
            stageId: movedApp.originalStageId,
            substageId: movedApp.originalSubstageId
          };
        }
        return app;
      })
    );

    // Hide toast
    setShowUndoToast(false);
    setUndoData(null);
  };

  const hideUndoToast = () => {
    setShowUndoToast(false);
    setUndoData(null);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // Toast notification helper
  const showToastNotification = showTransientToast;

  const hideToast = () => {
    setShowToast(false);
    setToastMessage('');
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();

    const board = boardRef.current;
    if (!board) return;

    const rect = board.getBoundingClientRect();
    const edgeZone = 80;
    const maxSpeed = 18;
    const x = e.clientX;

    let speed = 0;
    if (x < rect.left + edgeZone) {
      speed = -maxSpeed * (1 - (x - rect.left) / edgeZone);
    } else if (x > rect.right - edgeZone) {
      speed = maxSpeed * (1 - (rect.right - x) / edgeZone);
    }

    if (dragScrollFrameRef.current) {
      cancelAnimationFrame(dragScrollFrameRef.current);
      dragScrollFrameRef.current = null;
    }

    if (speed !== 0) {
      const scroll = () => {
        board.scrollLeft += speed;
        dragScrollFrameRef.current = requestAnimationFrame(scroll);
      };
      dragScrollFrameRef.current = requestAnimationFrame(scroll);
    }
  };

  // Open onboarding transition modal with pre-filled data
  const openOnboardingTransitionModal = async (applicant) => {
    try {
      if (applicant?.id?.startsWith('_DEMO_APP_')) {
        const demoName = applicant.name || applicant.username || 'New Hire';
        const demoEmail = applicant.email || 'candidate@example.com';
        const jobRole = selectedJobData?.data?.jobOpening || selectedJobData?.data?.title || selectedJobData?.title || 'Senior Software Engineer';
        const jobDept = selectedJobData?.data?.department || selectedJobData?.department || 'Engineering';

        setOnboardingForm({
          name: demoName,
          email: demoEmail,
          role: jobRole,
          department: jobDept,
          startDate: '',
          manager: 'John Carter',
          buddyMentor: 'Emily Davis'
        });
        setOnboardingOfferContext(null);
        setOnboardingApplicant(applicant);
        setShowOnboardingTransition(true);
        return;
      }

      // Get applicant details
      let applicantName = applicant.name || applicant.username || '';
      let applicantEmail = '';
      let applicantType = applicant.applicantType || 'authenticated';

      // Fetch email based on applicant type
      if (applicantType === 'imported') {
        let profileRef = ref(database, `imported_profiles/${userUID}/${applicant.id}`);
        let profileSnap = await get(profileRef);

        if (!profileSnap.exists()) {
          profileRef = ref(database, `imported_profiles/${applicant.id}`);
          profileSnap = await get(profileRef);
        }

        if (profileSnap.exists()) {
          const profileData = profileSnap.val();
          applicantEmail = profileData.email || '';
          applicantName = applicantName || profileData.name || '';
        }
      } else {
        // For authenticated users, try to get email from Firebase Auth
        try {
          const emailResp = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: applicant.id })
          });
          const emailData = await emailResp.json();
          applicantEmail = emailData.email || '';
        } catch (e) {
          console.error('Failed to fetch applicant email:', e);
        }
      }

      // Get job data for role and department
      let jobRole = '';
      let jobDepartment = '';

      if (selectedJobData) {
        jobRole = selectedJobData.data?.jobOpening || selectedJobData.data?.title || selectedJobData.title || '';
        jobDepartment = selectedJobData.data?.department || selectedJobData.department || '';
      }

      const {
        offer,
        offerId,
        connectionKey,
        connectionData
      } = await loadOfferForApplicant(applicant);
      const linkedOfferTerms = offer?.terms || {};
      const acceptedOfferAt = offer?.candidateResponse?.acceptedAt
        || offer?.acceptedOfferAt
        || offer?.offerAcceptedAt
        || connectionData?.offerAcceptedAt
        || connectionData?.acceptedOfferAt
        || '';
      const plannedStartDate = toOfferDateInput(
        linkedOfferTerms.startDate
        || connectionData?.plannedStartDate
        || connectionData?.startDate
        || ''
      );
      const offerRole = offer?.job?.title || connectionData?.jobTitle || jobRole;
      const offerDepartment = offer?.job?.department || connectionData?.department || jobDepartment;
      setOnboardingOfferContext(offerId ? {
        offerId,
        connectionKey,
        version: offer?.version || '',
        status: offer?.status || connectionData?.offerStatus || '',
        acceptedOfferAt,
        offerSentAt: offer?.sentAt || connectionData?.offerSentAt || '',
        offerViewedAt: offer?.viewedAt || connectionData?.offerViewedAt || '',
        plannedStartDate
      } : null);

      // Pre-fill the form
      setOnboardingForm({
        name: applicantName,
        email: applicantEmail,
        role: offerRole,
        department: offerDepartment,
        startDate: plannedStartDate,
        manager: '',
        buddyMentor: ''
      });

      setOnboardingApplicant(applicant);
      setShowOnboardingTransition(true);
    } catch (error) {
      console.error('Error opening onboarding transition modal:', error);
      alert(uiText('Failed to load applicant details. Please try again.'));
    }
  };

  // Submit onboarding transition
  const handleSubmitOnboarding = async () => {
    if (!onboardingForm.name || !onboardingForm.email || !onboardingForm.startDate) {
      alert(t('workspace.onboardingModal.requiredFields', 'Please fill in required fields: Name, Email, and Start Date'));
      return;
    }
    if (onboardingEstablishments.length > 1 && !onboardingEstablishmentId) {
      alert(t('workspace.onboardingModal.establishmentRequired', 'Select the legal company or branch for this employee.'));
      return;
    }

    try {
      // Fetch team and contacts for intelligent task assignment
      const [teamSnap, contactsSnap] = await Promise.all([
        get(ref(database, `companies/${userUID}/teamMembers`)),
        get(ref(database, `companies/${userUID}/externalContacts`))
      ]);
	      const teamMembers = teamSnap.exists() ? Object.values(teamSnap.val()) : [];
	      const externalContacts = contactsSnap.exists() ? Object.values(contactsSnap.val()) : [];

	      // Helper to resolve task assignee based on category
	      const normalizeAssigneeCategory = (category) => String(category || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
	      const getTemplateRolePerson = (category) => {
	        const cat = normalizeAssigneeCategory(category);
	        const people = Array.isArray(selectedTemplate?.teamRoles?.[cat]) ? selectedTemplate.teamRoles[cat] : [];
	        return people.find(member => member?.email) || null;
	      };
	      const pickTemplateRoleAssignee = (category) => {
	        const person = getTemplateRolePerson(category);
	        return person?.email || '';
	      };
	      const personLooksLike = (person, pattern) => {
	        const haystack = [
	          person?.role,
	          person?.department,
	          person?.title,
	          person?.team,
	          person?.function
	        ].map(value => String(value || '').toLowerCase()).join(' ');
	        return pattern.test(haystack);
	      };
	      const resolveAssignee = (category) => {
	        if (!category) return onboardingForm.manager || '';
	        const cat = normalizeAssigneeCategory(category);

	        if (cat === 'new_hire' || cat === 'newhire') return onboardingForm.email;
	        if (cat === 'manager') return onboardingForm.manager;

	        const templateRoleAssignee = pickTemplateRoleAssignee(cat);
	        if (templateRoleAssignee) return templateRoleAssignee;

	        if (cat === 'it') {
	          const itPerson = teamMembers.find(m => (m.role === 'IT' || m.department === 'IT')) ||
	            externalContacts.find(c => (c.role === 'IT' || c.department === 'IT'));
	          if (itPerson) return itPerson.email;
          // Fallback to admin
          const admin = teamMembers.find(m => m.role === 'admin' || m.isAdmin);
          return admin ? admin.email : (teamMembers[0]?.email || onboardingForm.manager);
        }

        if (cat === 'hr') {
          const hrPeople = teamMembers.filter(m => m.role === 'HR' || m.department === 'HR');
          if (hrPeople.length > 0) return hrPeople[Math.floor(Math.random() * hrPeople.length)].email;

          // Random admin or collaborator
          const adminsAndColl = teamMembers.filter(m => m.role !== 'IT'); // Exclude IT if we are picky, otherwise just pool
          const pool = adminsAndColl.length > 0 ? adminsAndColl : teamMembers;
	          if (pool.length > 0) return pool[Math.floor(Math.random() * pool.length)].email;
	        }

	        if (cat === 'legal') {
	          const legalPerson = teamMembers.find(m => personLooksLike(m, /(legal|compliance|contracts|counsel|law)/)) ||
	            externalContacts.find(c => personLooksLike(c, /(legal|compliance|contracts|counsel|law)/));
	          if (legalPerson) return legalPerson.email;
	        }

	        if (cat === 'finance') {
	          const financePerson = teamMembers.find(m => personLooksLike(m, /(finance|accounting|payroll|tax|treasury|controller)/)) ||
	            externalContacts.find(c => personLooksLike(c, /(finance|accounting|payroll|tax|treasury|controller)/));
	          if (financePerson) return financePerson.email;
	        }

	        return onboardingForm.manager;
	      };

      const toBoolean = (value) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
          const normalized = value.trim().toLowerCase();
          if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
          if (['false', '0', 'no', 'n', ''].includes(normalized)) return false;
        }
        if (typeof value === 'number') return value === 1;
        return false;
      };

      const normalizeExecutionMode = (task = {}) => {
        const rawMode = String(
          task?.executionMode
          || task?.execution_mode
          || task?.scheduleMode
          || task?.schedule_mode
          || task?.runMode
          || task?.run_mode
          || task?.timingMode
          || task?.timing_mode
          || task?.timing?.mode
          || task?.execution?.mode
          || ''
        ).trim().toLowerCase();

        if (
          rawMode === 'immediate'
          || rawMode === 'now'
          || rawMode === 'send_immediately'
          || rawMode === 'sendimmediately'
          || rawMode === 'instant'
        ) {
          return 'immediate';
        }

        if (
          toBoolean(task?.isImmediate)
          || toBoolean(task?.sendImmediately)
          || toBoolean(task?.executeImmediately)
          || toBoolean(task?.immediate)
        ) {
          return 'immediate';
        }

        return 'scheduled';
      };

      const getRelativeDays = (task = {}) => {
        const rawValue = task?.relativeDays ?? task?.defaultDays ?? task?.dayOffset ?? task?.day_offset ?? 0;
        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : 0;
      };

      const toIsoDate = (dateValue) => {
        const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);
        if (Number.isNaN(dateObj.getTime())) return '';
        return dateObj.toISOString().split('T')[0];
      };

      const toLocalIsoDate = (dateValue = new Date()) => {
        const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);
        if (Number.isNaN(dateObj.getTime())) return '';
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

	      const todayIso = toLocalIsoDate(new Date());
	      const nowMs = Date.now();
	      const nowIso = new Date(nowMs).toISOString();

      const newHireRef = push(ref(database, `companies/${userUID}/onboarding`));
      // This is only a fallback. Once the ATS connection is loaded below, formal
      // onboarding reuses its pre-onboarding employee key when one already exists.
      let hireId = newHireRef.key;
      const startDate = new Date(onboardingForm.startDate);

      // Try to load templates from Firebase
      let selectedTemplate = null;
      try {
        const templatesRef = ref(database, `companies/${userUID}/onboardingTemplates`);
        const templatesSnap = await get(templatesRef);

        if (templatesSnap.exists()) {
          const templates = templatesSnap.val();
          const templatesArray = Object.entries(templates).map(([id, t]) => ({ id, ...t }));

          // Priority: Department-specific > General ("all") > First available
          const dept = onboardingForm.department?.toLowerCase();
          selectedTemplate = templatesArray.find(t => t.department === dept);

          if (!selectedTemplate) {
            selectedTemplate = templatesArray.find(t => t.department === 'all' || t.isDefault);
          }

          if (!selectedTemplate && templatesArray.length > 0) {
            selectedTemplate = templatesArray[0];
          }
        }
      } catch (err) {
        console.warn('Could not load templates, using placeholder:', err);
      }

      // Generate tasks
      const tasks = {};

      if (selectedTemplate?.stages) {
        // Build tasks from saved template
        Object.entries(selectedTemplate.stages).forEach(([stageName, stageData]) => {
          tasks[stageName] = { workflow: { tasks: {} } };
          const stageTasks = Array.isArray(stageData?.tasks)
            ? stageData.tasks
            : (stageData?.workflow?.tasks && typeof stageData.workflow.tasks === 'object'
              ? Object.values(stageData.workflow.tasks)
              : []);

          stageTasks.forEach((task, index) => {
            const executionMode = normalizeExecutionMode(task);
            const dueDate = new Date(startDate);
            dueDate.setDate(dueDate.getDate() + getRelativeDays(task));
            const dueDateIso = executionMode === 'immediate' ? todayIso : toIsoDate(dueDate);

            const taskId = `task_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;

            // Determine assignee
            let assignee = onboardingForm.manager || '';
            if (task.type === 'automated') {
              assignee = 'System';
            } else if (task.assigneeCategory) {
              assignee = resolveAssignee(task.assigneeCategory);
            }

	            const templateRolePerson = task.assigneeCategory ? getTemplateRolePerson(task.assigneeCategory) : null;
	            const isTemplateRoleAssignee = Boolean(templateRolePerson?.email && templateRolePerson.email === assignee);

		            const taskData = {
		              id: taskId,
		              title: task.title,
	              description: task.description || '',
	              dueDate: dueDateIso,
	              relativeDays: getRelativeDays(task),
	              status: 'not_started',
	              createdAt: nowMs,
	              updatedAt: nowMs,
	              completedAt: null,
	              completedBy: null,
		              completionSource: null,
		              assignee: assignee,
	              assigneeEmail: assignee,
	              assigneeName: task.type === 'automated' ? 'System (Auto)' : (isTemplateRoleAssignee ? (templateRolePerson.name || templateRolePerson.email) : ''),
	              assigneeDepartment: isTemplateRoleAssignee ? (templateRolePerson.department || '') : '',
	              assigneeSource: isTemplateRoleAssignee ? 'template_team' : '',
	              executionMode
	            };

            // Preserve task type and automation details
            if (task.type === 'automated' || task.automationType) {
              taskData.type = 'automated';
              taskData.automationType = task.automationType || 'document_send';
              taskData.executionMode = executionMode;
              if (task.emailDraft) taskData.emailDraft = task.emailDraft;
              if (task.formConfig) taskData.formConfig = task.formConfig;
            }

            // Preserve assignee category for human tasks
            if (task.assigneeCategory) {
              taskData.assigneeCategory = task.assigneeCategory;
            }

            tasks[stageName].workflow.tasks[taskId] = taskData;
          });
        });
      } else {
        // Use enhanced placeholder template with automated welcome email
        const enhancedPlaceholder = {
          'Before Day One': [
            // AUTOMATED: Welcome email - immediate execution
            {
              title: 'Send welcome email',
              description: 'Send personalized welcome email to new hire',
              relativeDays: 0,
              type: 'automated',
              automationType: 'document_send',
              executionMode: 'immediate',
              emailDraft: {
                subject: `Welcome to the team, ${onboardingForm.name.split(' ')[0]}!`,
                body: `Hi ${onboardingForm.name.split(' ')[0]},\n\nWe are thrilled to welcome you to our team! Your start date is confirmed for ${onboardingForm.startDate}.\n\nWe'll be in touch with more details soon.\n\nBest regards,\nThe Team`
              }
            },
            { title: 'Prepare workstation', description: 'Set up desk, computer, and necessary equipment', relativeDays: -3, assigneeCategory: 'it' },
            { title: 'Create email account', description: 'Set up company email and necessary accounts', relativeDays: -3, assigneeCategory: 'it' },
            { title: 'Prepare welcome kit', description: 'Assemble welcome kit with company swag and materials', relativeDays: -2, assigneeCategory: 'hr' },
            { title: 'Schedule orientation', description: 'Book rooms and send calendar invites for Day 1', relativeDays: -2, assigneeCategory: 'hr' },
            // AUTOMATED: Request pre-boarding documents
            {
              title: 'Request pre-boarding documents',
              description: 'Requested pre-boarding documents',
              relativeDays: -5,
              type: 'automated',
              automationType: 'document_request',
              requiredDocuments: ['Government ID', 'Bank details', 'Emergency contacts']
            }
          ],
          'Day One': [
            { title: 'Welcome and office tour', description: 'Greet new hire and show them around the office', relativeDays: 0, assigneeCategory: 'hr' },
            { title: 'IT setup', description: 'Help with computer login, software installation, and access', relativeDays: 0, assigneeCategory: 'it' },
            { title: 'Team introduction', description: 'Introduce to immediate team members', relativeDays: 0, assigneeCategory: 'manager' },
            { title: 'Review policies and handbook', description: 'Go through key company policies and expectations', relativeDays: 0, assigneeCategory: 'hr' },
            { title: 'Lunch with team', description: 'Arrange team lunch or coffee', relativeDays: 0, assigneeCategory: 'manager' }
          ],
          'First Week': [
            { title: 'Department overview meeting', description: 'Schedule meeting with department head', relativeDays: 2, assigneeCategory: 'manager' },
            { title: 'Set up 1-on-1s with key stakeholders', description: 'Schedule introductory meetings with cross-functional partners', relativeDays: 3, assigneeCategory: 'manager' },
            { title: 'Assign initial training modules', description: 'Provide access to role-specific training materials', relativeDays: 1, assigneeCategory: 'hr' },
            { title: 'Review job responsibilities', description: 'Detailed walkthrough of role expectations and goals', relativeDays: 2, assigneeCategory: 'manager' },
            { title: 'First week check-in', description: 'Scheduled feedback session to address questions', relativeDays: 5, assigneeCategory: 'manager' }
          ],
          'First Month': [
            { title: 'Set 30-day goals', description: 'Define clear objectives for the first month', relativeDays: 7, assigneeCategory: 'manager' },
            { title: 'Assign first project', description: 'Provide meaningful work assignment', relativeDays: 10, assigneeCategory: 'manager' },
            { title: 'Schedule regular 1-on-1s', description: 'Establish recurring check-in meetings', relativeDays: 7, assigneeCategory: 'manager' },
            { title: '30-day feedback session', description: 'Comprehensive check-in on progress and concerns', relativeDays: 30, assigneeCategory: 'hr' }
          ],
          '90-Day Review': [
            { title: 'Performance review preparation', description: 'Gather feedback from team and prepare review', relativeDays: 85, assigneeCategory: 'manager' },
            { title: '90-day performance review', description: 'Formal review of progress and integration', relativeDays: 90, assigneeCategory: 'hr' },
            { title: 'Set quarterly goals', description: 'Define objectives for next quarter', relativeDays: 90, assigneeCategory: 'manager' }
          ]
        };

        Object.entries(enhancedPlaceholder).forEach(([category, templateTasks]) => {
          tasks[category] = { workflow: { tasks: {} } };
          templateTasks.forEach((template, index) => {
            const executionMode = normalizeExecutionMode(template);
            const dueDate = new Date(startDate);
            dueDate.setDate(dueDate.getDate() + getRelativeDays(template));
            const dueDateIso = executionMode === 'immediate' ? todayIso : toIsoDate(dueDate);

            const taskId = `task_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;

            // Determine assignee
            let assignee = onboardingForm.manager || '';
            if (template.type === 'automated') {
              assignee = 'System';
            } else if (template.assigneeCategory) {
              assignee = resolveAssignee(template.assigneeCategory);
            }

	            const taskData = {
	              id: taskId,
	              title: template.title,
              description: template.description,
              dueDate: dueDateIso,
	              relativeDays: getRelativeDays(template),
	              status: 'not_started',
	              createdAt: nowMs,
	              updatedAt: nowMs,
	              completedAt: null,
	              completedBy: null,
	              completionSource: null,
	              assignee: assignee,
              executionMode
            };

            // Handle automated tasks
            if (template.type === 'automated') {
              taskData.type = 'automated';
              taskData.automationType = template.automationType;
              taskData.assigneeName = 'System (Auto)';
              taskData.executionMode = executionMode;
              if (template.emailDraft) taskData.emailDraft = template.emailDraft;
            } else {
              // Store category
              if (template.assigneeCategory) {
                taskData.assigneeCategory = template.assigneeCategory;
              }
            }

            tasks[category].workflow.tasks[taskId] = taskData;
          });
        });
      }

      const onboardingConnectionKey = onboardingOfferContext?.connectionKey || resolveApplicantConnectionKey(onboardingApplicant);
      let connectionData = {};
      if (onboardingConnectionKey) {
        try {
          const connectionSnap = await get(ref(database, `connections/${onboardingConnectionKey}`));
          if (connectionSnap.exists()) {
            connectionData = connectionSnap.val() || {};
          }
        } catch (connectionError) {
          console.warn('Failed to load ATS connection before onboarding handoff:', connectionError);
        }
      }
      hireId = selectOnboardingEmployeeId({
        allocatedEmployeeId: hireId,
        connectionData
      });

      // Merge into the linked forecast employee instead of replacing it. This
      // preserves employee numbers, portal state and lifecycle evidence, and—most
      // importantly—prevents one person from appearing twice in quota forecasts.
      let existingEmployee = {};
      try {
        const existingEmployeeSnap = await get(ref(database, `companies/${userUID}/employees/${hireId}`));
        existingEmployee = existingEmployeeSnap.exists() ? (existingEmployeeSnap.val() || {}) : {};
      } catch (employeeError) {
        console.warn('Failed to load linked employee before onboarding handoff:', employeeError);
        const identityError = new Error('The linked employee identity could not be verified.');
        identityError.code = 'EMPLOYEE_IDENTITY_UNVERIFIED';
        throw identityError;
      }
      if (!employeeMatchesOnboardingCandidate({
        employee: existingEmployee,
        connectionKey: onboardingConnectionKey,
        applicantId: onboardingApplicant?.id
      })) {
        // Never auto-clear or overwrite a conflicting link. An administrator must
        // inspect and deliberately repair the ATS connection so audit history and
        // a real employee record cannot be reassigned to the wrong person.
        const identityError = new Error('The ATS connection points to a different employee.');
        identityError.code = 'EMPLOYEE_IDENTITY_MISMATCH';
        throw identityError;
      }
      const hasExplicitStartConfirmation = Boolean(
        existingEmployee?.lifecycle?.actualStartConfirmedAt
        && existingEmployee?.lifecycle?.startDate
      );
      const employeeLifecycleStatus = hasExplicitStartConfirmation ? 'active' : 'onboarding';
      const currentJobSignals = selectedJobData?.data || selectedJobData || {};
      const normalizedSource = normalizeAnalyticsSource({
        utmSource: connectionData?.utm_source || connectionData?.utmSource || '',
        source: connectionData?.primarySource || connectionData?.source || '',
        sourceDetails: connectionData?.sourceDetails || '',
        sourceCategory: connectionData?.sourceCategory || '',
        sourceTags: connectionData?.sourceTags || [],
        applicantType: onboardingApplicant?.applicantType || connectionData?.applicantType || ''
      });
      const roleId = currentJobSignals?.id || currentJobSignals?.roleId || connectionData?.roleId || connectionData?.jobId || '';
      const ownerRecruiterId = currentJobSignals?.ownerRecruiterId || connectionData?.ownerRecruiterId || currentUser?.uid || '';
      const ownerRecruiterEmail = currentJobSignals?.ownerRecruiterEmail || connectionData?.ownerRecruiterEmail || currentUser?.email || '';
      const sourceOfferId = onboardingOfferContext?.offerId || connectionData?.currentOfferId || connectionData?.offerId || '';
      const sourceOfferVersion = onboardingOfferContext?.version || connectionData?.currentOfferVersion || '';
      let acceptedOfferAt = onboardingOfferContext?.acceptedOfferAt
        || connectionData?.offerAcceptedAt
        || connectionData?.acceptedOfferAt
        || '';
      let manualOfferAcceptance = false;

      if (!acceptedOfferAt) {
        const proceed = window.confirm(
          t('workspace.onboardingModal.manualAcceptanceConfirm')
        );
        if (!proceed) {
          return;
        }
        acceptedOfferAt = nowIso;
        manualOfferAcceptance = true;
      }

      if (sourceOfferId && manualOfferAcceptance) {
        await offerApi.manualAccept(userUID, sourceOfferId, acceptedOfferAt);
      }

      const hireData = {
        ...onboardingForm,
        candidateId: onboardingApplicant.id,
        applicantId: onboardingApplicant.id,
        connectionKey: onboardingConnectionKey,
        roleId,
        ownerRecruiterId,
        ownerRecruiterEmail,
        primarySource: normalizedSource.primarySource,
        sourceCategory: normalizedSource.sourceCategory,
        sourceTags: normalizedSource.sourceTags,
        location: currentJobSignals?.companyLocation || currentJobSignals?.location || connectionData?.location || '',
        tasks,
        createdAt: nowMs,
        createdBy: userUID,
        fromApplicants: true,
        employeeUID: hireId,
        status: employeeLifecycleStatus,
        lifecycleStatus: hasExplicitStartConfirmation ? 'active' : 'in_progress',
        sourceOfferId,
        sourceOfferVersion,
        offerStatus: 'accepted',
        offerSentAt: onboardingOfferContext?.offerSentAt || connectionData?.offerSentAt || '',
        offerViewedAt: onboardingOfferContext?.offerViewedAt || connectionData?.offerViewedAt || '',
        acceptedOfferAt,
        offerAcceptedAt: acceptedOfferAt,
        onboardingLaunchedAt: nowIso,
        onboardingStartedAt: nowIso,
        plannedStartDate: onboardingForm.startDate,
        actualStartDate: hasExplicitStartConfirmation ? existingEmployee.lifecycle.startDate : null,
        onboardingCompletedAt: null,
        manualOfferAcceptance,
        templateUsed: selectedTemplate?.id || 'placeholder'
      };

      const employeeRecord = buildEmployeeRecordFromHire({
        employeeId: hireId,
        companyUID: userUID,
        createdBy: userUID,
        existingEmployee,
        status: employeeLifecycleStatus,
        source: 'ats_onboarding',
        hire: {
          name: onboardingForm.name,
          personalEmail: onboardingForm.email,
          role: onboardingForm.role,
          department: onboardingForm.department,
          // For a previously confirmed employee, never overwrite the actual start
          // with a newly edited onboarding forecast date.
          startDate: hasExplicitStartConfirmation
            ? existingEmployee.lifecycle.startDate
            : onboardingForm.startDate,
		          plannedStartDate: onboardingForm.startDate,
		          actualStartDate: hasExplicitStartConfirmation ? existingEmployee.lifecycle.startDate : null,
	          acceptedOfferAt,
	          offerAcceptedAt: acceptedOfferAt,
	          onboardingLaunchedAt: nowIso,
	          onboardingStartedAt: nowIso,
	          sourceOfferId,
	          sourceOfferVersion,
	          managerUID: onboardingForm.manager,
	          buddyMentor: onboardingForm.buddyMentor,
	          candidateId: onboardingApplicant?.id,
	          applicantId: onboardingApplicant?.id,
	          connectionKey: onboardingConnectionKey,
          roleId,
          ownerRecruiterId,
          ownerRecruiterEmail,
          primarySource: normalizedSource.primarySource,
          sourceCategory: normalizedSource.sourceCategory,
          sourceTags: normalizedSource.sourceTags,
          location: currentJobSignals?.companyLocation || currentJobSignals?.location || connectionData?.location || '',
        },
      });
      const roleDepartment = [onboardingForm.role, onboardingForm.department]
        .map(val => (val || '').trim())
        .filter(Boolean)
        .join(' / ');
      if (roleDepartment) {
        employeeRecord.roleDepartment = roleDepartment;
      }
      if (onboardingForm.manager && !employeeRecord.managerId) {
        employeeRecord.managerId = onboardingForm.manager;
      }
      employeeRecord.plannedStartDate = onboardingForm.startDate;
      employeeRecord.lifecycle = {
        ...(employeeRecord.lifecycle || {}),
        plannedStartDate: onboardingForm.startDate
      };

      const inviteToken = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const employeePortalBase = (process.env.REACT_APP_EMPLOYEE_PORTAL_URL || 'http://localhost:3001').replace(/\/$/, '');
      const portalInviteLink = `${employeePortalBase}/accept-invite?token=${inviteToken}&cid=${userUID}&eid=${hireId}`;
      employeeRecord.portal = {
        ...(employeeRecord.portal || {}),
        invite: {
          ...(employeeRecord.portal?.invite || {}),
	          token: inviteToken,
	          status: 'pending',
	          generatedAt: nowMs,
	          sentAt: nowMs,
	          invitedBy: currentUser?.email || '',
	          inviteEmail: onboardingForm.email
	        }
      };

      const updates = {};
      updates[`companies/${userUID}/onboarding/${hireId}`] = hireData;
      updates[`companies/${userUID}/employees/${hireId}`] = employeeRecord;
      if (onboardingConnectionKey) {
        const connectionLifecyclePatch = {
          employeeKey: hireId,
          onboardingHireId: hireId,
          onboardingStatus: hasExplicitStartConfirmation ? 'active' : 'launched',
          onboardingLaunchedAt: nowIso,
          onboardingStartedAt: nowIso,
          plannedStartDate: onboardingForm.startDate,
          actualStartDate: hasExplicitStartConfirmation ? existingEmployee.lifecycle.startDate : null,
          sourceOfferId,
          currentOfferId: sourceOfferId,
          currentOfferVersion: sourceOfferVersion,
          offerStatus: 'accepted',
          acceptedOfferAt,
          offerAcceptedAt: acceptedOfferAt,
          manualOfferAcceptance,
          lastUpdated: nowMs
        };
        Object.entries(connectionLifecyclePatch).forEach(([key, value]) => {
          updates[`connections/${onboardingConnectionKey}/${key}`] = value;
        });

        const indexJobId = roleId || connectionData?.jobId || selectedJobData?.id || '';
        if (indexJobId) {
          Object.entries(connectionLifecyclePatch).forEach(([key, value]) => {
            updates[`companies/${userUID}/applicant_index/by_job/${indexJobId}/${onboardingConnectionKey}/${key}`] = value;
          });
        }
      }

      // Queue lifecycle delivery in the same multi-path write as the new hire.
      // This minimized marker omits wage/nationality/values, but employee ID and
      // change category are still sensitive metadata and require restricted rules.
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(updates, {
        companyUID: userUID,
        employeeId: hireId,
        source: 'jobseeqr_hire',
        // New hires enter the forecast through lifecycle state. Their official
        // occupation remains unconfirmed until the protected mapping workflow.
        changeKinds: ['lifecycle'],
        establishmentId: onboardingEstablishmentId || undefined
      });

      await update(ref(database), updates);
      let lifecycleSync = null;
      try {
        lifecycleSync = await nationalizationService.syncEmployeeLifecycle(userUID, hireId, {
          eventId: lifecycleEvent.eventId,
          source: 'jobseeqr_hire',
          changeKinds: ['lifecycle'],
          establishmentId: onboardingEstablishmentId || undefined
        });
      } catch (syncError) {
        console.warn('Nationalization lifecycle sync is pending for the new hire:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID: userUID,
        source: 'jobseeqr_hire',
        recalculated: false,
        requiresRegulatoryCompletion: lifecycleSync?.requires_regulatory_completion !== false,
        syncPending: !lifecycleSync
      });

      emitAtsAnalyticsEvent({
        company_uid: userUID,
        event_type: 'onboarding_launched_from_offer',
        occurred_at: nowIso,
        subject: {
          applicantId: onboardingApplicant?.id || '',
          connectionKey: onboardingConnectionKey || '',
          applicantType: onboardingApplicant?.applicantType || connectionData?.applicantType || (String(onboardingApplicant?.id || '').startsWith('temp_') ? 'imported' : 'authenticated')
        },
        job: {
          jobId: roleId || '',
          roleId: roleId || '',
          jobTitle: onboardingForm.role || currentJobSignals?.jobOpening || currentJobSignals?.title || '',
          department: onboardingForm.department || currentJobSignals?.department || '',
          location: currentJobSignals?.companyLocation || currentJobSignals?.location || connectionData?.location || ''
        },
        context: {
          hireId,
          sourceOfferId,
          acceptedOfferAt,
          plannedStartDate: onboardingForm.startDate,
          manualOfferAcceptance,
          ...buildActorAnalyticsContext({
            actorUserId: currentUser?.uid || '',
            actorUserEmail: currentUser?.email || '',
            ownerRecruiterId,
            ownerRecruiterEmail,
            primarySource: normalizedSource.primarySource,
            sourceCategory: normalizedSource.sourceCategory,
            sourceTags: normalizedSource.sourceTags
          })
        }
      });

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL_PROD || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        await fetch(`${backendUrl}/send-onboarding-notification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipientEmail: onboardingForm.email,
            recipientName: onboardingForm.name,
            subject: `Welcome to your employee portal, ${onboardingForm.name.split(' ')[0] || onboardingForm.name}`,
            message: 'Your onboarding portal is ready. Use the button below to activate access and complete your onboarding tasks.',
            showDashboardButton: false,
            actionButtonLabel: 'Open Employee Portal',
            actionButtonUrl: portalInviteLink
          })
        });
      } catch (inviteEmailError) {
        console.warn('Failed to send portal invite email for ATS onboarding:', inviteEmailError);
      }

      try {
        if (currentUser) {
          const backendUrl = process.env.REACT_APP_BACKEND_URL_PROD || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
          const authToken = await currentUser.getIdToken();
          const automationTasks = [];
          Object.entries(tasks).forEach(([stageName, stageData]) => {
            const workflowTasks = stageData?.workflow?.tasks || {};
            Object.values(workflowTasks).forEach((task) => {
              if (task?.type === 'automated' || task?.automationType) {
                automationTasks.push({ stage: stageName, task });
              }
            });
          });

          await Promise.all(
            automationTasks.map(async ({ stage, task }) => {
              const isImmediate = normalizeExecutionMode(task) === 'immediate';
              const response = await fetch(`${backendUrl}/onboarding/workflow/schedule-automation`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                  companyUID: userUID,
                  hireId,
                  taskId: task.id,
                  automationType: task.automationType,
                  executeAt: isImmediate ? null : task.dueDate,
                  payload: {
                    stage,
                    title: task.title,
                    executionMode: isImmediate ? 'immediate' : 'scheduled',
                    executeImmediately: isImmediate,
                    emailDraft: task.emailDraft || null,
                    formConfig: task.formConfig || null,
                    meetingConfig: task.meetingConfig || null,
                    requiredDocuments: task.requiredDocuments || [],
                    documentUploadLink: task.documentUploadLink || null,
                    attachments: task.attachments || []
                  }
                })
              });
              if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.error || `Automation scheduling failed for task ${task.id}`);
              }
            })
          );
        }
      } catch (automationScheduleError) {
        console.warn('Failed to schedule one or more ATS onboarding automations:', automationScheduleError);
      }

      // Move applicant to move-to-onboarding substage
      if (onboardingApplicant) {
        await updateApplicantPositionInFirebase(
          onboardingApplicant.id,
          'stage-1',
          'move-to-onboarding'
        );
      }

      // Keep community membership in sync for workforce immediately after onboarding move.
      try {
        const applicantType = onboardingApplicant?.applicantType || 'authenticated';
        const isImported = applicantType === 'imported';
        const connectionKey = onboardingApplicant?.connectionKey
          || (isImported ? `${userUID}_imported_${onboardingApplicant?.id}` : `${userUID}_${onboardingApplicant?.id}`);

        await syncCommunityMember(currentUser, {
          member_id: isImported
            ? `imported_${onboardingApplicant?.id}`
            : `person_${onboardingApplicant?.id}`,
          member_type: 'employee_or_onboarding',
          person_uid: isImported ? null : onboardingApplicant?.id,
          imported_profile_id: isImported ? onboardingApplicant?.id : null,
          employee_id: hireId,
          applicant_id: onboardingApplicant?.id,
          connection_key: connectionKey,
          email: onboardingForm.email,
          channels: {
            workforce: true,
          },
        });
      } catch (syncError) {
        console.warn('Community workforce sync failed during onboarding move:', syncError);
      }

      try {
        await triggerCommunityAutoSync(currentUser);
      } catch (syncError) {
        console.warn('Community auto-sync failed during onboarding move:', syncError);
      }

      showToastNotification(t('workspace.toast.onboardingMoved', { name: onboardingForm.name }));

      // Close modal and reset
	      setShowOnboardingTransition(false);
	      setOnboardingApplicant(null);
	      setOnboardingOfferContext(null);
      setOnboardingForm({
        name: '',
        email: '',
        role: '',
        department: '',
        startDate: '',
        manager: '',
        buddyMentor: ''
      });
      setOnboardingEstablishmentId(
        onboardingEstablishments.length === 1 ? onboardingEstablishments[0].id : ''
      );

    } catch (error) {
      console.error('Error adding to onboarding:', error);
      if (error?.code === 'EMPLOYEE_IDENTITY_MISMATCH' || error?.code === 'EMPLOYEE_IDENTITY_UNVERIFIED') {
        alert(t(
          'workspace.onboardingModal.employeeLinkMismatch',
          'This candidate is linked to a different or unverified employee record. Nothing was changed. Ask a workspace administrator to repair the ATS employee link, then try again.'
        ));
      } else {
        alert(t('workspace.onboardingModal.submitFailed', 'Failed to add to onboarding. Please try again.'));
      }
    }
  };

  // ──────────────────────────────────────────────────────────
  // Offer email modal helpers
  // ──────────────────────────────────────────────────────────
  const ensureQuillLoaded = () => {
    return new Promise((resolve, reject) => {
      if (window.Quill) {
        resolve();
        return;
      }

      let quillCSS = document.querySelector('link[data-offer-quill="css"]');
      if (!quillCSS) {
        quillCSS = document.createElement('link');
        quillCSS.rel = 'stylesheet';
        quillCSS.href = 'https://cdn.quilljs.com/1.3.6/quill.snow.css';
        quillCSS.setAttribute('data-offer-quill', 'css');
        document.head.appendChild(quillCSS);
      }

      const existingScript = document.querySelector('script[data-offer-quill="js"]');
      if (existingScript && existingScript.getAttribute('data-loaded') === 'true') {
        resolve();
        return;
      }

      let quillScript = existingScript;
      if (!quillScript) {
        quillScript = document.createElement('script');
        quillScript.src = 'https://cdn.quilljs.com/1.3.6/quill.js';
        quillScript.setAttribute('data-offer-quill', 'js');
        document.body.appendChild(quillScript);
      }

      quillScript.addEventListener('load', () => {
        quillScript.setAttribute('data-loaded', 'true');
        resolve();
      });
      quillScript.addEventListener('error', reject);
    });
  };

  const buildDefaultOfferSubject = (jobTitle, companyLabel) => {
    const role = jobTitle || uiText('Your Offer');
    const company = companyLabel || uiText('our team');
    return uiText('Offer for {{role}} at {{company}}', { role, company });
  };

  const buildDefaultOfferBody = (candidateName, jobTitle, companyLabel) => {
    const friendlyName = (candidateName || uiText('there')).split(' ')[0];
    const role = jobTitle || uiText('the role');
    const company = companyLabel || uiText('our team');
    return [
      uiText('<p>Hi {{name}},</p>', { name: friendlyName }),
      uiText('<p>We are thrilled to move forward with an offer for {{role}} at {{company}}.</p>', { role, company }),
      uiText('<p><strong>Next steps:</strong></p>'),
      '<ul>',
      uiText('<li>Review the attached offer letter and compensation details.</li>'),
      uiText('<li>Share any questions or updates you would like to discuss.</li>'),
      uiText('<li>Confirm your preferred start date so we can plan onboarding.</li>'),
      '</ul>',
      uiText('<p>We’re excited about the possibility of you joining us and are here to help with anything you need.</p>'),
      uiText('<p>Best regards,<br/>The {{company}} team</p>', { company })
    ].join('');
  };

  const getOfferTemplateContext = (applicant = {}) => {
    const jobTitle = selectedJobData?.data?.jobOpening || selectedJobData?.data?.title || selectedJobData?.title || applicant.jobTitle || '';
    const companyLabel = companyName || selectedJobData?.companyName || selectedJobData?.data?.companyName || '';
    return {
      candidateName: applicant?.name || applicant?.username || applicant?.applicant_name || 'Candidate',
      jobTitle,
      companyName: companyLabel
    };
  };

  const buildOfferTemplateContent = (applicant = {}) => {
    const context = getOfferTemplateContext(applicant);
    const fallbackSubject = buildDefaultOfferSubject(context.jobTitle, context.companyName);
    const fallbackBody = buildDefaultOfferBody(context.candidateName, context.jobTitle, context.companyName);
    return {
      subject: applyOfferPlaceholders(offerTemplate?.subject || fallbackSubject, context),
      messageHTML: applyOfferPlaceholders(normalizeOfferTemplateGreeting(offerTemplate?.messageHTML || fallbackBody), context)
    };
  };

  const buildDefaultOfferFollowUpsForApplicant = (applicant = {}) => {
    const context = getOfferTemplateContext(applicant);
    return createDefaultOfferFollowUps().map((item, index) => ({
      ...item,
      subject: applyOfferPlaceholders(item.subject, { ...context, sequence: String(index + 1) }),
      messageHTML: applyOfferPlaceholders(item.messageHTML, { ...context, sequence: String(index + 1) })
    }));
  };

  const applySavedOfferTemplate = () => {
    const content = buildOfferTemplateContent(offerModalApplicants[0] || {});
    setOfferSubject(content.subject);
    setOfferMessageHTML(content.messageHTML);
    if (offerQuillRef.current) {
      const delta = offerQuillRef.current.clipboard.convert(content.messageHTML || '');
      offerQuillRef.current.setContents(delta, 'silent');
    } else {
      offerResetRef.current = true;
    }
  };

  const saveCurrentOfferTemplate = async () => {
    if (!userUID) return;
    const messageHTML = offerQuillRef.current ? offerQuillRef.current.root.innerHTML : offerMessageHTML;
    const template = {
      subject: tokenizeOfferRecipient(offerSubject, getOfferTemplateContext(offerModalApplicants[0]).candidateName),
      messageHTML: tokenizeOfferRecipient(messageHTML, getOfferTemplateContext(offerModalApplicants[0]).candidateName),
      updatedAt: Date.now(),
      updatedBy: {
        uid: currentUser?.uid || '',
        email: currentUser?.email || ''
      }
    };

    try {
      await set(ref(database, `companies/${userUID}/settings/offerTemplate`), template);
      setOfferTemplate(template);
      showToastNotification('Offer template saved.');
    } catch (error) {
      console.error('Failed to save offer template:', error);
      alert(uiText('Failed to save the offer template.'));
    }
  };

  const normalizeOfferFollowUpItems = (followUps = {}, fallbackItems = createDefaultOfferFollowUps()) => {
    const sourceItems = Array.isArray(followUps.items) && followUps.items.length
      ? followUps.items
      : Array.isArray(followUps.schedule) && followUps.schedule.length
        ? followUps.schedule
        : fallbackItems;

    return sourceItems.map((item, index) => {
      const day = Number.parseInt(item.daysAfterSend ?? item.day ?? item.days, 10);
      const safeDay = Number.isFinite(day) && day > 0 ? day : DEFAULT_OFFER_FOLLOW_UP_DAYS[index] || ((index + 1) * 3);
      return {
        id: item.id || `day_${safeDay}`,
        daysAfterSend: safeDay,
        subject: item.subject || followUps.subject || uiText(DEFAULT_OFFER_FOLLOW_UP_SUBJECT).replace('{{sequence}}', String(index + 1)),
        messageHTML: item.messageHTML || item.messageHtml || followUps.messageHTML || followUps.messageHtml || buildDefaultOfferFollowUpMessage(index + 1)
      };
    });
  };

  const updateOfferFollowUp = (id, patch) => {
    setOfferFollowUps((prev) => prev.map((item) => (
      item.id === id ? { ...item, ...patch } : item
    )));
  };

  const addOfferFollowUp = () => {
    setOfferFollowUps((prev) => {
      const maxDay = prev.reduce((max, item) => Math.max(max, Number.parseInt(item.daysAfterSend, 10) || 0), 0);
      const nextIndex = prev.length + 1;
      const nextDay = maxDay > 0 ? maxDay + 3 : DEFAULT_OFFER_FOLLOW_UP_DAYS[0];
      return [
        ...prev,
        {
          id: `day_${nextDay}_${Date.now()}`,
          daysAfterSend: nextDay,
          subject: uiText(DEFAULT_OFFER_FOLLOW_UP_SUBJECT).replace('{{sequence}}', String(nextIndex)),
          messageHTML: buildDefaultOfferFollowUpMessage(nextIndex)
        }
      ];
    });
  };

  const removeOfferFollowUp = (id) => {
    delete offerFollowUpQuillRefs.current[id];
    delete offerFollowUpEditorRefs.current[id];
    setOfferFollowUps((prev) => prev.length > 1 ? prev.filter((item) => item.id !== id) : prev);
  };

  const buildStructuredFollowUps = (context = {}) => {
    const normalizedItems = normalizeOfferFollowUpItems({ items: offerFollowUps }, offerFollowUps)
      .sort((left, right) => left.daysAfterSend - right.daysAfterSend)
      .map((item, index) => {
        const editor = offerFollowUpQuillRefs.current[item.id];
        const messageHTML = editor ? editor.root.innerHTML : item.messageHTML;
        const itemContext = { ...context, sequence: String(index + 1) };
        return {
          id: item.id || `day_${item.daysAfterSend}`,
          daysAfterSend: item.daysAfterSend,
          subject: applyOfferPlaceholders(item.subject, itemContext),
          messageHTML: applyOfferPlaceholders(messageHTML, itemContext)
        };
      });

    return {
      enabled: offerFollowUpsEnabled,
      cadenceDays: normalizedItems.map((item) => item.daysAfterSend),
      schedule: normalizedItems,
      items: normalizedItems,
      stopOnCandidateResponse: true
    };
  };

  const toOfferDateInput = (dateValue) => {
    if (!dateValue) return '';
    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
    }
    const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateInputFromOffset = (daysFromNow) => {
    const dateObj = new Date();
    dateObj.setDate(dateObj.getDate() + daysFromNow);
    return toOfferDateInput(dateObj);
  };

  const getOfferTimelineError = (terms = offerTerms) => {
    const expiresAt = toOfferDateInput(terms.expiresAt);
    const startDate = toOfferDateInput(terms.startDate);
    if (expiresAt && startDate && expiresAt > startDate) {
      return t(
        'workspace.offerModal.dateOrderError',
        'Offer expiration must be on or before the start date.'
      );
    }
    return '';
  };

  const offerTimelineError = getOfferTimelineError();

  const validateOfferTimeline = () => {
    const timelineError = getOfferTimelineError();
    if (timelineError) {
      alert(timelineError);
      return false;
    }
    return true;
  };

  const resolveApplicantConnectionKey = (applicant) => {
    if (!applicant) return '';
    if (applicant.connectionKey) return applicant.connectionKey;
    if (!userUID || !applicant.id) return '';
    return applicant.applicantType === 'imported'
      ? `${userUID}_imported_${applicant.id}`
      : `${userUID}_${applicant.id}`;
  };

  const resolveApplicantConnectionContext = async (applicant) => {
    if (!userUID || !applicant) {
      return { connectionKey: '', connectionData: {} };
    }

    const candidateId = applicant.id || applicant.applicantId || applicant.candidateId || '';
    const currentJobSignals = selectedJobData?.data || selectedJobData || {};
    const selectedJobId = selectedJobData?.id
      || currentJobSignals?.id
      || applicant.jobId
      || applicant.roleId
      || '';
    const candidateKeys = new Set();
    const addKey = (key) => {
      if (key && typeof key === 'string') candidateKeys.add(key);
    };

    addKey(applicant.connectionKey);
    if (candidateId && String(candidateId).startsWith(`${userUID}_`)) {
      addKey(candidateId);
    }
    if (candidateId) {
      if (applicant.applicantType === 'imported' || String(candidateId).startsWith('temp_')) {
        addKey(`${userUID}_imported_${candidateId}`);
      }
      addKey(`${userUID}_${candidateId}`);
      addKey(`${userUID}_imported_${candidateId}`);
    }

    const readConnection = async (connectionKey) => {
      if (!connectionKey) return null;
      try {
        const connectionSnap = await get(ref(database, `connections/${connectionKey}`));
        if (connectionSnap.exists()) {
          return { connectionKey, connectionData: connectionSnap.val() || {} };
        }
      } catch (error) {
        console.warn('Could not resolve applicant connection:', connectionKey, error);
      }
      return null;
    };

    for (const key of candidateKeys) {
      const resolved = await readConnection(key);
      if (resolved) return resolved;
    }

    const matchesApplicant = (entry = {}, key = '') => {
      if (!candidateId) return false;
      return key === candidateId
        || entry.connectionKey === candidateId
        || entry.applicantId === candidateId
        || entry.candidateId === candidateId
        || entry.jobSeekerUid === candidateId
        || entry.tempProfileId === candidateId;
    };

    const inspectIndexBucket = async (jobId) => {
      if (!jobId) return null;
      try {
        const bucketSnap = await get(ref(database, `companies/${userUID}/applicant_index/by_job/${jobId}`));
        if (!bucketSnap.exists()) return null;
        const bucket = bucketSnap.val() || {};
        for (const [connectionKey, entry] of Object.entries(bucket)) {
          if (matchesApplicant(entry, connectionKey)) {
            const resolved = await readConnection(connectionKey);
            return resolved || { connectionKey, connectionData: entry || {} };
          }
        }
      } catch (error) {
        console.warn('Could not inspect applicant index for connection:', error);
      }
      return null;
    };

    const selectedBucketMatch = await inspectIndexBucket(selectedJobId);
    if (selectedBucketMatch) return selectedBucketMatch;

    try {
      const allIndexSnap = await get(ref(database, `companies/${userUID}/applicant_index/by_job`));
      if (allIndexSnap.exists()) {
        const byJob = allIndexSnap.val() || {};
        for (const bucket of Object.values(byJob)) {
          for (const [connectionKey, entry] of Object.entries(bucket || {})) {
            if (matchesApplicant(entry, connectionKey)) {
              const resolved = await readConnection(connectionKey);
              return resolved || { connectionKey, connectionData: entry || {} };
            }
          }
        }
      }
    } catch (error) {
      console.warn('Could not inspect global applicant index for connection:', error);
    }

    return {
      connectionKey: candidateKeys.values().next().value || '',
      connectionData: {}
    };
  };

  const buildDefaultOfferTerms = (applicant = {}) => ({
    ...createDefaultOfferTerms(),
    employmentType: applicant.employmentType || selectedJobData?.data?.employmentType || selectedJobData?.employmentType || 'Full-time',
    workMode: applicant.workMode || selectedJobData?.data?.workMode || selectedJobData?.workMode || selectedJobData?.data?.remoteType || 'On-site',
    startDate: applicant.startDate || applicant.expectedStartDate || dateInputFromOffset(21),
    expiresAt: applicant.offerExpiresAt || dateInputFromOffset(7)
  });

  const mapOfferAttachmentsForBackend = (attachments = offerAttachments) => (
    (Array.isArray(attachments) ? attachments : []).map((att) => ({
      name: att.name,
      content_base64: att.content_base64 || att.content,
      mime_type: att.mime_type || att.type || 'application/octet-stream',
      size: att.size || 0
    }))
  );

  const loadOfferForApplicant = async (applicant, knownConnectionData = null) => {
    if (!userUID || !applicant) return { offer: null, offerId: '', connectionKey: '', connectionData: knownConnectionData || {} };

    const connectionKey = resolveApplicantConnectionKey(applicant);
    let connectionData = knownConnectionData || {};

    if (connectionKey && !knownConnectionData) {
      try {
        const connectionSnap = await get(ref(database, `connections/${connectionKey}`));
        if (connectionSnap.exists()) {
          connectionData = connectionSnap.val() || {};
        }
      } catch (error) {
        console.warn('Failed to load applicant connection for offer context:', error);
      }
    }

    const offerId = applicant.currentOfferId || connectionData.currentOfferId || connectionData.offerId || '';
    if (!offerId) {
      return { offer: null, offerId: '', connectionKey, connectionData };
    }

    try {
      const offerSnap = await get(ref(database, `companies/${userUID}/offers/${offerId}`));
      if (offerSnap.exists()) {
        return { offer: { id: offerId, ...offerSnap.val() }, offerId, connectionKey, connectionData };
      }
    } catch (error) {
      console.warn('Failed to load linked offer record:', error);
    }

    return { offer: null, offerId, connectionKey, connectionData };
  };

  const buildStructuredOfferPayload = async (applicant, offerData = {}) => {
    const applicantName = applicant?.name || applicant?.username || applicant?.applicant_name || 'Candidate';
    const applicantEmail = await resolveApplicantEmail(applicant);
    if (!applicantEmail) {
      throw new Error(`${applicantName} is missing an email address`);
    }

    const { connectionKey, connectionData } = await resolveApplicantConnectionContext(applicant);
    const candidateId = applicant?.id
      || applicant?.applicantId
      || applicant?.candidateId
      || connectionData?.candidateId
      || connectionData?.jobSeekerUid
      || connectionData?.tempProfileId
      || '';
    const applicantType = applicant?.applicantType
      || connectionData?.applicantType
      || (String(candidateId || '').startsWith('temp_') ? 'imported' : 'authenticated');
    const jobSignals = selectedJobData?.data || selectedJobData || {};
    const jobId = selectedJobData?.id
      || jobSignals?.id
      || applicant?.jobId
      || connectionData?.jobId
      || connectionData?.roleId
      || (typeof currActiveJob === 'object' ? currActiveJob?.id : '')
      || '';
    const jobTitle = jobSignals?.jobOpening || jobSignals?.title || selectedJobData?.title || applicant?.jobTitle || '';
    const companyLabel = companyName || selectedJobData?.companyName || jobSignals?.companyName || '';
    const listedSalary = getJobListedSalary(jobSignals, selectedJobData, currActiveJob, connectionData?.job);
    const terms = {
      ...createDefaultOfferTerms(),
      ...offerTerms,
      ...(offerData.terms || {})
    };
    const templateContext = getOfferTemplateContext(applicant);
    const followUps = offerData.followUps || buildStructuredFollowUps(templateContext);
    const normalizedAttachments = mapOfferAttachmentsForBackend(offerData.attachments || offerAttachments);
    const previewName = getOfferTemplateContext(offerModalApplicants[0]).candidateName;
    const personalize = value => applyOfferPlaceholders(tokenizeOfferRecipient(value, previewName), templateContext);
    const messageHTML = personalize(offerData.messageHTML || offerMessageHTML);
    const messageText = personalize(offerData.messageText || '');

    return {
      id: activeOfferId || offerData.offerId || undefined,
      companyName: companyLabel,
      candidate: {
        id: candidateId,
        name: applicantName,
        email: applicantEmail,
        applicantType,
        connectionKey
      },
      job: {
        jobId,
        roleId: applicant?.roleId || connectionData?.roleId || jobSignals?.roleId || jobId,
        title: jobTitle,
        listedSalary,
        salary: listedSalary,
        department: jobSignals?.department || applicant?.department || connectionData?.department || '',
        location: jobSignals?.companyLocation || jobSignals?.location || applicant?.location || connectionData?.location || '',
        ownerRecruiterId: jobSignals?.ownerRecruiterId || applicant?.ownerRecruiterId || connectionData?.ownerRecruiterId || currentUser?.uid || '',
        ownerRecruiterEmail: jobSignals?.ownerRecruiterEmail || applicant?.ownerRecruiterEmail || connectionData?.ownerRecruiterEmail || currentUser?.email || ''
      },
      terms,
      email: {
        subject: personalize(offerData.subject || offerSubject || buildDefaultOfferSubject(jobTitle, companyLabel)),
        messageHTML,
        messageText: messageText?.trim ? messageText.trim() : messageText,
        attachments: normalizedAttachments
      },
      documents: {
        attachments: normalizedAttachments.map(({ content_base64, ...metadata }) => metadata)
      },
      approval: {
        note: offerApprovalNote || offerData.approvalNote || ''
      },
      followUps,
      targetStage: offerTargetStage,
      targetSubstage: offerTargetSubstage,
      source: 'applicants_tab'
    };
  };

  const buildApprovalFromOfferRecord = (offer = {}) => {
    const candidate = offer.candidate || {};
    const job = offer.job || {};
    const candidateId = candidate.id || candidate.applicantId || candidate.candidateId || '';
    const attachments = offer.email?.attachments || offer.documents?.attachments || [];
    const listedSalary = getJobListedSalary(job);

    return {
      id: offer.approval?.approvalId || `offer_${offer.offerId}`,
      type: 'offer_letter',
      status: offer.status === 'pending_approval' ? 'pending' : offer.status,
      submittedBy: offer.approval?.submittedBy || offer.createdBy || '',
      submittedAt: offer.approval?.submittedAt || offer.updatedAt || offer.createdAt || Date.now(),
      entityId: candidateId,
      entityName: candidate.name || 'Candidate',
      snapshot: {
        title: uiText('Offer for {{name}}', { name: candidate.name || uiText('Candidate') }),
        jobTitle: job.title || job.jobTitle || '',
        candidates: 1
      },
      metadata: {
        offerId: offer.offerId,
        applicants: [{
          id: candidateId,
          name: candidate.name || 'Candidate',
          email: candidate.email || '',
          connectionKey: candidate.connectionKey || '',
          applicantType: candidate.applicantType || 'authenticated'
        }],
        subject: offer.email?.subject || '',
        messageHTML: offer.email?.messageHTML || '',
        messageText: offer.email?.messageText || '',
        attachments,
        terms: offer.terms || {},
        followUps: offer.followUps || {},
        approvalNote: offer.approval?.note || '',
        companyName: offer.companyName || companyName || '',
        jobId: job.jobId || job.roleId || '',
        jobTitle: job.title || job.jobTitle || '',
        listedSalary,
        job: {
          ...job,
          listedSalary
        },
        targetStage: offer.targetStage || 'stage-1',
        targetSubstage: offer.targetSubstage || 'offer-sent',
        ownerRecruiterId: job.ownerRecruiterId || '',
        ownerRecruiterEmail: job.ownerRecruiterEmail || ''
      }
    };
  };

  const handleReviewOfferFromHub = (offer) => {
    const approval = pendingApprovalByOfferId.get(offer.offerId) || buildApprovalFromOfferRecord(offer);
    openOfferReviewModal(approval);
  };

  const handleApproveOfferFromHub = async (offer) => {
    if (!offer?.offerId || !canReviewOffer) return;
    setOffersHubActionBusy(`approve:${offer.offerId}`);
    try {
      await offerApi.approveAndSend(userUID, offer.offerId, 'Approved from Offers hub');
      const candidateId = offer.candidate?.id || offer.candidate?.applicantId || offer.candidate?.candidateId || '';
      if (candidateId) {
        setApplicantsData((prev) =>
          prev.map((applicant) =>
            applicant.id === candidateId
              ? { ...applicant, stageId: 'stage-1', substageId: 'offer-sent' }
              : applicant
          )
        );
      }
      showToastNotification('Offer approved and sent.');
    } catch (error) {
      console.error('Failed to approve offer from hub:', error);
      alert(error.message || 'Failed to approve and send the offer.');
    } finally {
      setOffersHubActionBusy('');
    }
  };

  const handleRejectOfferFromHub = async (offer, reason) => {
    if (!offer?.offerId || !canReviewOffer) return;
    setOffersHubActionBusy(`reject:${offer.offerId}`);
    try {
      await offerApi.rejectApproval(userUID, offer.offerId, reason);
      showToastNotification('Offer approval rejected.');
    } catch (error) {
      console.error('Failed to reject offer from hub:', error);
      alert(error.message || 'Failed to reject the offer.');
    } finally {
      setOffersHubActionBusy('');
    }
  };

  const handleSaveDraftOfferFromHub = async (offer) => {
    if (!offer?.offerId || !canDraftOffer) return;
    setOffersHubActionBusy(`save:${offer.offerId}`);
    try {
      await offerApi.saveDraft(userUID, offer, offer.offerId);
      showToastNotification('Offer draft saved.');
    } catch (error) {
      console.error('Failed to save offer draft from hub:', error);
      alert(error.message || 'Failed to save the offer draft.');
      throw error;
    } finally {
      setOffersHubActionBusy('');
    }
  };

  const handleSubmitDraftOfferFromHub = async (offer) => {
    if (!offer?.offerId || !canDraftOffer) return;
    if (!mustSubmitForApproval) {
      alert(uiText('This user can send the offer to the candidate directly.'));
      return;
    }

    setOffersHubActionBusy(`submit:${offer.offerId}`);
    try {
      await offerApi.submitForApproval(userUID, offer, offer.offerId);
      showToastNotification('Offer pushed for approval.');
    } catch (error) {
      console.error('Failed to submit draft offer for approval:', error);
      alert(error.message || 'Failed to push the offer for approval.');
      throw error;
    } finally {
      setOffersHubActionBusy('');
    }
  };

  const handleSendDraftOfferFromHub = async (offer) => {
    if (!offer?.offerId || !canDraftOffer) return;
    if (!canSendOfferToCandidate) {
      alert(uiText('Offer approval is required before this offer can be sent.'));
      return;
    }

    setOffersHubActionBusy(`send:${offer.offerId}`);
    try {
      const result = await offerApi.send(userUID, offer, offer.offerId);
      const sentOffer = result?.offer || offer;
      const candidateId = sentOffer.candidate?.id || sentOffer.candidate?.applicantId || sentOffer.candidate?.candidateId || '';
      const targetStageId = sentOffer.targetStage || offer.targetStage || 'stage-1';
      const targetSubstageId = sentOffer.targetSubstage || offer.targetSubstage || 'offer-sent';

      if (candidateId) {
        setApplicantsData((prev) =>
          prev.map((applicant) =>
            applicant.id === candidateId
              ? { ...applicant, stageId: targetStageId, substageId: targetSubstageId }
              : applicant
          )
        );
      }

      showToastNotification('Offer sent to candidate.');
    } catch (error) {
      console.error('Failed to send draft offer from hub:', error);
      alert(error.message || 'Failed to send the offer.');
      throw error;
    } finally {
      setOffersHubActionBusy('');
    }
  };

  const handleOpenOfferApplicant = (offer) => {
    const candidate = offer?.candidate || {};
    const applicantId = candidate.id || candidate.applicantId || candidate.candidateId;
    if (!applicantId) return;
    const applicant = applicantsData.find((item) => item.id === applicantId) || {
      id: applicantId,
      name: candidate.name || 'Candidate',
      email: candidate.email || '',
      applicantType: candidate.applicantType || 'authenticated',
      connectionKey: candidate.connectionKey || ''
    };
    onOpenApplicantPopup(applicant);
  };

  const openOfferEmailModal = (applicants = [], targetStageId = 'stage-1', targetSubstageId = 'offer-sent') => {
    if (!applicants || applicants.length === 0) return;
    if (!canDraftOffer) {
      alert(uiText('You do not have permission to draft offers.'));
      return;
    }
    const templateContent = buildOfferTemplateContent(applicants[0]);

    setOfferSubject(templateContent.subject);
    setOfferMessageHTML(templateContent.messageHTML);
    offerResetRef.current = true;
    setOfferModalApplicants(applicants);
    setOfferTargetStage(targetStageId);
    setOfferTargetSubstage(targetSubstageId);
    setOfferAttachments([]);
    setActiveOfferId('');
    setOfferTerms(buildDefaultOfferTerms(applicants[0]));
    setOfferContextListedSalary(getJobListedSalary(selectedJobData, currActiveJob));
    setOfferFollowUpsEnabled(true);
    setOfferFollowUps(buildDefaultOfferFollowUpsForApplicant(applicants[0]));
    setOfferApprovalNote('');
    setOfferRejectionReason('');
    setOfferReviewMode(false);
    setReviewApprovalData(null);
    setShowOfferEmailModal(true);
  };

  const handleOfferAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result;
              const base64Content = typeof result === 'string' && result.includes(',')
                ? result.split(',')[1]
                : result;
              resolve({
                name: file.name,
                content: base64Content,
                type: file.type || 'application/octet-stream',
                size: file.size || 0
              });
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
          })
      )
    )
      .then((processed) => setOfferAttachments((prev) => [...prev, ...processed]))
      .catch((error) => console.error('Error reading attachment:', error));
  };

  const handleRemoveOfferAttachment = (index) => {
    setOfferAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!showOfferEmailModal) return;
    let cancelled = false;

    const setOfferEditorContent = (quill, html) => {
      const delta = quill.clipboard.convert(html || '');
      quill.setContents(delta, 'silent');
    };

    const loadEditor = async () => {
      try {
        await ensureQuillLoaded();
        if (cancelled || !offerEditorRef.current) return;

        if (!offerQuillRef.current) {
          const quill = new window.Quill(offerEditorRef.current, {
            theme: 'snow',
            placeholder: 'Draft your offer email...',
            modules: {
              toolbar: [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['clean']
              ]
            }
          });
          quill.on('text-change', () => {
            setOfferMessageHTML(quill.root.innerHTML);
          });
          quill.enable(!offerFieldsReadOnly);
          offerQuillRef.current = quill;
          if (offerResetRef.current) {
            setOfferEditorContent(quill, offerMessageHTML);
            offerResetRef.current = false;
          }
        } else if (offerResetRef.current) {
          setOfferEditorContent(offerQuillRef.current, offerMessageHTML);
          offerResetRef.current = false;
        } else {
          offerQuillRef.current.enable(!offerFieldsReadOnly);
        }
      } catch (error) {
        console.error('Failed to load offer editor:', error);
      }
    };

    loadEditor();
    return () => {
      cancelled = true;
    };
  }, [showOfferEmailModal, offerFieldsReadOnly, offerMessageHTML]);

  useEffect(() => {
    if (!showOfferEmailModal || !offerFollowUpsEnabled) return;
    let cancelled = false;

    const setEditorContent = (quill, html) => {
      const delta = quill.clipboard.convert(html || '');
      quill.setContents(delta, 'silent');
    };

    const loadFollowUpEditors = async () => {
      try {
        await ensureQuillLoaded();
        if (cancelled) return;

        offerFollowUps.forEach((followUp) => {
          const editorNode = offerFollowUpEditorRefs.current[followUp.id];
          if (!editorNode || offerFollowUpQuillRefs.current[followUp.id]) return;

          const quill = new window.Quill(editorNode, {
            theme: 'snow',
            placeholder: 'Draft the reminder email...',
            modules: {
              toolbar: [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['clean']
              ]
            }
          });
          setEditorContent(quill, followUp.messageHTML);
          quill.on('text-change', () => {
            updateOfferFollowUp(followUp.id, { messageHTML: quill.root.innerHTML });
          });
          quill.enable(!offerFieldsReadOnly);
          offerFollowUpQuillRefs.current[followUp.id] = quill;
        });

        Object.values(offerFollowUpQuillRefs.current).forEach((quill) => {
          quill.enable(!offerFieldsReadOnly);
        });
      } catch (error) {
        console.error('Failed to load offer follow-up editors:', error);
      }
    };

    loadFollowUpEditors();
    return () => {
      cancelled = true;
    };
  }, [showOfferEmailModal, offerFollowUpsEnabled, offerFollowUps, offerFieldsReadOnly]);

  useEffect(() => {
    if (!showOfferEmailModal && (offerQuillRef.current || Object.keys(offerFollowUpQuillRefs.current).length > 0)) {
      // Drop the reference so a fresh editor mounts next time, preventing phantom unmounted nodes
      offerQuillRef.current = null;
      offerFollowUpQuillRefs.current = {};
      offerFollowUpEditorRefs.current = {};
    }
  }, [showOfferEmailModal]);

  const resolveApplicantEmail = async (applicant) => {
    if (!applicant) return '';
    if (applicant.email) return applicant.email;
    if (applicant.applicantEmail) return applicant.applicantEmail;
    if (!applicant.id) return '';

    const lookupConnectionEmail = async (key) => {
      if (!key) return '';
      try {
        const connSnap = await get(ref(database, `connections/${key}`));
        if (connSnap.exists()) {
          const connData = connSnap.val() || {};
          return connData.applicantEmail || connData.email || '';
        }
      } catch (e) {
        console.warn('Could not read connection for email:', e);
      }
      return '';
    };

    let connectionKey = applicant.connectionKey || '';
    if (!connectionKey) {
      if (applicant.applicantType === 'imported') {
        connectionKey = `${userUID}_imported_${applicant.id}`;
      } else {
        connectionKey = `${userUID}_${applicant.id}`;
      }
    }

    let email = await lookupConnectionEmail(connectionKey);
    if (!email && applicant.applicantType !== 'imported') {
      const importedConnectionKey = `${userUID}_imported_${applicant.id}`;
      if (importedConnectionKey !== connectionKey) {
        email = await lookupConnectionEmail(importedConnectionKey);
        if (email) {
          connectionKey = importedConnectionKey;
        }
      }
    }
    if (email) return email;

    const shouldCheckImported = applicant.applicantType === 'imported' || !applicant.applicantType;
    if (shouldCheckImported) {
      try {
        let importedRef = ref(database, `imported_profiles/${userUID}/${applicant.id}`);
        let importedSnap = await get(importedRef);

        if (!importedSnap.exists()) {
          importedRef = ref(database, `imported_profiles/${applicant.id}`);
          importedSnap = await get(importedRef);
        }

        if (importedSnap.exists()) {
          const importedData = importedSnap.val() || {};
          if (importedData.email) return importedData.email;
        }
      } catch (e) { }
    }

    if (applicant.applicantType !== 'imported') {
      try {
        const personSnap = await get(ref(database, `people/${applicant.id}`));
        if (personSnap.exists()) {
          const personData = personSnap.val() || {};
          if (personData.email) return personData.email;
        }
      } catch (e) { }

      // Fallback cloud function for authenticated users
      try {
        const emailResp = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: applicant.id })
        });
        const emailData = await emailResp.json();
        if (emailData.email) return emailData.email;
      } catch (e) {
        console.warn('Could not fetch email via cloud function:', e);
      }
    }

    return '';
  };

  const getCurrentOfferModalData = () => {
    const quillHTML = offerQuillRef.current ? offerQuillRef.current.root.innerHTML : offerMessageHTML;
    const quillText = offerQuillRef.current ? offerQuillRef.current.getText() : '';

    return {
      subject: offerSubject,
      messageHTML: quillHTML,
      messageText: quillText?.trim() ? quillText : '',
      attachments: offerAttachments,
      terms: offerTerms,
      approvalNote: offerApprovalNote
    };
  };

  const resetOfferModalAfterMutation = () => {
    setShowOfferEmailModal(false);
    setOfferModalApplicants([]);
    setOfferAttachments([]);
    setOfferMessageHTML('');
    setActiveOfferId('');
    setOfferContextListedSalary('');
    setOfferRejectionReason('');
  };

  const handleSaveOfferDraft = async () => {
    if (!canDraftOffer) {
      alert(uiText('You do not have permission to draft offers.'));
      return;
    }
    if (!offerModalApplicants || offerModalApplicants.length === 0) {
      setShowOfferEmailModal(false);
      return;
    }

    setOfferActionMode('saving');
    setOfferSending(true);
    try {
      const failures = [];
      const saved = [];
      const offerData = getCurrentOfferModalData();

      for (const applicant of offerModalApplicants) {
        const applicantName = applicant?.name || applicant?.username || 'Candidate';
        try {
          const payload = await buildStructuredOfferPayload(applicant, offerData);
          const result = await offerApi.saveDraft(
            userUID,
            payload,
            offerModalApplicants.length === 1 ? (activeOfferId || null) : null
          );
          saved.push(result);
        } catch (error) {
          console.error('Error saving offer draft:', error);
          failures.push(`${applicantName} (${error.message || 'draft save failed'})`);
        }
      }

      if (saved.length === 0) {
        throw new Error(failures.join(', ') || 'No offer drafts were saved');
      }

      if (failures.length > 0) {
        showToastNotification(t('workspace.toast.offerIssues', { count: failures.length, issues: failures.join(', ') }));
      } else {
        showToastNotification(t('workspace.toast.offerDraftSaved', 'Offer draft saved.'));
      }

      resetOfferModalAfterMutation();
    } catch (error) {
      console.error('Error saving offer draft:', error);
      alert(error.message || 'Failed to save offer draft. Please try again.');
    } finally {
      setOfferSending(false);
      setOfferActionMode('');
    }
  };

  // Submit offer for approval instead of sending directly
  const submitOfferForApproval = async (applicants, offerData) => {
    if (!canDraftOffer) {
      alert(uiText('You do not have permission to submit offers for approval.'));
      return false;
    }
    setOfferSending(true);
    setOfferActionMode('submitting');
    try {
      const failures = [];
      const submitted = [];

      for (const applicant of applicants) {
        const applicantName = applicant?.name || applicant?.username || 'Candidate';
        try {
          const payload = await buildStructuredOfferPayload(applicant, offerData);
          const result = await offerApi.submitForApproval(
            userUID,
            payload,
            applicants.length === 1 ? (activeOfferId || null) : null
          );
          submitted.push(result);
        } catch (error) {
          console.error('Error submitting structured offer for approval:', error);
          failures.push(`${applicantName} (${error.message || 'approval submit failed'})`);
        }
      }

      if (submitted.length === 0) {
        throw new Error(failures.join(', ') || 'No offers were submitted');
      }

      if (failures.length > 0) {
        showToastNotification(t('workspace.toast.offerIssues', { count: failures.length, issues: failures.join(', ') }));
      } else {
        showToastNotification(t('workspace.toast.offerSubmittedForApproval', 'Offer submitted for approval. An approver will review it shortly.'));
      }

      resetOfferModalAfterMutation();

      return failures.length === 0;
    } catch (error) {
      console.error('Error submitting offer for approval:', error);
      alert(uiText('Failed to submit offer for approval. Please try again.'));
      return false;
    } finally {
      setOfferSending(false);
      setOfferActionMode('');
    }
  };

  const handleSendOfferEmail = async () => {
    if (!offerModalApplicants || offerModalApplicants.length === 0) {
      setShowOfferEmailModal(false);
      return;
    }

    if (!canDraftOffer) {
      alert(uiText('You do not have permission to draft or send offers.'));
      return;
    }

    if (!validateOfferTimeline()) {
      return;
    }

    // APPROVAL WORKFLOW: Check if approvals are required and user lacks bypass permission
    if (mustSubmitForApproval) {
      await submitOfferForApproval(offerModalApplicants, getCurrentOfferModalData());
      return;
    }

    try {
      setOfferSending(true);
      setOfferActionMode('sending');
      const offerData = getCurrentOfferModalData();

      const failures = [];
      const successfulOfferSends = [];
      for (const applicant of offerModalApplicants) {
        const applicantName = applicant.name || applicant.username || applicant.applicant_name || 'Candidate';

        try {
          const payload = await buildStructuredOfferPayload(applicant, {
            ...offerData,
            subject: offerData.subject,
            messageHTML: offerData.messageHTML,
            messageText: offerData.messageText,
            attachments: offerAttachments
          });
          const result = await offerApi.send(
            userUID,
            payload,
            offerModalApplicants.length === 1 ? (activeOfferId || null) : null
          );

          successfulOfferSends.push({
            applicant,
            id: applicant.id,
            connectionKey: result?.offer?.candidate?.connectionKey || applicant.connectionKey || '',
            applicantType: applicant.applicantType || (String(applicant.id || '').startsWith('temp_') ? 'imported' : 'authenticated'),
            applicantName,
            offerId: result?.offerId || ''
          });
        } catch (error) {
          console.error('Error sending structured offer:', error);
          failures.push(`${applicantName} (${error.message || 'offer send failed'})`);
        }
      }

      if (successfulOfferSends.length === 0) {
        showToastNotification(t('workspace.toast.offerFailed', {
          issues: failures.join(', ') || 'No offers were sent.'
        }));
        return;
      }

      setApplicantsData((prev) =>
        prev.map((app) =>
          successfulOfferSends.some((a) => a.id === app.id)
            ? { ...app, stageId: offerTargetStage, substageId: offerTargetSubstage }
            : app
        )
      );

      if (onClearSelection) {
        onClearSelection();
      }

      if (failures.length > 0) {
        showToastNotification(t('workspace.toast.offerIssues', { count: failures.length, issues: failures.join(', ') }));
      } else {
        showToastNotification(t('workspace.toast.offerSuccess'));
      }
    } catch (error) {
      console.error('Failed to send offer email or move candidate:', error);
      alert(uiText('Could not send the offer email. Please try again.'));
    } finally {
      setOfferSending(false);
      setOfferActionMode('');
      resetOfferModalAfterMutation();
    }
  };

  const handleApproveOffer = async () => {
    if (!reviewApprovalData) return;
    if (!canReviewOffer) {
      alert(uiText('You do not have permission to approve offers.'));
      return;
    }

    if (!validateOfferTimeline()) {
      return;
    }

    setOfferSending(true);
    setOfferActionMode('approving');

    try {
      const approval = reviewApprovalData;
      const metadata = approval.metadata || {};
      const structuredOfferId = activeOfferId || metadata.offerId || '';

      if (structuredOfferId) {
        const currentHtml = offerQuillRef.current ? offerQuillRef.current.root.innerHTML : offerMessageHTML;
        const currentText = offerQuillRef.current ? offerQuillRef.current.getText() : '';
        const approvalApplicants = offerModalApplicants?.length ? offerModalApplicants : (metadata.applicants || []);
        const applicant = approvalApplicants[0];

        if (applicant) {
          const payload = await buildStructuredOfferPayload(applicant, {
            offerId: structuredOfferId,
            subject: offerSubject || metadata.subject || 'Job Offer',
            messageHTML: currentHtml && currentHtml.trim() ? currentHtml : (metadata.messageHTML || ''),
            messageText: currentText?.trim() ? currentText : (metadata.messageText || ''),
            attachments: offerAttachments,
            terms: offerTerms,
            approvalNote: offerApprovalNote
          });
          await offerApi.saveDraft(userUID, payload, structuredOfferId);
        }

        await offerApi.approveAndSend(userUID, structuredOfferId, offerApprovalNote);

        const targetStageId = offerTargetStage || metadata.targetStage || 'stage-1';
        const targetSubstageId = offerTargetSubstage || metadata.targetSubstage || 'offer-sent';
        setApplicantsData((prev) =>
          prev.map((app) =>
            approvalApplicants.some((candidate) => candidate?.id === app.id)
              ? { ...app, stageId: targetStageId, substageId: targetSubstageId }
              : app
          )
        );

        showToastNotification(t('workspace.toast.offerSuccess'));

        setShowOfferEmailModal(false);
        setOfferReviewMode(false);
        setReviewApprovalData(null);
        setActiveOfferId('');
        setOfferRejectionReason('');
        return;
      }

      const now = Date.now();
      const BACKEND_URL = offerApi.backendUrl;

      // Use current state to allow edits (files/message)
      const currentHtml = offerQuillRef.current ? offerQuillRef.current.root.innerHTML : offerMessageHTML;
      const currentText = offerQuillRef.current ? offerQuillRef.current.getText() : '';
      const currentAttachments = offerAttachments.map(att => ({
        name: att.name,
        content_base64: att.content,
        mime_type: att.type
      }));
      const approvalApplicants = offerModalApplicants?.length ? offerModalApplicants : (metadata.applicants || []);
      const subject = offerSubject?.trim() ? offerSubject : (metadata.subject || 'Job Offer');
      const messageHtml = currentHtml && currentHtml.trim() ? currentHtml : (metadata.messageHTML || '');
      const messageText = currentText?.trim() ? currentText : (metadata.messageText || '');
      const companyLabel = metadata.companyName || companyName || '';
      const jobTitle = metadata.jobTitle || '';

      const emailFailures = [];
      const resolvedApplicants = [];

      if (Array.isArray(approvalApplicants)) {
        for (const applicant of approvalApplicants) {
          const applicantName = applicant?.name || applicant?.username || 'Candidate';
          if (!applicant?.id) {
            emailFailures.push(`${applicantName} (missing id)`);
            continue;
          }

          const applicantEmail = await resolveApplicantEmail(applicant);
          const normalizedApplicant = {
            id: applicant.id,
            name: applicantName
          };
          if (applicantEmail) {
            normalizedApplicant.email = applicantEmail;
          } else if (applicant?.email || applicant?.applicantEmail) {
            normalizedApplicant.email = applicant.email || applicant.applicantEmail;
          }
          if (applicant?.connectionKey) {
            normalizedApplicant.connectionKey = applicant.connectionKey;
          }
          if (applicant?.applicantType) {
            normalizedApplicant.applicantType = applicant.applicantType;
          }
          resolvedApplicants.push(normalizedApplicant);

          if (!applicantEmail) {
            emailFailures.push(`${applicantName} (missing email)`);
            continue;
          }

          try {
            const resp = await fetch(`${BACKEND_URL}/send-offer-email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                applicant_email: applicantEmail,
                applicant_name: applicantName,
                subject: subject,
                message_html: messageHtml,
                message_text: messageText || undefined,
                attachments: currentAttachments,
                company_name: companyLabel,
                job_title: jobTitle || undefined
              })
            });

            const raw = await resp.text();
            let respData = null;
            try {
              respData = raw ? JSON.parse(raw) : null;
            } catch (e) { }

            if (!resp.ok || (respData && respData.success === false)) {
              const backendError = respData?.error || raw || `Status ${resp.status}`;
              throw new Error(backendError);
            }
          } catch (e) {
            console.error("Email send fail", e);
            emailFailures.push(`${applicantName} (email send failed)`);
          }
        }
      }

      // 2. Update Firebase Status
      const updates = {};
      updates[`companies/${userUID}/pending_approvals/${approval.id}/status`] = 'approved';
      updates[`companies/${userUID}/pending_approvals/${approval.id}/reviewedBy`] = {
        uid: currentUser?.uid,
        name: currentUser?.displayName || currentUser?.email,
        email: currentUser?.email
      };
      updates[`companies/${userUID}/pending_approvals/${approval.id}/reviewedAt`] = now;

      // Update metadata with what was actually sent
      updates[`companies/${userUID}/pending_approvals/${approval.id}/metadata/subject`] = subject;
      updates[`companies/${userUID}/pending_approvals/${approval.id}/metadata/messageHTML`] = messageHtml;
      updates[`companies/${userUID}/pending_approvals/${approval.id}/metadata/messageText`] = messageText || '';
      updates[`companies/${userUID}/pending_approvals/${approval.id}/metadata/attachments`] = currentAttachments;
      if (resolvedApplicants.length > 0) {
        updates[`companies/${userUID}/pending_approvals/${approval.id}/metadata/applicants`] = resolvedApplicants;
      }

      const requesterUid = approval.submittedBy?.uid || approval.submittedBy;
      if (requesterUid) {
        const approverName = currentUser?.displayName || currentUser?.email || 'Approver';
        const approvalLabel = metadata.jobTitle || approval.entityName || approval.snapshot?.title || 'Offer';
        const notifRef = push(ref(database, `companies/${userUID}/user_notifications/${requesterUid}`));
        updates[`companies/${userUID}/user_notifications/${requesterUid}/${notifRef.key}`] = {
          id: notifRef.key,
          type: 'approval_decision',
          approvalType: 'offer_letter',
          status: 'approved',
          ...systemMessage('approval_decision', { entity: approvalLabel, name: approverName, status: 'approved', approvalType: 'offer_letter' }),
          title: 'Offer approval approved',
          message: `${approvalLabel} was approved by ${approverName}.`,
          text: `${approvalLabel} was approved by ${approverName}.`,
          timestamp: now,
          approvalId: approval.id,
          entityId: approval.entityId || '',
          entityName: approvalLabel,
          approver: {
            uid: currentUser?.uid,
            name: approverName,
            email: currentUser?.email
          }
        };
      }

      await update(ref(database), updates);

      if (Array.isArray(approvalApplicants)) {
        approvalApplicants.forEach((applicant) => {
          if (!applicant?.id) return;
          const analyticsContext = buildActorAnalyticsContext({
            actorUserId: currentUser?.uid || '',
            actorUserEmail: currentUser?.email || '',
            ownerRecruiterId: metadata.ownerRecruiterId || selectedJobData?.data?.ownerRecruiterId || selectedJobData?.ownerRecruiterId || '',
            ownerRecruiterEmail: metadata.ownerRecruiterEmail || selectedJobData?.data?.ownerRecruiterEmail || selectedJobData?.ownerRecruiterEmail || ''
          });
          emitAtsAnalyticsEvent({
            company_uid: userUID,
            event_type: 'offer_approved',
            occurred_at: new Date(now).toISOString(),
            subject: {
              applicantId: applicant.id,
              connectionKey: applicant.connectionKey || '',
              applicantType: applicant.applicantType || (String(applicant.id || '').startsWith('temp_') ? 'imported' : 'authenticated')
            },
            job: {
              jobId: metadata.jobId || '',
              jobTitle: jobTitle || metadata.jobTitle || ''
            },
            context: {
              approvalId: approval.id,
              ...analyticsContext
            }
          });
        });
      }

      const targetStageId = offerTargetStage || metadata.targetStage || 'stage-1';
      const targetSubstageId = offerTargetSubstage || metadata.targetSubstage || 'offer-sent';
      const moveFailures = [];

      if (Array.isArray(approvalApplicants) && approvalApplicants.length > 0) {
        const moveResults = await Promise.allSettled(
          approvalApplicants.map((applicant) => {
            if (!applicant?.id) {
              moveFailures.push('Unknown candidate (missing id)');
              return Promise.resolve();
            }
            return updateApplicantPositionInFirebase(applicant.id, targetStageId, targetSubstageId);
          })
        );

        moveResults.forEach((result, index) => {
          if (result.status === 'rejected') {
            const applicantName = approvalApplicants[index]?.name || approvalApplicants[index]?.username || 'Candidate';
            moveFailures.push(`${applicantName} (move failed)`);
          }
        });
      }

      const failures = [...emailFailures, ...moveFailures];
      if (failures.length > 0) {
        showToastNotification(t('workspace.toast.offerIssues', { count: failures.length, issues: failures.join(', ') }));
      } else {
        showToastNotification(t('workspace.toast.offerSuccess'));
      }

      setShowOfferEmailModal(false);
      setOfferReviewMode(false);
      setReviewApprovalData(null);
    } catch (e) {
      console.error("Approval failed", e);
      alert(uiText("Failed to approve offer."));
    } finally {
      setOfferSending(false);
      setOfferActionMode('');
    }
  };

  const handleRejectOffer = async () => {
    if (!reviewApprovalData) return;
    if (!canReviewOffer) {
      alert(uiText('You do not have permission to reject offers.'));
      return;
    }
    const approval = reviewApprovalData;
    const structuredOfferId = activeOfferId || approval.metadata?.offerId || '';

    if (structuredOfferId && !offerRejectionReason.trim()) {
      alert(uiText('Please enter a rejection reason before rejecting the offer.'));
      return;
    }

    setOfferSending(true);
    setOfferActionMode('rejecting');
    try {
      if (structuredOfferId) {
        await offerApi.rejectApproval(userUID, structuredOfferId, offerRejectionReason.trim());
        showToastNotification('Offer approval rejected.');
        setShowOfferEmailModal(false);
        setOfferReviewMode(false);
        setReviewApprovalData(null);
        setActiveOfferId('');
        setOfferRejectionReason('');
        return;
      }

      const updates = {};
      const now = Date.now();

      updates[`companies/${userUID}/pending_approvals/${approval.id}/status`] = 'rejected';
      updates[`companies/${userUID}/pending_approvals/${approval.id}/reviewedBy`] = {
        uid: currentUser?.uid,
        name: currentUser?.displayName || currentUser?.email,
        email: currentUser?.email
      };
      updates[`companies/${userUID}/pending_approvals/${approval.id}/reviewedAt`] = now;

      const requesterUid = approval.submittedBy?.uid || approval.submittedBy;
      if (requesterUid) {
        const approverName = currentUser?.displayName || currentUser?.email || 'Approver';
        const approvalLabel = approval.metadata?.jobTitle || approval.entityName || approval.snapshot?.title || 'Offer';
        const notifRef = push(ref(database, `companies/${userUID}/user_notifications/${requesterUid}`));
        updates[`companies/${userUID}/user_notifications/${requesterUid}/${notifRef.key}`] = {
          id: notifRef.key,
          type: 'approval_decision',
          approvalType: 'offer_letter',
          status: 'rejected',
          ...systemMessage('approval_decision', { entity: approvalLabel, name: approverName, status: 'rejected', approvalType: 'offer_letter' }),
          title: 'Offer approval rejected',
          message: `${approvalLabel} was rejected by ${approverName}.`,
          text: `${approvalLabel} was rejected by ${approverName}.`,
          timestamp: now,
          approvalId: approval.id,
          entityId: approval.entityId || '',
          entityName: approvalLabel,
          approver: {
            uid: currentUser?.uid,
            name: approverName,
            email: currentUser?.email
          }
        };
      }
      await update(ref(database), updates);

      const metadata = approval.metadata || {};
      const approvalApplicants = Array.isArray(metadata.applicants) ? metadata.applicants : [];
      approvalApplicants.forEach((applicant) => {
        if (!applicant?.id) return;
        const analyticsContext = buildActorAnalyticsContext({
          actorUserId: currentUser?.uid || '',
          actorUserEmail: currentUser?.email || '',
          ownerRecruiterId: metadata.ownerRecruiterId || selectedJobData?.data?.ownerRecruiterId || selectedJobData?.ownerRecruiterId || '',
          ownerRecruiterEmail: metadata.ownerRecruiterEmail || selectedJobData?.data?.ownerRecruiterEmail || selectedJobData?.ownerRecruiterEmail || ''
        });
        emitAtsAnalyticsEvent({
          company_uid: userUID,
          event_type: 'offer_rejected',
          occurred_at: new Date(now).toISOString(),
          subject: {
            applicantId: applicant.id,
            connectionKey: applicant.connectionKey || '',
            applicantType: applicant.applicantType || (String(applicant.id || '').startsWith('temp_') ? 'imported' : 'authenticated')
          },
          job: {
            jobId: metadata.jobId || '',
            jobTitle: metadata.jobTitle || approval.entityName || ''
            },
            context: {
              approvalId: approval.id,
              ...analyticsContext
            }
          });
        });

      setShowOfferEmailModal(false);
      setOfferReviewMode(false);
      setReviewApprovalData(null);
    } catch (e) {
      console.error("Rejection failed", e);
      alert(uiText("Failed to reject offer."));
    } finally {
      setOfferSending(false);
      setOfferActionMode('');
    }
  };

  /**
   * Commit a drag move only after every pre-move gate has completed.
   *
   * Keeping this write path separate lets the two-way question review pause a
   * drop without duplicating position, notification, local-state, or undo work.
   */
  const completeApplicantDrop = async ({
    applicantsToMove,
    targetStageId,
    targetSubstageId,
    currentStageId,
    primaryApplicant
  }) => {
    const movedApplicantsData = applicantsToMove.map((candidate) => ({
      id: candidate.id,
      name: candidate.name || candidate.username || 'Unknown',
      originalStageId: candidate.stageId,
      originalSubstageId: candidate.substageId
    }));

    for (const candidate of applicantsToMove) {
      await updateApplicantPositionInFirebase(
        candidate.id,
        targetStageId,
        targetSubstageId,
        '',
        {
          openAssignManager: currentStageId === 'stage-1'
            && targetStageId === 'stage-2'
            && candidate.id === primaryApplicant.id
        }
      );
    }

    setApplicantsData((current) => current.map((candidate) => (
      applicantsToMove.some((item) => item.id === candidate.id)
        ? { ...candidate, stageId: targetStageId, substageId: targetSubstageId }
        : candidate
    )));

    if (onClearSelection) onClearSelection();

    if (applicantsToMove.length > 1) {
      setUndoData({
        movedApplicants: movedApplicantsData,
        targetStageId,
        targetSubstageId,
        count: applicantsToMove.length
      });
      setShowUndoToast(true);
    }

    if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
    undoTimeoutRef.current = setTimeout(() => {
      setShowUndoToast(false);
      setUndoData(null);
    }, 5000);
  };

  const handleDrop = async (e, targetStageId, targetSubstageId) => {
    e.preventDefault();
    if (dragScrollFrameRef.current) {
      cancelAnimationFrame(dragScrollFrameRef.current);
      dragScrollFrameRef.current = null;
    }
    const draggedApplicantId = e.dataTransfer.getData("text/plain");

    // Determine which applicants to move: if dragged applicant is in selection, move all selected; otherwise just the one
    let applicantsToMove = [];
    if (selectedApplicants.includes(draggedApplicantId) && selectedApplicants.length > 1) {
      // Move all selected applicants
      applicantsToMove = applicantsData.filter(a => selectedApplicants.includes(a.id));
    } else {
      // Move just the one dragged applicant
      const applicant = applicantsData.find(a => a.id === draggedApplicantId);
      if (!applicant) return;
      applicantsToMove = [applicant];
    }

    if (applicantsToMove.length === 0) return;

    // If all selected applicants are already in the target stage/substage, do nothing
    const allAlreadyInTarget = applicantsToMove.every(app => (
      app.stageId === targetStageId && app.substageId === targetSubstageId
    ));
    if (allAlreadyInTarget) {
      if (onClearSelection) {
        onClearSelection();
      }
      return;
    }

    // Use the first applicant to check current position for validation
    const applicant = applicantsToMove[0];
    const currentStageId = applicant.stageId;
    const currentSubstageId = applicant.substageId;

    // === Status-aware move warnings (single applicant only) ===
    if (applicantsToMove.length === 1) {
      const status = applicantStatusMap[applicant.id] || {};
      const isNeverOpened = !applicant.firstOpenedAt;
      const currentSubstage = dynamicSubstages[currentStageId]?.find(s => s.id === currentSubstageId);
      const currentSubstageType = currentSubstage?.type || 'static';
      const isInAssessmentStep = isAssessmentStepType(currentSubstageType);
      const isInInterviewStep = currentSubstageType === 'interview';

      const currentSubstageAssessmentId = currentSubstage?.link || null;
      const assessmentStepStatus = isInAssessmentStep && currentSubstageAssessmentId
        ? (status.assessmentsByStep || {})[currentSubstageAssessmentId]
        : null;

      if (isInAssessmentStep && assessmentStepStatus !== 'available') {
        if (!window.confirm(t('cellStatus.warnMoveAwaitingResults'))) {
          if (onClearSelection) onClearSelection();
          return;
        }
      } else if (isInInterviewStep && status.interviewScheduled) {
        const dateLabel = status.interviewScheduled.date || '';
        if (!window.confirm(t('cellStatus.warnMoveInterviewScheduled', { date: dateLabel }))) {
          if (onClearSelection) onClearSelection();
          return;
        }
      } else if (isNeverOpened) {
        if (!window.confirm(t('cellStatus.warnMoveNeverOpened'))) {
          if (onClearSelection) onClearSelection();
          return;
        }
      }
    }

    // Check if dropping into "Meet the Team & Assign Buddy" substage
    const targetSubstage = dynamicSubstages[targetStageId]?.find(s => s.id === targetSubstageId);

    // Check if dropping into "Interview" substage - Handle bulk scheduling
    if (targetSubstage && targetSubstage.type === 'interview') {
      // Check if multiple candidates are being dropped
      if (applicantsToMove.length > 1) {
        // Check for max limit of 10 interviews
        if (applicantsToMove.length > 10) {
          showToastNotification(t('workspace.toast.maxInterviews', { limit: 10, count: applicantsToMove.length }));
          // Don't add the candidates
          if (onClearSelection) {
            onClearSelection();
          }
          return;
        }

        // Show bulk interview scheduler
        setBulkInterviewApplicants(applicantsToMove.map(app => ({
          ...app,
          targetStageId,
          targetSubstageId
        })));
        setBulkInterviewSubstageData(targetSubstage);
        setShowBulkInterviewScheduler(true);
        return;
      } else {
        // Single candidate — move first, then scheduler opens after position update below
      }
    }

    // Check if dropping into "Assessment" substage - Handle bulk assessment invites
    if (targetSubstage && isAssessmentStepType(targetSubstage.type)) {
      // Check if multiple candidates are being dropped
      if (applicantsToMove.length > 1) {
        // Check for max limit of 10 assessments
        if (applicantsToMove.length > 10) {
          showToastNotification(t('workspace.toast.maxAssessments', { limit: 10, count: applicantsToMove.length }));
          // Don't add the candidates
          if (onClearSelection) {
            onClearSelection();
          }
          return;
        }

        if (targetSubstage.type === 'ai-interview') {
          const pausedForQuestions = await openLiveQuestionGate({
            applicants: applicantsToMove,
            targetStageId,
            targetSubstageId,
            targetSubstage,
            currentStageId,
            primaryApplicant: applicant,
            isBulk: true
          });
          if (pausedForQuestions) return;
        }

        // Send bulk assessment invites
        await handleBulkAssessmentInvites(applicantsToMove, targetStageId, targetSubstageId, targetSubstage);

        // Clear selection after successful bulk send
        if (onClearSelection) {
          onClearSelection();
        }
        return;
      } else {
        // Single candidate - use existing flow (sendAssessmentNotification will be called below)
      }
    }

    // Confirmation when hiring
    if (targetSubstageId === 'hired-applicants') {
      const proceed = window.confirm(t('alerts.confirmHire'));
      if (!proceed) {
        return; // cancel drop
      }
    }

    // Special handling for "Move to Onboarding" substage
    if (targetSubstageId === 'move-to-onboarding') {
      // Open onboarding transition modal to transfer to onboarding system
      await openOnboardingTransitionModal(applicant);
      return; // Don't proceed with normal drop logic
    }

    // Helper: build ordered substage list for a stage
    const getOrderedSubstageIds = (stageId) => {
      // Helper: sanitize department names for Firebase keys
      const sanitizeDepartmentKey = (dept) => {
        // Replace forbidden characters: . # $ / [ ]
        return dept.replace(/[.#$/[\]]/g, '_');
      };

      // Helper: filter substages by department
      const filterSubstagesByDepartment = (substages, currentDept) => {
        return substages.filter(substage => {
          // If substage has no departments field, show it for all departments
          if (!substage.departments || Object.keys(substage.departments).length === 0) {
            return true;
          }

          // If no current department selected, show all substages
          if (!currentDept) {
            return true;
          }

          // Check if current department is in the substage's departments (sanitized)
          const sanitizedDept = sanitizeDepartmentKey(currentDept);
          return substage.departments[sanitizedDept] === true;
        });
      };

      const sortSubstages = (substages, currentDept) => {
        // First filter by department
        const visibleSubstages = filterSubstagesByDepartment(substages, currentDept);

        const byId = {};
        substages.forEach((s) => { byId[s.id] = s; });

        const visibleIds = new Set(visibleSubstages.map(s => s.id));

        const withNoAfter = visibleSubstages.filter(s => !s.afterId);

        // Sort by timestamp DESCENDING (latest first)
        withNoAfter.sort((a, b) => {
          const tsA = parseInt(a.id.split('-').pop(), 10) || 0;
          const tsB = parseInt(b.id.split('-').pop(), 10) || 0;
          return tsB - tsA; // DESCENDING
        });

        const ordered = [...withNoAfter];

        // Group substages by their afterId
        const withAfter = visibleSubstages.filter(s => s.afterId);
        const byAfterId = {};
        withAfter.forEach(s => {
          if (!byAfterId[s.afterId]) {
            byAfterId[s.afterId] = [];
          }
          byAfterId[s.afterId].push(s);
        });

        // Sort each group by timestamp DESCENDING (latest first)
        Object.keys(byAfterId).forEach(afterId => {
          byAfterId[afterId].sort((a, b) => {
            const tsA = parseInt(a.id.split('-').pop(), 10) || 0;
            const tsB = parseInt(b.id.split('-').pop(), 10) || 0;
            return tsB - tsA; // DESCENDING - latest timestamp first
          });
        });

        // Now iteratively place substages
        const placed = new Set(ordered.map(s => s.id));
        let guard = 0;
        const maxIterations = 100;

        while (Object.keys(byAfterId).length > 0 && guard < maxIterations) {
          let madeProgress = false;

          for (const afterId of Object.keys(byAfterId)) {
            const group = byAfterId[afterId];
            if (group.length === 0) {
              delete byAfterId[afterId];
              continue;
            }

            // Check if afterId is visible
            const afterIdVisible = visibleIds.has(afterId);

            if (afterIdVisible) {
              // Normal case: afterId is visible
              if (placed.has(afterId)) {
                // Place all substages in this group right after afterId
                const idx = ordered.findIndex(item => item.id === afterId);
                if (idx !== -1) {
                  // Insert all items from this group in reverse order so latest timestamp is closest
                  for (let i = group.length - 1; i >= 0; i--) {
                    ordered.splice(idx + 1, 0, group[i]);
                    placed.add(group[i].id);
                  }
                  delete byAfterId[afterId];
                  madeProgress = true;
                }
              }
            } else {
              // Special case: afterId is hidden
              const hiddenStep = byId[afterId];
              if (hiddenStep && hiddenStep.afterId) {
                const hiddenStepAfterId = hiddenStep.afterId;

                if (placed.has(hiddenStepAfterId)) {
                  // Place all substages in this group right after the hidden step's afterId
                  const idx = ordered.findIndex(item => item.id === hiddenStepAfterId);
                  if (idx !== -1) {
                    // Insert all items from this group in reverse order so latest timestamp is closest
                    for (let i = group.length - 1; i >= 0; i--) {
                      ordered.splice(idx + 1, 0, group[i]);
                      placed.add(group[i].id);
                    }
                    delete byAfterId[afterId];
                    madeProgress = true;
                  }
                }
              } else {
                // Hidden step has no afterId, add all to end
                ordered.push(...group);
                group.forEach(s => placed.add(s.id));
                delete byAfterId[afterId];
                madeProgress = true;
              }
            }
          }

          if (!madeProgress) {
            // Add remaining to end
            Object.values(byAfterId).forEach(group => {
              ordered.push(...group);
            });
            console.warn('Could not resolve all afterId dependencies, adding remaining to end');
            break;
          }

          guard++;
        }

        if (guard >= maxIterations) {
          console.warn('Reached max iterations for substage ordering');
          Object.values(byAfterId).forEach(group => {
            ordered.push(...group);
          });
        }

        return ordered;
      };

      const currentDept = selectedJobData?.data?.department || selectedJobData?.department || null;
      const dyn = (dynamicSubstages && dynamicSubstages[stageId]) ? [...dynamicSubstages[stageId]] : [];

      if (stageId === 'stage-1') {
        const filteringSubstage = dyn.find(s => s.id === 'filter-applicants');
        const otherDynamicSubstages = dyn.filter(s => s.id !== 'filter-applicants');
        const orderedOtherDynamic = sortSubstages(otherDynamicSubstages, currentDept);

        const base = ['all-applicants-1'];
        if (filteringSubstage) base.push(filteringSubstage.id);
        base.push(...orderedOtherDynamic.map(s => s.id));
        base.push('offer-sent', 'hired-applicants', 'move-to-onboarding');
        return base;
      }

      const orderedDynamic = sortSubstages(dyn, currentDept);

      if (stageId === 'stage-2') {
        return ['background-checks', ...orderedDynamic.map(s => s.id)];
      }
      if (stageId === 'stage-3') {
        return ['orientation', ...orderedDynamic.map(s => s.id)];
      }
      return [];
    };

    let allowMove = true;

    if (currentStageId === targetStageId) {
      const orderedIds = getOrderedSubstageIds(currentStageId);
      const currentIdx = orderedIds.indexOf(currentSubstageId);
      const targetIdx = orderedIds.indexOf(targetSubstageId);

      console.log('Drag and drop debug:', {
        stage: currentStageId,
        orderedIds,
        currentSubstage: currentSubstageId,
        targetSubstage: targetSubstageId,
        currentIdx,
        targetIdx
      });

      if (currentIdx === -1 || targetIdx === -1) {
        allowMove = false; // fallback safety
      } else {
        // Special case: hired applicants can only move forward, not backward
        if (currentSubstageId === 'hired-applicants' && targetIdx < currentIdx) {
          window.alert('This candidate has already been hired and cannot be moved back to previous hiring stages.');
          return;
        }

        // Check if steps are being skipped (moving more than one step forward)
        if (targetIdx > currentIdx + 1) {
          const orderedIds = getOrderedSubstageIds(currentStageId);
          const skippedSteps = orderedIds.slice(currentIdx + 1, targetIdx);

          // Get the names of the skipped steps
          const skippedStepNames = skippedSteps.map(stepId => {
            // Check if it's a dynamic substage first
            const dynamicSubstage = dynamicSubstages[currentStageId]?.find(s => s.id === stepId);
            if (dynamicSubstage) {
              return dynamicSubstage.title || stepId;
            }

            // Otherwise, map known substage IDs to readable names
            return translateSubstageName(stepId);
          });

          const applicantText = applicantsToMove.length === 1
            ? `candidate "${applicantsToMove[0].name || applicantsToMove[0].username || 'Unknown'}"`
            : `${applicantsToMove.length} candidates`;

          const confirmMessage = t('workspace.skipStepsConfirm', {
            name: applicantText,
            steps: skippedStepNames.join('\n• ')
          });

          if (!window.confirm(confirmMessage)) {
            return; // Cancel the drop if not confirmed
          }
        }
        // Backwards moves allowed for non-hired applicants (targetIdx <= currentIdx)
      }
    }

    // Offer flow: open email modal before moving
    if (targetSubstageId === 'offer-sent') {
      openOfferEmailModal(applicantsToMove, targetStageId, targetSubstageId);
      if (onClearSelection) {
        onClearSelection();
      }
      return;
    }

    // A two-way interview may need employer-approved job questions. Pause here,
    // after normal move validation but before the first Firebase position write.
    if (targetSubstage?.type === 'ai-interview') {
      const pausedForQuestions = await openLiveQuestionGate({
        applicants: applicantsToMove,
        targetStageId,
        targetSubstageId,
        targetSubstage,
        currentStageId,
        primaryApplicant: applicant,
        isBulk: false
      });
      if (pausedForQuestions) return;
    }

    await completeApplicantDrop({
      applicantsToMove,
      targetStageId,
      targetSubstageId,
      currentStageId,
      primaryApplicant: applicant
    });
  };

  // OPTIMIZED VERSION: Using new indexed database structure  
  const updateApplicantPositionInFirebase = async (
    applicantId,
    newStageId,
    newSubstageId,
    jobTitle = '',
    { openAssignManager = false, suppressAssessmentNotification = false } = {}
  ) => {
    try {
      // 🚀 OPTIMIZED: Generate proper connection key based on applicant data
      let connectionKey = null;
      const currentApplicant = applicantsData.find(app => app.id === applicantId);

      console.log('DEBUG: Updating applicant position for:', {
        applicantId,
        currentApplicant: currentApplicant ? {
          id: currentApplicant.id,
          connectionKey: currentApplicant.connectionKey,
          applicantType: currentApplicant.applicantType
        } : null,
        newStageId,
        newSubstageId
      });

      if (currentApplicant && currentApplicant.connectionKey) {
        connectionKey = currentApplicant.connectionKey;
        console.log('DEBUG: Using existing connectionKey:', connectionKey);
      } else {
        // Fallback: Try both authenticated and imported connection patterns
        console.log('DEBUG: No connectionKey found, trying fallback logic');

        // First try imported pattern if we suspect it's imported
        if (currentApplicant && currentApplicant.applicantType === 'imported') {
          connectionKey = `${userUID}_imported_${applicantId}`;
          console.log('DEBUG: Using imported pattern:', connectionKey);
        } else {
          connectionKey = `${userUID}_${applicantId}`;
          console.log('DEBUG: Using authenticated pattern, checking if imported exists...');

          // Check if this is actually an imported applicant
          try {
            const importedConnectionKey = `${userUID}_imported_${applicantId}`;
            const importedRef = ref(database, `connections/${importedConnectionKey}`);
            const importedSnap = await get(importedRef);
            if (importedSnap.exists()) {
              connectionKey = importedConnectionKey;
              console.log('DEBUG: Found imported connection, using:', connectionKey);
            }
          } catch (error) {
            console.warn('Could not check for imported connection, using authenticated pattern');
          }
        }
      }

      const additionalData = {};
      const currentJobSignals = selectedJobData?.data || selectedJobData || {};
      if (jobTitle) {
        additionalData.jobTitle = jobTitle;
      }
      additionalData.candidateId = currentApplicant?.candidateId || applicantId;
      additionalData.roleId = currentApplicant?.roleId || currentJobSignals?.roleId || currentJobSignals?.id || '';
      additionalData.department = currentApplicant?.department || currentJobSignals?.department || '';
      additionalData.location = currentApplicant?.location || currentJobSignals?.companyLocation || currentJobSignals?.location || '';
      additionalData.ownerRecruiterId = currentApplicant?.ownerRecruiterId || currentJobSignals?.ownerRecruiterId || '';
      additionalData.ownerRecruiterEmail = currentApplicant?.ownerRecruiterEmail || currentJobSignals?.ownerRecruiterEmail || '';
      additionalData.actorUserId = currentUser?.uid || '';
      additionalData.actorUserEmail = currentUser?.email || '';
      additionalData.primarySource = currentApplicant?.primarySource || 'JOBSEEQR';
      additionalData.sourceCategory = currentApplicant?.sourceCategory || 'platform';
      additionalData.sourceTags = currentApplicant?.sourceTags || [];

      // Handle special states for hired and rejected
      if (newSubstageId === 'hired-applicants') {
        additionalData.hired = true;
        additionalData.hiredDate = new Date().toISOString();
      }

      if (newSubstageId === 'rejected-applicants') {
        additionalData.rejected = true;
        additionalData.rejectedDate = new Date().toISOString();
      }

      console.log('DEBUG: About to call updateApplicantPositionOptimized with:', {
        connectionKey,
        newStageId,
        newSubstageId,
        additionalData
      });

      // Use optimized update function that maintains all indexes
      await updateApplicantPositionOptimized(connectionKey, newStageId, newSubstageId, additionalData);
      console.log(`Applicant ${applicantId} moved to ${newStageId}/${newSubstageId} (optimized)`);

      // Ensure an employee record exists when entering Pre‑Onboarding (stage-2)
      if (newStageId === 'stage-2') {
        try {
          const minimalApplicant = currentApplicant || {
            id: applicantId,
            applicantType: connectionKey.includes('_imported_') ? 'imported' : 'authenticated',
            connectionKey
          };
          const employeeResult = await ensureEmployeeRecord(minimalApplicant);
          if (openAssignManager && employeeResult?.employeeKey) {
            openAssignManagerModal({
              employeeKey: employeeResult.employeeKey,
              applicantId,
              employeeName: employeeResult.employeeName || currentApplicant?.name || 'New Employee',
              employeesList: employeeResult.employeesList || []
            });
          }
        } catch (e) {
          console.error('Failed to ensure employee record during position update:', e);
        }
      }

      // Update local state immediately for responsive UI
      setApplicantsData(prev =>
        prev.map(app =>
          app.id === applicantId ? {
            ...app,
            stageId: newStageId,
            substageId: newSubstageId,
            ...(newSubstageId === 'hired-applicants' && { hired: true, isHired: true, hiredDate: new Date().toISOString() }), // Persist hired status in local state
            ...(newSubstageId === 'rejected-applicants' && { rejected: true })
          } : app
        )
      );

      // Call hired notification if moved to hired
      if (newSubstageId === 'hired-applicants') {
        callHiredNotificationAndEmail(applicantId, userUID);
      }

      // Find the substage to check its type and trigger notifications
      // First try to find in dynamicSubstages state
      let substage = dynamicSubstages[newStageId]?.find(s => s.id === newSubstageId);

      // Fallback: If not found in state, read directly from Firebase
      if (!substage) {
        try {
          console.log(`Substage ${newSubstageId} not found in dynamicSubstages, reading from Firebase...`);
          const substageRef = ref(database, `companies/${userUID}/hiringWorkflow/${newStageId}/substages/${newSubstageId}`);
          const substageSnapshot = await get(substageRef);
          if (substageSnapshot.exists()) {
            const substageData = substageSnapshot.val();
            substage = {
              id: newSubstageId,
              ...substageData,
              title: substageData.name || substageData.title || 'Assessment'
            };
            console.log(`Substage loaded from Firebase:`, substage);
          }
        } catch (firebaseError) {
          console.error('Error reading substage from Firebase:', firebaseError);
        }
      }

      // Trigger assessment notifications for both AI and external assessments
      if (substage && isAssessmentStepType(substage.type) && !suppressAssessmentNotification) {
        console.log(`Assessment substage detected (type: ${substage.type}), sending notification...`);
        try {
          await sendAssessmentNotification(applicantId, userUID, substage);
          console.log(`✓ Assessment notification sent successfully for ${substage.type === 'assessment' ? 'external' : (substage.type === 'ai-interview' ? 'AI interview' : 'AI')} assessment`);
        } catch (assessmentError) {
          console.error('Error sending assessment notification:', assessmentError);
          showToastNotification(`Candidate moved, but the invitation was not delivered: ${assessmentError.message}`);
        }
      }

      // Check for interview substage and trigger scheduling popup after move
      if (substage && substage.type === 'interview' && onCheckAndPromptForLink) {
        onCheckAndPromptForLink(applicantId, newStageId, newSubstageId);
      }

    } catch (error) {
      console.error('Error updating applicant position (optimized):', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });

      // Show more specific error message
      const errorMessage = error.code === 'PERMISSION_DENIED'
        ? 'Permission denied. Please check your database security rules.'
        : `Failed to update applicant position: ${error.message}`;

      alert(errorMessage);
    }
  };

  const ensureEmployeeRecord = async (applicant) => {
    try {
      if (!userUID || !applicant) return;
      let connectionKey = applicant.connectionKey || '';
      if (!connectionKey) {
        if (applicant.applicantType === 'imported') {
          connectionKey = `${userUID}_imported_${applicant.id}`;
        } else {
          connectionKey = `${userUID}_${applicant.id}`;
          try {
            const importedKey = `${userUID}_imported_${applicant.id}`;
            const importedSnap = await get(ref(database, `connections/${importedKey}`));
            if (importedSnap.exists()) connectionKey = importedKey;
          } catch (e) { }
        }
      }

      const employeesRef = ref(database, `companies/${userUID}/employees`);
      const employeesSnap = await get(employeesRef);
      let existingId = null;
      let employeesList = [];
      if (employeesSnap.exists()) {
        const all = employeesSnap.val() || {};
        employeesList = Object.entries(all).map(([id, v]) => ({ id, name: v?.name || '—', employeeId: v?.employeeId || '' }));
        for (const [eid, v] of Object.entries(all)) {
          if (v?.connectionKey === connectionKey || v?.applicantId === applicant.id) {
            existingId = eid;
            break;
          }
        }
      }

      // Resolve job/post data from the actual connection first
      let jobId = '';
      let jobAppliedFor = '';
      try {
        const connSnap = await get(ref(database, `connections/${connectionKey}`));
        if (connSnap.exists()) {
          const conn = connSnap.val() || {};
          jobId = conn.jobId || '';
          jobAppliedFor = conn.jobAppliedFor || conn.jobTitle || '';
        }
      } catch (e) { }

      // Load job post directly using jobId to ensure accuracy
      let jobData = {};
      if (jobId) {
        try {
          const postSnap = await get(ref(database, `companies/${userUID}/posts/${jobId}`));
          if (postSnap.exists()) jobData = postSnap.val() || {};
        } catch (e) { }
      }

      // Fallback to selected job option only if jobId not found
      if (!jobId && (!jobData || Object.keys(jobData).length === 0)) {
        const jobOption = jobOptions.find(opt => opt.title === selectedJobName);
        if (jobOption && jobOption.type === 'posted_job') {
          jobData = jobOption.data || {};
          jobId = jobOption.id || '';
        }
      }

      // Map fields from job post; otherwise fallback to applicant/selection
      const role = (jobData.jobOpening || jobAppliedFor || applicant.jobTitle || selectedJobName || '').trim();
      const department = (jobData.department || '').trim();
      const location = (jobData.companyLocation || '').trim();

      let name = applicant.name || '';
      let email = applicant.email || '';
      if (!name || (!email && applicant.applicantType === 'imported')) {
        try {
          if (applicant.applicantType === 'imported') {
            let profRef = ref(database, `imported_profiles/${userUID}/${applicant.id}`);
            let profSnap = await get(profRef);

            if (!profSnap.exists()) {
              profRef = ref(database, `imported_profiles/${applicant.id}`);
              profSnap = await get(profRef);
            }

            const prof = profSnap.val() || {};
            name = name || prof.name || '';
            email = email || prof.email || '';
          } else {
            const personSnap = await get(ref(database, `people/${applicant.id}`));
            const person = personSnap.val() || {};
            name = name || person.username || person.name || '';
          }
        } catch (e) { }
      }

      const now = Date.now();
      const payload = {
        name: name || 'New Employee',
        roleDepartment: department ? `${role} / ${department}` : (role || ''),
        role: role || '',
        department: department || '',
        location: location || '',
        startDate: '',
        contact: {
          email: email || '',
          phone: ''
        },
        createdAt: now,
        // Entering pre-onboarding is only a workforce forecast. It must not count
        // as an active employee until Onboarding's explicit "Confirm started"
        // action records the actual date and confirmation evidence.
        status: 'onboarding',
        plannedStartDate: '',
        lifecycle: {
          status: 'onboarding',
          plannedStartDate: '',
          lastActivityAt: now
        },
        managerId: null,
        applicantId: applicant.id,
        connectionKey,
        jobId: jobId || '',
        jobTitle: role || ''
      };

      if (existingId) {
        // Preserve existing contact info (e.g., workEmail, phoneNumber) and only backfill missing basics
        let existingEmployeeData = {};
        let existingOnboardingData = {};
        try {
          const existingEmployeeRef = ref(database, `companies/${userUID}/employees/${existingId}`);
          const existingOnboardingRef = ref(database, `companies/${userUID}/onboarding/${existingId}`);
          const [existingEmployeeSnap, existingOnboardingSnap] = await Promise.all([
            get(existingEmployeeRef),
            get(existingOnboardingRef)
          ]);
          existingEmployeeData = existingEmployeeSnap.exists() ? (existingEmployeeSnap.val() || {}) : {};
          existingOnboardingData = existingOnboardingSnap.exists() ? (existingOnboardingSnap.val() || {}) : {};
        } catch (e) {
          existingEmployeeData = {};
          existingOnboardingData = {};
        }

        const existingContact = (existingEmployeeData && typeof existingEmployeeData.contact === 'object')
          ? existingEmployeeData.contact
          : {};
        const mergedContact = { ...existingContact };
        // Backfill plain email only if not already present
        if (!mergedContact.email && payload.contact && payload.contact.email) {
          mergedContact.email = payload.contact.email;
        }

        const confirmedAt = existingEmployeeData?.lifecycle?.actualStartConfirmedAt
          || existingOnboardingData?.actualStartConfirmedAt;
        const confirmedStartDate = existingEmployeeData?.lifecycle?.startDate
          || existingOnboardingData?.actualStartDate;
        const hasExplicitStartConfirmation = Boolean(confirmedAt && confirmedStartDate);
        const nextStatus = hasExplicitStartConfirmation ? 'active' : 'onboarding';
        const employeePath = `companies/${userUID}/employees/${existingId}`;
        const rootUpdates = {
          [`${employeePath}/name`]: payload.name,
          [`${employeePath}/role`]: payload.role,
          [`${employeePath}/department`]: payload.department,
          [`${employeePath}/location`]: payload.location,
          // Do not wipe existing contact fields; merge instead.
          [`${employeePath}/contact`]: mergedContact,
          // Old records used a bare active flag while still in pre-onboarding.
          // Preserve active only when an explicit actual-start confirmation exists.
          [`${employeePath}/status`]: nextStatus,
          [`${employeePath}/lifecycle/status`]: nextStatus,
          [`${employeePath}/lifecycle/lastActivityAt`]: now,
          [`${employeePath}/connectionKey`]: payload.connectionKey,
          [`${employeePath}/applicantId`]: payload.applicantId,
          [`${employeePath}/jobId`]: payload.jobId,
          [`${employeePath}/jobTitle`]: payload.jobTitle,
          [`connections/${connectionKey}/employeeKey`]: existingId
        };
        if (hasExplicitStartConfirmation) {
          // Migrate proof created by the older onboarding-only confirmation flow
          // next to the employee lifecycle so every later handoff sees it.
          rootUpdates[`${employeePath}/lifecycle/startDate`] = confirmedStartDate;
          rootUpdates[`${employeePath}/lifecycle/actualStartConfirmedAt`] = confirmedAt;
          rootUpdates[`${employeePath}/lifecycle/actualStartConfirmedBy`] = existingEmployeeData?.lifecycle?.actualStartConfirmedBy
            || existingOnboardingData?.actualStartConfirmedBy
            || '';
          rootUpdates[`${employeePath}/profile/startDate`] = confirmedStartDate;
        }
        const lifecycleEvent = addNationalizationLifecycleEventToUpdates(rootUpdates, {
          companyUID: userUID,
          employeeId: existingId,
          source: 'jobseeqr_pre_onboarding',
          changeKinds: ['lifecycle']
        });
        // Keep the occupation review separate. Applicant/onboarding managers may
        // deliver lifecycle state, while only an employee/nationalization manager
        // may invalidate a previously confirmed regulatory classification.
        const occupationEvent = addNationalizationLifecycleEventToUpdates(rootUpdates, {
          companyUID: userUID,
          employeeId: existingId,
          source: 'jobseeqr_pre_onboarding_role_changed',
          changeKinds: ['occupation']
        });

        // Employee state, connection linkage and the durable retry marker commit
        // together. A browser/network failure cannot strand an untracked change.
        await update(ref(database), rootUpdates);
        let lifecycleSync = null;
        try {
          lifecycleSync = await nationalizationService.syncEmployeeLifecycle(userUID, existingId, {
            eventId: lifecycleEvent.eventId,
            source: 'jobseeqr_pre_onboarding',
            changeKinds: ['lifecycle']
          });
        } catch (syncError) {
          console.warn('Nationalization lifecycle sync is pending for pre-onboarding:', syncError);
        }
        try {
          await nationalizationService.syncEmployeeLifecycle(userUID, existingId, {
            eventId: occupationEvent.eventId,
            source: 'jobseeqr_pre_onboarding_role_changed',
            changeKinds: ['occupation']
          });
        } catch (syncError) {
          // This is expected for an applicant-only manager. The atomic marker is
          // retained for an authorized employee/nationalization manager.
          console.warn('Nationalization occupation review remains queued:', syncError);
        }
        announceNationalizationWorkforceChange({
          companyUID: userUID,
          source: 'jobseeqr_pre_onboarding',
          recalculated: lifecycleSync?.recalculated === true,
          syncPending: !lifecycleSync
        });
        return { employeeKey: existingId, created: false, employeeName: payload.name, employeesList };
      } else {
        const newRef = push(employeesRef);
        const rootUpdates = {
          [`companies/${userUID}/employees/${newRef.key}`]: payload,
          [`connections/${connectionKey}/employeeKey`]: newRef.key
        };
        const lifecycleEvent = addNationalizationLifecycleEventToUpdates(rootUpdates, {
          companyUID: userUID,
          employeeId: newRef.key,
          source: 'jobseeqr_pre_onboarding',
          // Creating a record changes the forecast only. Occupation changes are
          // queued separately after an employer confirms an official code.
          changeKinds: ['lifecycle'],
          establishmentId: onboardingEstablishmentId || undefined
        });

        // The forecast record and delivery marker are one atomic root update.
        await update(ref(database), rootUpdates);
        let lifecycleSync = null;
        try {
          lifecycleSync = await nationalizationService.syncEmployeeLifecycle(userUID, newRef.key, {
            eventId: lifecycleEvent.eventId,
            source: 'jobseeqr_pre_onboarding',
            changeKinds: ['lifecycle'],
            establishmentId: onboardingEstablishmentId || undefined
          });
        } catch (syncError) {
          console.warn('Nationalization lifecycle sync is pending for pre-onboarding:', syncError);
        }
        announceNationalizationWorkforceChange({
          companyUID: userUID,
          source: 'jobseeqr_pre_onboarding',
          recalculated: lifecycleSync?.recalculated === true,
          syncPending: !lifecycleSync
        });
        return { employeeKey: newRef.key, created: true, employeeName: payload.name, employeesList };
      }
    } catch (err) {
      console.error('Failed to ensure employee record:', err);
      return null;
    }
  };

  // Assign Manager & Employee ID modal state and handlers
  const [showAssignManager, setShowAssignManager] = useState(false);
  const [assignEmployeeKey, setAssignEmployeeKey] = useState('');
  const [assignApplicantId, setAssignApplicantId] = useState('');
  const [assignEmployeeName, setAssignEmployeeName] = useState('');
  const [managerOptions, setManagerOptions] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [enteredEmployeeId, setEnteredEmployeeId] = useState('');
  const [managerSearch, setManagerSearch] = useState('');
  const [managerDropdownOpen, setManagerDropdownOpen] = useState(false);
  const managerDropdownRef = useRef(null);
  const [assignStartDate, setAssignStartDate] = useState('');
  const [assignPtoBalance, setAssignPtoBalance] = useState('');

  const openAssignManagerModal = ({ employeeKey, applicantId, employeeName, employeesList }) => {
    setAssignEmployeeKey(employeeKey);
    setAssignApplicantId(applicantId);
    setAssignEmployeeName(employeeName || 'New Employee');
    // Exclude the same employee from manager options
    const filtered = (employeesList || []).filter(e => e.id !== employeeKey);
    // Sort alphabetically for easier scanning
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setManagerOptions(filtered);
    setSelectedManagerId('');
    setEnteredEmployeeId('');
    setManagerSearch('');
    setManagerDropdownOpen(false);
    setAssignStartDate('');
    setAssignPtoBalance('');
    setShowAssignManager(true);
  };

  const closeAssignManagerModal = () => {
    setShowAssignManager(false);
    setAssignEmployeeKey('');
    setAssignApplicantId('');
    setAssignEmployeeName('');
    setManagerOptions([]);
    setSelectedManagerId('');
    setEnteredEmployeeId('');
    setManagerSearch('');
    setManagerDropdownOpen(false);
    setAssignStartDate('');
    setAssignPtoBalance('');
  };

  // Close manager dropdown when clicking outside
  useEffect(() => {
    const onClickOutside = (e) => {
      if (managerDropdownRef.current && !managerDropdownRef.current.contains(e.target)) {
        setManagerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const saveAssignManager = async () => {
    try {
      if (!userUID || !assignEmployeeKey) return;
      const employeePath = `companies/${userUID}/employees/${assignEmployeeKey}`;
      const updates = {};
      const setEmployeeField = (path, value) => {
        updates[`${employeePath}/${path}`] = value;
      };
      if (selectedManagerId) {
        // Auto-promote the assigned manager
        try {
          const managerPermRef = ref(database, `companies/${userUID}/employees/${selectedManagerId}/portal/permissions`);
          get(managerPermRef).then((snap) => {
            const perms = snap.val() || ['employee'];
            if (!perms.includes('manager')) {
              const newPerms = [...perms, 'manager'];
              update(ref(database), {
                [`companies/${userUID}/employees/${selectedManagerId}/portal/permissions`]: newPerms
              });
            }
          });
        } catch (err) {
          console.error("Error auto-promoting manager:", err);
        }

        // Try to resolve manager name from options
        const mgr = managerOptions.find(m => m.id === selectedManagerId);
        if (mgr?.name) setEmployeeField('managerName', mgr.name);
        setEmployeeField('managerId', selectedManagerId);
      } else {
        setEmployeeField('managerId', null);
        setEmployeeField('managerName', '');
      }
      if (enteredEmployeeId && enteredEmployeeId.trim()) {
        setEmployeeField('employeeId', enteredEmployeeId.trim());
      }
      let lifecycleEvent = null;
      if (assignStartDate && assignStartDate.trim()) {
        const plannedStartDate = assignStartDate.trim();
        // This is a forecast date, not proof that the person started. Keep the
        // legacy top-level field for compatibility and write the explicit meaning
        // used by the nationalization calculator.
        setEmployeeField('startDate', plannedStartDate);
        setEmployeeField('plannedStartDate', plannedStartDate);
        setEmployeeField('lifecycle/plannedStartDate', plannedStartDate);
        setEmployeeField('lifecycle/lastActivityAt', Date.now());
        lifecycleEvent = addNationalizationLifecycleEventToUpdates(updates, {
          companyUID: userUID,
          employeeId: assignEmployeeKey,
          source: 'employee_planned_start_changed',
          changeKinds: ['lifecycle']
        });
      }
      if (assignPtoBalance && `${assignPtoBalance}`.trim() !== '') {
        const bal = parseFloat(assignPtoBalance);
        if (!Number.isNaN(bal)) {
          setEmployeeField('pto/balanceDays', bal);
        }
      }
      setEmployeeField('updatedAt', Date.now());
      // Planned-date edits and their retry marker share this root update.
      await update(ref(database), updates);

      if (lifecycleEvent) {
        let lifecycleSync = null;
        try {
          lifecycleSync = await nationalizationService.syncEmployeeLifecycle(userUID, assignEmployeeKey, {
            eventId: lifecycleEvent.eventId,
            source: 'employee_planned_start_changed',
            changeKinds: ['lifecycle']
          });
        } catch (syncError) {
          console.warn('Nationalization planned-start sync is pending:', syncError);
        }
        announceNationalizationWorkforceChange({
          companyUID: userUID,
          source: 'employee_planned_start_changed',
          recalculated: lifecycleSync?.recalculated === true,
          syncPending: !lifecycleSync
        });
      }
    } catch (e) {
      console.error('Failed to save manager/employeeId:', e);
    } finally {
      closeAssignManagerModal();
    }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || process.env.REACT_APP_BACKEND_URL || '';

  /** Ask the authenticated two-way planner for one job-bound employer draft. */
  const generateConversationalGoalDraft = async (assessmentId, jobId, count) => {
    if (!currentUser) throw new Error('Employer authentication is required to generate interview goals.');
    const authToken = await currentUser.getIdToken();
    const response = await fetch(
      `${BACKEND_URL}/companies/${encodeURIComponent(userUID)}/ai-interviews/${encodeURIComponent(assessmentId)}/jobs/${encodeURIComponent(jobId)}/coverage-goals`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ count: Math.min(6, Math.max(1, Number(count) || 5)) })
      }
    );
    const payload = await response.json();
    if (!response.ok || !payload?.success || !payload?.draft?.questions?.length) {
      throw new Error(payload?.detail || payload?.error || 'Could not generate conversational interview goals.');
    }
    return payload.draft;
  };

  /** Resolve a candidate's existing company connection and its bound job. */
  const resolveLiveInterviewBinding = async (candidate) => {
    let connectionKey = candidate?.connectionKey || '';
    if (!connectionKey) {
      connectionKey = candidate?.applicantType === 'imported'
        ? `${userUID}_imported_${candidate.id}`
        : `${userUID}_${candidate.id}`;
      if (candidate?.applicantType !== 'imported') {
        const importedKey = `${userUID}_imported_${candidate.id}`;
        const importedSnapshot = await get(ref(database, `connections/${importedKey}`));
        if (importedSnapshot.exists()) connectionKey = importedKey;
      }
    }
    const connectionSnapshot = await get(ref(database, `connections/${connectionKey}`));
    const connection = connectionSnapshot.exists() ? connectionSnapshot.val() : {};
    return {
      connectionKey,
      jobId: String(connection.jobId || ''),
      jobTitle: connection.jobAppliedFor || connection.jobTitle || ''
    };
  };

  /**
   * Pause a candidate move when its job questions still need generation or
   * employer approval. Returning true guarantees no position write occurred.
   */
  const openLiveQuestionGate = async ({
    applicants,
    targetStageId,
    targetSubstageId,
    targetSubstage,
    currentStageId,
    primaryApplicant,
    isBulk
  }) => {
    try {
      const assessmentId = targetSubstage?.link;
      if (!assessmentId) return false;
      const assessmentSnapshot = await get(ref(database, `companies/${userUID}/assessments/${assessmentId}`));
      if (!assessmentSnapshot.exists()) return false;
      const assessment = assessmentSnapshot.val();
      if (assessment?.deliveryType !== 'two-way-voice' || assessment?.answerFormat !== 'live-voice') {
        return false;
      }

      const bindings = await Promise.all(applicants.map(resolveLiveInterviewBinding));
      const jobId = bindings[0]?.jobId;
      if (!jobId) throw new Error(t('workspace.questionGate.missingJob'));
      if (bindings.some((binding) => binding.jobId !== jobId)) {
        throw new Error(t('workspace.questionGate.differentJobs'));
      }

      const jobRecord = assessment.jobQuestions?.[jobId] || null;
      const classification = classifyInterviewQuestions(jobRecord, assessment.questions || []);
      let questionState = classification.state;
      if (classification.state === 'ready') {
        const currentHash = await interviewGoalHash(classification.questions);
        if (currentHash === jobRecord.contentHash) return false;
        questionState = 'review';
      }

      setLiveQuestionGate({
        phase: questionState === 'review' ? 'review' : 'confirm-generation',
        editing: false,
        error: '',
        isRTL,
        questions: classification.questions,
        sourceRecord: classification.source || jobRecord,
        context: {
          applicants,
          targetStageId,
          targetSubstageId,
          targetSubstage,
          currentStageId,
          primaryApplicant,
          isBulk,
          assessmentId,
          jobId,
          jobTitle: bindings[0]?.jobTitle || jobRecord?.jobTitle || t('workspace.questionGate.thisJob'),
          duration: Math.min(20, Math.max(1, Number(jobRecord?.duration_minutes || assessment.duration_minutes || 20))),
          questionCount: Math.min(6, Math.max(1, Number(assessment.baseConfig?.numQuestions) || 5))
        }
      });
      return true;
    } catch (error) {
      console.error('Could not prepare the two-way interview question gate:', error);
      showToastNotification(error?.message || t('workspace.questionGate.prepareError'));
      return true;
    }
  };

  /** Generate the pending job draft only after the employer confirms the action. */
  const generatePendingLiveQuestions = async () => {
    if (!liveQuestionGate?.context || liveQuestionGate.phase === 'generating') return;
    setLiveQuestionGate((current) => ({ ...current, phase: 'generating', error: '' }));
    try {
      const { assessmentId, jobId, questionCount } = liveQuestionGate.context;
      const draft = await generateConversationalGoalDraft(assessmentId, jobId, questionCount);
      setLiveQuestionGate((current) => ({
        ...current,
        phase: 'review',
        editing: false,
        error: '',
        questions: canonicalInterviewGoals(draft.questions),
        sourceRecord: draft,
        context: {
          ...current.context,
          duration: Math.min(20, Math.max(1, Number(draft.duration_minutes || current.context.duration || 20)))
        }
      }));
    } catch (error) {
      console.error('Could not generate live interview questions:', error);
      setLiveQuestionGate((current) => ({
        ...current,
        phase: 'confirm-generation',
        error: error?.message || t('workspace.questionGate.generationError')
      }));
    }
  };

  /** Approve the reviewed questions, then perform the one previously paused move. */
  const confirmLiveQuestionsAndMove = async () => {
    if (!liveQuestionGate?.context || liveQuestionGate.phase === 'moving') return;
    const questions = canonicalInterviewGoals(liveQuestionGate.questions);
    if (!questions.length) {
      setLiveQuestionGate((current) => ({ ...current, error: t('workspace.questionGate.questionRequired') }));
      return;
    }

    setLiveQuestionGate((current) => ({ ...current, phase: 'moving', error: '' }));
    try {
      if (!currentUser) throw new Error(t('workspace.questionGate.authRequired'));
      const context = liveQuestionGate.context;
      const approval = {
        ...(liveQuestionGate.sourceRecord || {}),
        questions,
        duration_minutes: context.duration,
        jobTitle: context.jobTitle,
        status: 'approved',
        approvedAt: new Date().toISOString(),
        approvedBy: currentUser.uid,
        contentHash: await interviewGoalHash(questions)
      };
      await set(
        ref(database, `companies/${userUID}/assessments/${context.assessmentId}/jobQuestions/${context.jobId}`),
        approval
      );
      await update(ref(database, `companies/${userUID}/assessments/${context.assessmentId}`), {
        previewPendingJobs: false
      });

      if (context.isBulk) {
        await handleBulkAssessmentInvites(
          context.applicants,
          context.targetStageId,
          context.targetSubstageId,
          context.targetSubstage
        );
        if (onClearSelection) onClearSelection();
      } else {
        await completeApplicantDrop({
          applicantsToMove: context.applicants,
          targetStageId: context.targetStageId,
          targetSubstageId: context.targetSubstageId,
          currentStageId: context.currentStageId,
          primaryApplicant: context.primaryApplicant
        });
      }
      setLiveQuestionGate(null);
    } catch (error) {
      console.error('Could not approve questions and move candidate:', error);
      setLiveQuestionGate((current) => ({
        ...current,
        phase: 'review',
        error: error?.message || t('workspace.questionGate.moveError')
      }));
    }
  };

  /** Open a disposable edit draft without changing the reviewed questions. */
  const editPendingLiveQuestions = () => {
    setLiveQuestionGate((current) => current ? {
      ...current,
      editing: true,
      error: '',
      draftQuestions: (current.questions || []).map((question) => ({ ...question }))
    } : current);
  };

  /** Update one disposable draft question; reviewed state remains untouched. */
  const updatePendingLiveQuestion = (index, text) => {
    setLiveQuestionGate((current) => current ? {
      ...current,
      draftQuestions: (current.draftQuestions || []).map((question, questionIndex) => (
        questionIndex === index ? { ...question, text } : question
      )),
      error: ''
    } : current);
  };

  /** Commit the edit draft locally, then return to the separate review step. */
  const savePendingLiveQuestionEdits = () => {
    setLiveQuestionGate((current) => {
      if (!current) return current;
      const questions = canonicalInterviewGoals(current.draftQuestions);
      if (!questions.length) {
        return { ...current, error: t('workspace.questionGate.questionRequired') };
      }
      return {
        ...current,
        questions,
        draftQuestions: null,
        editing: false,
        error: ''
      };
    });
  };

  /** Discard the edit draft and return to the unchanged review step. */
  const cancelPendingLiveQuestionEdits = () => {
    setLiveQuestionGate((current) => current ? {
      ...current,
      draftQuestions: null,
      editing: false,
      error: ''
    } : current);
  };

  /**
   * Ensure a job-varying assessment has questions for the connection's job.
   *
   * Two-way interviews are prepared only by the pre-move review modal above;
   * this notification-time fallback can verify approval but must never generate
   * or approve live questions after the candidate has already moved. Legacy
   * job-varying assessments keep their existing generation behavior.
   * @returns {Promise<{proceed: boolean, message?: string}>}
   */
  const checkAndGenerateJobAssessment = async (applicantId, substage) => {
    try {
      const assessmentId = substage.link;
      if (!assessmentId) return { proceed: true };

      // Load assessment data
      const assessmentSnap = await get(ref(database, `companies/${userUID}/assessments/${assessmentId}`));
      if (!assessmentSnap.exists()) return { proceed: true };

      const assessmentData = assessmentSnap.val();

      // If not job-varying, proceed normally
      if (!assessmentData.varyByJob) return { proceed: true };

      // Get the candidate's job from their connection
      const currentApplicant = applicantsData.find(app => app.id === applicantId);
      let connectionKey = currentApplicant?.connectionKey;
      if (!connectionKey) {
        connectionKey = `${userUID}_${applicantId}`;
        // Check for imported candidate pattern
        try {
          const importedKey = `${userUID}_imported_${applicantId}`;
          const importedSnap = await get(ref(database, `connections/${importedKey}`));
          if (importedSnap.exists()) connectionKey = importedKey;
        } catch (e) { }
      }

      const connSnap = await get(ref(database, `connections/${connectionKey}`));
      const connData = connSnap.exists() ? connSnap.val() : {};
      const jobId = connData.jobId;
      const jobTitle = connData.jobAppliedFor || connData.jobTitle || 'Untitled Job';

      if (!jobId) {
        console.warn('No jobId found for candidate, cannot check job-specific questions');
        return {
          proceed: false,
          message: 'This assessment varies by job description, but the candidate is not linked to a job.'
        };
      }

      // Check if questions exist for this job
      const jobQuestionsExist = assessmentData.jobQuestions && assessmentData.jobQuestions[jobId];

      if (jobQuestionsExist) {
        if (assessmentData.deliveryType === 'two-way-voice' && (
          jobQuestionsExist.status !== 'approved'
          || !jobQuestionsExist.approvedAt
          || !jobQuestionsExist.approvedBy
          || !jobQuestionsExist.contentHash
        )) {
          // Interactive board/list/gallery moves are blocked earlier by the
          // review modal. A non-interactive fallback must never auto-generate,
          // auto-approve, redirect, or display the legacy assessment loader.
          return { proceed: false, message: 'Job-specific interview questions require employer approval.' };
        }
        console.log(`Job-specific questions already exist for job ${jobId}`);
        return { proceed: true };
      }

      if (assessmentData.deliveryType === 'two-way-voice') {
        return {
          proceed: false,
          message: 'Interview questions must be generated and approved before the candidate moves.'
        };
      }

      // Need to generate questions for this new job - show modal and generate
      console.log(`No questions found for job ${jobId}, generating on-demand...`);
      setGeneratingJobTitle(jobTitle);
      setShowGeneratingModal(true);

      const baseConfig = assessmentData.baseConfig || {};
      // Get company description
      const companyDescSnap = await get(ref(database, `companies/${userUID}/description`));
      const companyDescription = companyDescSnap.exists() ? companyDescSnap.val() : '';

      // Get job description
      const jobSnap = await get(ref(database, `companies/${userUID}/posts/${jobId}`));
      const jobData = jobSnap.exists() ? jobSnap.val() : {};
      const jobDescription = jobData.jobDescription || '';

      // Generate legacy assessment questions for non-conversational products.
      const requestConfig = baseConfig.config || {};

      const resp = await fetch(`${BACKEND_URL}/generate-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: assessmentData.title || 'Assessment',
          assessment_type: baseConfig.assessmentType || 'mixed',
          assessment_prompt: assessmentData.assessment_prompt || baseConfig.assessmentPrompt || '',
          departments: assessmentData.departments || baseConfig.departments || [],
          job_title: jobTitle,
          job_department: jobData.department || '',
          job_description: jobDescription,
          company_description: companyDescription,
          job_id: jobId,
          num_questions: baseConfig.numQuestions || 5,
          config: requestConfig
        })
      });

      const data = await resp.json();

      if (!resp.ok || !data?.success || !data?.assessment?.questions?.length) {
        throw new Error(data?.detail || data?.error || 'Could not generate job-specific assessment questions');
      }

      // Store the new job-specific questions
      await set(ref(database, `companies/${userUID}/assessments/${assessmentId}/jobQuestions/${jobId}`), {
        questions: data.assessment.questions,
        duration_minutes: data.assessment.duration_minutes,
        generatedAt: new Date().toISOString(),
        jobTitle: jobTitle,
        jobDepartment: jobData.department || '',
        ...(assessmentData.deliveryType === 'two-way-voice' ? { status: 'draft' } : {})
      });
      await update(ref(database, `companies/${userUID}/assessments/${assessmentId}`), {
        previewPendingJobs: false
      });

      console.log(`Successfully generated questions for job ${jobId}`);

      setShowGeneratingModal(false);

      // Redirect to editor for review - block the candidate drop
      navigate(`/assessment-editor/${assessmentId}?job=${jobId}`);

      return { proceed: false, message: 'Redirecting to review generated questions' };

    } catch (error) {
      console.error('Error checking/generating job assessment:', error);
      setShowGeneratingModal(false);
      alert(error?.message || 'Could not prepare the job-specific assessment. No link was sent.');
      return { proceed: false, message: error?.message || 'Job-specific questions are not ready' };
    }
  };

  /**
   * Preserve candidate-specific generation for legacy one-way AI interviews.
   * This function must never be called for `deliveryType = two-way-voice`.
   */
  const ensureAiInterviewQuestionsReady = async (companyId, assessmentId, applicantId, connectionKey) => {
    const response = await fetch(`${BACKEND_URL}/generate-candidate-ai-interview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_id: companyId,
        assessment_id: assessmentId,
        applicant_id: applicantId,
        connection_key: connectionKey
      })
    });

    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.detail || data?.error || 'Failed to generate AI interview questions for this candidate');
    }

    return data.assessment;
  };

  const sendAssessmentNotification = async (applicantId, employerId, substage) => {
    try {
      console.log(`[sendAssessmentNotification] Starting for applicant ${applicantId}, substage type: ${substage.type}`);

      // Check for job-varying assessment - may need on-demand generation
      if (substage.type === 'ai-assessment' && substage.varyByJob) {
        const checkResult = await checkAndGenerateJobAssessment(applicantId, substage);
        if (!checkResult.proceed) {
          console.log('[sendAssessmentNotification] Blocked - redirecting to editor for new job questions');
          return; // Don't send notification, user was redirected to editor
        }
      }

      // 1. Determine applicant type and get applicant info
      const currentApplicant = applicantsData.find(app => app.id === applicantId);
      const applicantType = currentApplicant?.applicantType || 'authenticated';
      console.log(`[sendAssessmentNotification] Applicant type: ${applicantType}`);

      let applicantData = {};
      let applicantEmail = '';

      if (applicantType === 'imported') {
        // Get data from imported_profiles node
        let applicantRef = ref(database, `imported_profiles/${employerId}/${applicantId}`);
        let applicantSnapshot = await get(applicantRef);

        if (!applicantSnapshot.exists()) {
          applicantRef = ref(database, `imported_profiles/${applicantId}`);
          applicantSnapshot = await get(applicantRef);
        }

        applicantData = applicantSnapshot.val() || {};
        applicantEmail = applicantData.email || '';
      } else {
        // Get data from people node (authenticated users)
        const applicantRef = ref(database, `people/${applicantId}`);
        const applicantSnapshot = await get(applicantRef);
        applicantData = applicantSnapshot.val() || {};

        // Fetch applicant email using cloud function (since email is not stored in RTDB for authenticated users)
        try {
          const emailResp = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: applicantId })
          });
          const emailData = await emailResp.json();
          applicantEmail = emailData.email || '';
        } catch (e) {
          console.error('Failed to fetch applicant email:', e);
        }
      }

      const companyRef = ref(database, `companies/${employerId}`);
      const companySnapshot = await get(companyRef);
      const companyData = companySnapshot.val();

      if (!applicantData || !companyData) {
        console.error("Could not find applicant or company data for notification.");
        return;
      }

      // 2. Resolve connection key and job title (jobAppliedFor)
      let connectionKey = null;
      if (currentApplicant && currentApplicant.connectionKey) {
        connectionKey = currentApplicant.connectionKey;
      } else {
        // Try both patterns
        connectionKey = `${employerId}_${applicantId}`;
        try {
          const importedKey = `${employerId}_imported_${applicantId}`;
          const importedRef = ref(database, `connections/${importedKey}`);
          const importedSnap = await get(importedRef);
          if (importedSnap.exists()) {
            connectionKey = importedKey;
          }
        } catch (e) { }
      }

      let jobTitle = '';
      try {
        const connSnap = await get(ref(database, `connections/${connectionKey}`));
        const connData = connSnap.val() || {};
        jobTitle = connData.jobAppliedFor || connData.jobTitle || '';
      } catch (e) {
        jobTitle = '';
      }

      // 3. Construct assessment link
      const assessmentId = substage.link;
      if (!assessmentId) {
        console.error("No assessment is linked to this substage. Cannot send notification.");
        return;
      }

      const assessmentSnap = await get(ref(database, `companies/${employerId}/assessments/${assessmentId}`));
      const assessmentData = assessmentSnap.exists() ? assessmentSnap.val() : null;
      const isTwoWayVoice = substage.type === 'ai-interview'
        && assessmentData?.deliveryType === 'two-way-voice'
        && assessmentData?.answerFormat === 'live-voice';

      if (isTwoWayVoice && (assessmentData?.varyByJob || assessmentData?.baseConfig?.generationMode === 'ai')) {
        const checkResult = await checkAndGenerateJobAssessment(applicantId, substage);
        if (!checkResult.proceed) return;
      }

      if (substage.type === 'ai-interview' && !isTwoWayVoice && assessmentData?.baseConfig?.generationMode === 'ai') {
        await ensureAiInterviewQuestionsReady(employerId, assessmentId, applicantId, connectionKey);
      }

      // Generate assessment link based on type
      let assessmentLink = '';
      if (isTwoWayVoice) {
        // Permanent two-way invitations are issued only by the authenticated
        // backend after it reloads company, connection, candidate, job, workflow,
        // assessment, and approval bindings. The browser does not construct a slug.
        if (!currentUser) throw new Error('Employer authentication is required to create an interview invitation.');
        const authToken = await currentUser.getIdToken();
        const invitationResponse = await fetch(`${BACKEND_URL}/ai-interviews/invitations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ companyId: employerId, connectionKey, assessmentId })
        });
        const invitation = await invitationResponse.json();
        if (!invitationResponse.ok || !invitation?.candidateUrl) {
          throw new Error(invitation?.detail || invitation?.error || 'Could not create the secure interview invitation.');
        }
        if (!invitation?.delivery?.delivered || !invitation?.delivery?.complete) {
          throw new Error('The secure invitation was created, but delivery did not complete on every applicable channel. Check the candidate email and company notification sender.');
        }
        return invitation.delivery;
      } else if (isAiEditableAssessmentStepType(substage.type)) {
        // For AI assessments, generate JOBSEEQR link with encrypted applicant data
        // COST-OPTIMIZED: Use URL-safe Base64 to prevent routing issues with '/' characters
        const rawSlug = btoa(`${employerId}_${assessmentId}_${applicantId}`);
        const slug = rawSlug.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        assessmentLink = `${window.location.origin}/take-assessment/${slug}`;
        console.log(`AI Assessment link generated: ${assessmentLink}`);
      } else if (substage.type === 'assessment') {
        // For external assessments, use the external link directly
        assessmentLink = assessmentId; // For external assessments, link field contains the external URL
        console.log(`External assessment link used: ${assessmentLink}`);
      } else {
        console.warn(`Unknown assessment type: ${substage.type}. Cannot generate link.`);
        return;
      }

      if (!assessmentLink) {
        console.error("Failed to generate assessment link. Cannot send notification.");
        return;
      }

      const displayJobTitle = jobTitle || 'the position';
      const isAiInterview = substage.type === 'ai-interview';
      const inviteType = isAiInterview ? 'interview' : 'assessment';
      const inviteLabel = isAiInterview ? 'AI interview' : 'assessment';

      // 4. Create platform message (only for authenticated users)
      if (applicantType === 'authenticated') {
        const displayName = applicantData.username || applicantData.name || 'there';
        const messageContent = `Hello ${displayName.split(' ')[0]},\n\n` +
          `We would like to invite you to the next stage for ${displayJobTitle} at ${companyData.companyName}. ` +
          `${isTwoWayVoice
            ? 'This stage is a live two-way AI voice interview. The AI interviewer speaks with you in real time. With your consent, your camera and microphone are privately recorded and a transcript is created for authorized hiring-team review.'
            : isAiInterview
              ? 'This stage is a one-way AI interview. You will respond to each question in video format.'
              : 'This stage is an assessment. Please click the link below to begin.'}\n\n` +
          `${assessmentLink}\n\n` +
          `Best of luck,\n` +
          `The ${companyData.companyName} Team`;

        const connectionKey = currentApplicant?.connectionKey || `${employerId}_${applicantId}`;
        const messagesRef = ref(database, `connections/${connectionKey}/messages`);
        const newMessageRef = push(messagesRef);
        await set(newMessageRef, {
          message: messageContent,
          user: employerId, // Indicates an automated message
          timestamp: new Date().toISOString()
        });

        console.log(`${inviteLabel} invitation sent to ${applicantData.username || applicantData.name} via platform message (connections/messages).`);
      } else {
        console.log('Skipping platform message for imported applicant - will send email only.');
      }

      // 5. Send email notification via backend (SendGrid styled)
      if (!applicantEmail) {
        console.error(`Cannot send ${inviteLabel} email: No email found for applicant ${applicantId} (type: ${applicantType})`);
        return;
      }

      try {
        const applicantName = applicantData.username || applicantData.name || 'Applicant';
        const assessmentTitle = substage.title || substage.name || (isAiInterview ? 'AI Interview' : 'Assessment');
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

        const resp = await fetch(`${BACKEND_URL}/send-assessment-invite-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyUID: employerId,
            applicant_email: applicantEmail,
            applicant_name: applicantName,
            company_name: companyData.companyName || 'A JOBSEEQR Company',
            assessment_title: assessmentTitle,
            assessment_link: assessmentLink,
            job_title: jobTitle || undefined,
            invite_type: inviteType,
            delivery_type: assessmentData?.deliveryType || undefined
          })
        });

        if (!resp.ok) {
          const errorText = await resp.text();
          throw new Error(`Backend email send failed: ${resp.status} - ${errorText}`);
        }

        const responseData = await resp.json();
        if (!responseData?.success) {
          throw new Error(responseData?.error || 'The email provider did not accept the invitation.');
        }
        console.log(`✓ ${substage.type === 'assessment' ? 'External' : (isAiInterview ? 'AI interview' : 'AI')} invitation email sent successfully.`);
      } catch (emailError) {
        console.error(`✗ Error sending ${substage.type === 'assessment' ? 'external' : (isAiInterview ? 'AI interview' : 'AI')} invitation email:`, emailError);
        throw emailError;
      }

    } catch (error) {
      console.error(`✗ Error sending ${substage?.type === 'assessment' ? 'external' : (substage?.type === 'ai-interview' ? 'AI interview' : 'AI')} assessment notification:`, {
        error: error.message,
        stack: error.stack,
        applicantId,
        substageType: substage?.type,
        substageId: substage?.id
      });
      // Re-throw to allow caller to handle if needed (e.g., bulk operations)
      throw error;
    }
  };



  const handleBulkAssessmentInvites = async (applicants, targetStageId, targetSubstageId, substage) => {
    try {
      const assessmentType = substage.type === 'assessment' ? 'external' : (substage.type === 'ai-interview' ? 'AI interview' : 'AI');
      const assessmentTypeLabel = assessmentType === 'external'
        ? t('workspace.toast.assessmentType.external')
        : (substage.type === 'ai-interview' ? 'AI interview' : t('workspace.toast.assessmentType.ai'));
      console.log(`Starting bulk ${assessmentType} assessment invites for ${applicants.length} candidates`);

      // Validate substage has a link
      if (!substage.link) {
        console.error('Cannot send bulk assessment invites: Substage has no link');
        showToastNotification(t('workspace.toast.missingAssessmentLink'));
        return;
      }

      // Show loading toast
      setBulkAssessmentCount(applicants.length);
      setIsSendingBulkAssessments(true);

      let successCount = 0;
      let failedCount = 0;
      const failedApplicants = [];

      // Move all applicants to the target substage and send assessment invites
      for (const applicant of applicants) {
        try {
          console.log(`Processing ${assessmentType} assessment invite for ${applicant.name || applicant.id}...`);

          // Move applicant to target substage
          await updateApplicantPositionInFirebase(
            applicant.id,
            targetStageId,
            targetSubstageId,
            '',
            { suppressAssessmentNotification: true }
          );

          // Bulk moves suppress the automatic hook above so this awaited call
          // owns exactly one invitation and can report per-candidate failure.
          await sendAssessmentNotification(applicant.id, userUID, substage);

          successCount++;
          console.log(`✓ ${assessmentType} assessment invite sent to ${applicant.name || applicant.id}`);

          // Update local state
          setApplicantsData(prev =>
            prev.map(app =>
              app.id === applicant.id
                ? { ...app, stageId: targetStageId, substageId: targetSubstageId }
                : app
            )
          );
        } catch (error) {
          const applicantName = applicant.name || applicant.id;
          console.error(`✗ Failed to send ${assessmentType} assessment invite to ${applicantName}:`, error);
          failedCount++;
          failedApplicants.push(applicantName);
        }
      }

      // Hide loading toast
      setIsSendingBulkAssessments(false);

      // Log summary
      console.log(`Bulk ${assessmentType} assessment invites completed:`, {
        total: applicants.length,
        success: successCount,
        failed: failedCount,
        failedApplicants: failedApplicants.length > 0 ? failedApplicants : 'None'
      });

      // Show success toast
      if (successCount > 0) {
        const successMessage = substage.type === 'ai-interview'
          ? (
            failedCount > 0
              ? `AI interview invites sent to ${successCount} candidate(s). ${failedCount} failed.`
              : `AI interview invites sent to ${successCount} candidate(s) successfully!`
          )
          : (
            failedCount > 0
              ? t('workspace.toast.assessmentPartial', { type: assessmentTypeLabel, success: successCount, failed: failedCount })
              : t('workspace.toast.assessmentSuccess', { type: assessmentTypeLabel, count: successCount })
          );
        showToastNotification(successMessage);
      } else {
        showToastNotification(
          substage.type === 'ai-interview'
            ? 'AI interview invites could not be sent.'
            : t('workspace.toast.assessmentFailed', { type: assessmentTypeLabel })
        );
      }

    } catch (error) {
      console.error('Error in bulk assessment invites:', error);
      setIsSendingBulkAssessments(false);
      showToastNotification(t('workspace.toast.assessmentError'));
    }
  };

  const callHiredNotificationAndEmail = (applicantId, employerId) => {
    // Implementation for hired notification
    console.log(`Hired notification for applicant ${applicantId}`);
  };

  const handleBulkInterviewScheduleSuccess = (data) => {
    // Move all applicants to the target substage after successful scheduling
    if (bulkInterviewApplicants && bulkInterviewApplicants.length > 0) {
      for (const applicant of bulkInterviewApplicants) {
        updateApplicantPositionInFirebase(
          applicant.id,
          applicant.targetStageId,
          applicant.targetSubstageId
        );
      }

      setApplicantsData(prev =>
        prev.map(app => {
          const scheduledApplicant = bulkInterviewApplicants.find(a => a.id === app.id);
          if (scheduledApplicant) {
            return {
              ...app,
              stageId: scheduledApplicant.targetStageId,
              substageId: scheduledApplicant.targetSubstageId,
              interviewScheduled: true
            };
          }
          return app;
        })
      );
    }

    setShowBulkInterviewScheduler(false);
    setBulkInterviewApplicants([]);
    setBulkInterviewSubstageData(null);

    // Clear selection after scheduling
    if (onClearSelection) {
      onClearSelection();
    }
  };

  // Handle moving applicant in list view
  const handleListViewMove = async (applicant, targetStageId, targetSubstageId, targetSubstage) => {
    console.log('🔍 List View Move Debug:', {
      applicantName: applicant.name,
      targetStageId,
      targetSubstageId,
      targetSubstage,
      substageType: targetSubstage?.type
    });

    if (targetSubstageId === 'offer-sent') {
      openOfferEmailModal([applicant], targetStageId, targetSubstageId);
      return true;
    }

    // Check if it's an INTERVIEW substage — move first, then open scheduler
    if (targetSubstage && targetSubstage.type === 'interview') {
      await updateApplicantPositionInFirebase(applicant.id, targetStageId, targetSubstageId);
      setApplicantsData(prev =>
        prev.map(app =>
          app.id === applicant.id
            ? { ...app, stageId: targetStageId, substageId: targetSubstageId }
            : app
        )
      );
      if (onCheckAndPromptForLink) {
        onCheckAndPromptForLink(applicant.id, targetStageId, targetSubstageId);
      }
      return true;
    }

    // FIXED: Check if it's an ASSESSMENT substage - SEND ASSESSMENT LINK
    if (targetSubstage && isAssessmentStepType(targetSubstage.type)) {
      console.log('✅ Assessment step detected - Sending assessment link');
      if (targetSubstage.type === 'ai-interview') {
        const pausedForQuestions = await openLiveQuestionGate({
          applicants: [applicant],
          targetStageId,
          targetSubstageId,
          targetSubstage,
          currentStageId: applicant.stageId,
          primaryApplicant: applicant,
          isBulk: false
        });
        if (pausedForQuestions) return false;
      }
      // Show loading toast
      showToastNotification(targetSubstage.type === 'ai-interview' ? 'Sending AI interview link to candidate...' : t('workspace.toast.sendingAssessment'));

      // Move applicant and send assessment
      await updateApplicantPositionInFirebase(applicant.id, targetStageId, targetSubstageId);

      setApplicantsData(prev =>
        prev.map(app =>
          app.id === applicant.id
            ? { ...app, stageId: targetStageId, substageId: targetSubstageId }
            : app
        )
      );
      return true;
    }

    // For ALL OTHER substages (static steps, etc.), just move the applicant - NO popups
    console.log('✅ Regular step - Just moving applicant');
    await updateApplicantPositionInFirebase(applicant.id, targetStageId, targetSubstageId);

    setApplicantsData(prev =>
      prev.map(app =>
        app.id === applicant.id
          ? { ...app, stageId: targetStageId, substageId: targetSubstageId }
          : app
      )
    );
    return true;
  };

  // GalleryViewCard Component
  const GalleryViewCard = ({ applicant, currentStageName, dynamicSubstages, selectedJobData, onOpenApplicantPopup, onMoveApplicant, userUID }) => {
  useUiLanguage();
    const [selectedStep, setSelectedStep] = useState(applicant.substageId || '');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
      setSelectedStep(applicant.substageId || '');
    }, [applicant.substageId]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsDropdownOpen(false);
        }
      };

      if (isDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isDropdownOpen]);

    // Helper: sanitize department names for Firebase keys
    const sanitizeDepartmentKey = (dept) => {
      return dept.replace(/[.#$/[\]]/g, '_');
    };

    // Helper: filter substages by department
    const filterSubstagesByDepartment = (substages, currentDept) => {
      return substages.filter(substage => {
        if (!substage.departments || Object.keys(substage.departments).length === 0) {
          return true;
        }
        if (!currentDept) {
          return true;
        }
        const sanitizedDept = sanitizeDepartmentKey(currentDept);
        return substage.departments[sanitizedDept] === true;
      });
    };

    // Helper: sort substages by afterId positioning - FULL topological sort
    const sortSubstagesByWorkflowOrder = (substages) => {
      if (!substages || substages.length === 0) return [];

      const visibleSubstages = [...substages];
      const visibleIds = new Set(visibleSubstages.map(s => s.id));

      const withNoAfter = visibleSubstages.filter(s => !s.afterId);
      withNoAfter.sort((a, b) => {
        const tsA = parseInt(a.id.split('-').pop(), 10);
        const tsB = parseInt(b.id.split('-').pop(), 10);
        return tsB - tsA;
      });

      const ordered = [...withNoAfter];
      const withAfter = visibleSubstages.filter(s => s.afterId);
      const byAfterId = {};
      withAfter.forEach(s => {
        if (!byAfterId[s.afterId]) {
          byAfterId[s.afterId] = [];
        }
        byAfterId[s.afterId].push(s);
      });

      Object.keys(byAfterId).forEach(afterId => {
        byAfterId[afterId].sort((a, b) => {
          const tsA = parseInt(a.id.split('-').pop(), 10);
          const tsB = parseInt(b.id.split('-').pop(), 10);
          return tsB - tsA;
        });
      });

      const placed = new Set(ordered.map(s => s.id));
      let guard = 0;
      const maxIterations = 100;

      while (Object.keys(byAfterId).length > 0 && guard < maxIterations) {
        let madeProgress = false;

        for (const afterId of Object.keys(byAfterId)) {
          const group = byAfterId[afterId];
          if (group.length === 0) {
            delete byAfterId[afterId];
            continue;
          }

          const afterIdVisible = visibleIds.has(afterId);

          if (afterIdVisible && placed.has(afterId)) {
            const idx = ordered.findIndex(item => item.id === afterId);
            if (idx !== -1) {
              ordered.splice(idx + 1, 0, ...group);
              group.forEach(s => placed.add(s.id));
              delete byAfterId[afterId];
              madeProgress = true;
            }
          } else if (!afterIdVisible) {
            ordered.push(...group);
            group.forEach(s => placed.add(s.id));
            delete byAfterId[afterId];
            madeProgress = true;
          }
        }

        if (!madeProgress) break;
        guard++;
      }

      return ordered;
    };

    // Build list of available substages in EXACT workflow order
    const getAvailableSteps = () => {
      const currentDept = selectedJobData?.data?.department || selectedJobData?.department || null;
      const steps = [];

      steps.push({ id: 'all-applicants-1', name: translateStaticStepName('all-applicants-1') || 'All Applicants', stageId: 'stage-1', type: 'static' });

      const filterSubstage = dynamicSubstages?.['stage-1']?.find(s => s.id === 'filter-applicants');
      if (filterSubstage) {
        const name = filterSubstage.title || filterSubstage.name || translateStaticStepName(filterSubstage.id) || filterSubstage.id;
        steps.push({ id: filterSubstage.id, name, stageId: 'stage-1', type: filterSubstage.type, afterId: filterSubstage.afterId });
      }

      const stage1Filtered = filterSubstagesByDepartment(
        dynamicSubstages?.['stage-1']?.filter(s => s.id !== 'filter-applicants') || [],
        currentDept
      );
      const stage1Ordered = sortSubstagesByWorkflowOrder(stage1Filtered);
      stage1Ordered.forEach(s => {
        const name = s.title || s.name || translateStaticStepName(s.id) || s.id;
        steps.push({ id: s.id, name, stageId: 'stage-1', type: s.type, afterId: s.afterId, ...s });
      });

      steps.push({ id: 'offer-sent', name: translateStaticStepName('offer-sent') || 'Offer Sent', stageId: 'stage-1', type: 'static' });
      steps.push({ id: 'hired-applicants', name: translateStaticStepName('hired-applicants') || 'Hired', stageId: 'stage-1', type: 'static' });

      return steps;
    };

    const handleStepChange = async (e) => {
      e.stopPropagation(); // Prevent card click
      const selectedStepId = e.target.value;

      if (!selectedStepId || selectedStepId === applicant.substageId) return;

      const steps = getAvailableSteps();
      const targetStep = steps.find(s => s.id === selectedStepId);

      if (!targetStep) {
        console.error('Target step not found:', selectedStepId);
        return;
      }

      // Check if skipping steps
      const currentStepIndex = steps.findIndex(s => s.id === applicant.substageId);
      const targetStepIndex = steps.findIndex(s => s.id === selectedStepId);

      if (targetStepIndex > currentStepIndex + 1) {
        const skippedSteps = steps.slice(currentStepIndex + 1, targetStepIndex);
        const skippedStepNames = skippedSteps.map(s => s.name);

        const confirmMessage = t('workspace.skipStepsConfirm', {
          name: applicant.name || applicant.username || t('workspace.candidate'),
          steps: skippedStepNames.join('\n• ')
        });

        if (!window.confirm(confirmMessage)) {
          setSelectedStep(applicant.substageId);
          return;
        }
      }

      // Move the applicant
      const moved = await onMoveApplicant(applicant, targetStep.stageId, targetStep.id, targetStep);

      // A false result means the question-review modal owns the pending move.
      if (moved !== false) setSelectedStep(selectedStepId);
    };

    const availableSteps = getAvailableSteps();

    // Helper: get FontAwesome icon class for step type (matching board view)
    const getStepIconClass = (step) => {
      if (step?.type === 'ai-interview' && step?.deliveryType === 'two-way-voice') {
        return 'fa-solid fa-robot';
      }
      // Type-based icons (for dynamic substages)
      const typeIconMap = {
        'interview': 'fa-solid fa-calendar-day',
        'assessment': 'fa-solid fa-gears',
        'ai-assessment': 'fa-solid fa-robot',
        'ai-interview': 'fa-solid fa-video',
        'paperwork': 'fa-solid fa-file-signature',
        'contact-info': 'fa-solid fa-address-card',
        'logistics': 'fa-solid fa-boxes-packing',
        'send-welcome-kit': 'fa-solid fa-gift',
        'meet-the-team-and-assign-buddy': 'fa-solid fa-users',
        'first-week-checklist': 'fa-solid fa-list-check',
        'training-calendar': 'fa-solid fa-calendar-week',
        'post-training-assessment': 'fa-solid fa-clipboard-list',
        'probation': 'fa-solid fa-hourglass-half',
        'filtering': 'fa-solid fa-filter'
      };

      if (step.type && typeIconMap[step.type]) {
        return typeIconMap[step.type];
      }

      // Static step icons (by ID)
      const staticIcons = {
        'all-applicants-1': 'fa-solid fa-users',
        'filter-applicants': 'fa-solid fa-filter',
        'offer-sent': 'fa-solid fa-envelope-open-text',
        'hired-applicants': 'fa-solid fa-user-check',
      };

      return staticIcons[step.id] || 'fa-solid fa-people-group';
    };

    const handleCardClick = (e) => {
      // Don't open popup if clicking on the dropdown
      if (e.target.closest('.gallery-card-stage-dropdown')) {
        return;
      }
      onOpenApplicantPopup(applicant);
    };

    const handleDropdownItemClick = async (stepId) => {
      if (stepId === selectedStep) {
        setIsDropdownOpen(false);
        return;
      }

      const steps = getAvailableSteps();
      const targetStep = steps.find(s => s.id === stepId);

      if (!targetStep) return;

      // Check if skipping steps - FIXED to match board/list view logic
      const currentStepIndex = steps.findIndex(s => s.id === applicant.substageId);
      const targetStepIndex = steps.findIndex(s => s.id === stepId);

      // Only warn if moving forward and skipping at least one step
      if (targetStepIndex > currentStepIndex + 1) {
        const skippedSteps = steps.slice(currentStepIndex + 1, targetStepIndex);
        const skippedStepNames = skippedSteps.map(s => translateSubstageName(s.id, s.name));

        const confirmMessage = t('workspace.skipStepsConfirm', {
          name: applicant.name || applicant.username || t('workspace.candidate'),
          steps: skippedStepNames.join('\n• ')
        });

        if (!window.confirm(confirmMessage)) {
          setIsDropdownOpen(false);
          return;
        }
      }

      // Status-aware warnings before move (gallery/list dropdown)
      const statusForApplicant = applicantStatusMap[applicant.id] || {};
      const isNeverOpenedApplicant = !applicant.firstOpenedAt;
      const curSubstageForApplicant = dynamicSubstages[applicant.stageId]?.find(s => s.id === applicant.substageId);
      const curSubstageTypeForApplicant = curSubstageForApplicant?.type || 'static';
      const isGalleryAssessmentStep = isAssessmentStepType(curSubstageTypeForApplicant);
      const galleryAssessmentStepStatus = isGalleryAssessmentStep && curSubstageForApplicant?.link
        ? (statusForApplicant.assessmentsByStep || {})[curSubstageForApplicant.link]
        : null;
      if (isGalleryAssessmentStep && galleryAssessmentStepStatus !== 'available') {
        if (!window.confirm(t('cellStatus.warnMoveAwaitingResults'))) { setIsDropdownOpen(false); return; }
      } else if (curSubstageTypeForApplicant === 'interview' && statusForApplicant.interviewScheduled) {
        const dl = statusForApplicant.interviewScheduled.date || '';
        if (!window.confirm(t('cellStatus.warnMoveInterviewScheduled', { date: dl }))) { setIsDropdownOpen(false); return; }
      } else if (isNeverOpenedApplicant) {
        if (!window.confirm(t('cellStatus.warnMoveNeverOpened'))) { setIsDropdownOpen(false); return; }
      }

      // Move the applicant
      const moved = await onMoveApplicant(applicant, targetStep.stageId, targetStep.id, targetStep);
      if (moved !== false) setSelectedStep(stepId);
      setIsDropdownOpen(false);
    };

    const currentStep = availableSteps.find(s => s.id === selectedStep);

    return (
      <div className={`gallery-card ${isDropdownOpen ? 'dropdown-open' : ''}`} onClick={handleCardClick} style={{ position: 'relative' }}>
        {/* Star icon for starred applicants */}
        {applicant.starred && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            fontSize: '16px',
            color: 'var(--app-tone-ffc107, #ffc107)',
            zIndex: 10
          }}>
            <i className="fa-solid fa-star"></i>
          </div>
        )}

        {/* Card content */}
        <div className="gallery-card-name">
          {applicant.name || applicant.username || uiText("Unknown")}
        </div>

        {/* Custom dropdown where stage tag was */}
        <div className="gallery-card-stage-dropdown" onClick={(e) => e.stopPropagation()} ref={dropdownRef}>
          <div
            className="gallery-dropdown-trigger"
            onClick={() => setIsDropdownOpen(prev => !prev)}
          >
            <i className={currentStep ? getStepIconClass(currentStep) : 'fa-solid fa-circle'}></i>
            <span>{currentStep ? currentStep.name : t('workspace.selectStep')}</span>
            <i className={`fa-solid fa-chevron-${isDropdownOpen ? 'up' : 'down'} gallery-dropdown-arrow`}></i>
          </div>

          {isDropdownOpen && (
            <div className="gallery-dropdown-menu">
              {availableSteps.map(step => (
                <div
                  key={step.id}
                  className={`gallery-dropdown-item ${step.id === selectedStep ? 'selected' : ''}`}
                  onClick={() => handleDropdownItemClick(step.id)}
                >
                  <i className={getStepIconClass(step)}></i>
                  <span>{step.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="gallery-card-footer">
          <span className="gallery-card-badge">
            <i className="fa-solid fa-clock"></i>
            {(() => {
              // READS FROM: connections/{connectionKey}/createdAt in Firebase RTDB
              // Represents when the applicant applied for the job (or CV was uploaded)
              const createdAt = applicant.createdAt || Date.now();
              const daysSince = Math.floor((Date.now() - createdAt) / (1000 * 60 * 60 * 24));
              if (daysSince === 0) return t('workspace.today');
              if (daysSince === 1) return t('workspace.dayAgo');
              return t('workspace.daysAgo', { days: daysSince });
            })()}
          </span>
        </div>
      </div>
    );
  };

  // ListViewRow Component
  const ListViewRow = ({ applicant, currentStageName, dynamicSubstages, selectedJobData, onOpenApplicantPopup, onMoveApplicant, userUID, isOfferApprover, pendingApprovals }) => {
  useUiLanguage();
    const [selectedStep, setSelectedStep] = useState('');

    // Helper: sanitize department names for Firebase keys
    const sanitizeDepartmentKey = (dept) => {
      return dept.replace(/[.#$/[\]]/g, '_');
    };

    // Helper: filter substages by department
    const filterSubstagesByDepartment = (substages, currentDept) => {
      return substages.filter(substage => {
        if (!substage.departments || Object.keys(substage.departments).length === 0) {
          return true;
        }
        if (!currentDept) {
          return true;
        }
        const sanitizedDept = sanitizeDepartmentKey(currentDept);
        return substage.departments[sanitizedDept] === true;
      });
    };

    // Helper: sort substages by afterId positioning - FULL topological sort matching StageSection.js
    const sortSubstagesByWorkflowOrder = (substages) => {
      if (!substages || substages.length === 0) return [];

      const visibleSubstages = [...substages];
      const visibleIds = new Set(visibleSubstages.map(s => s.id));

      // Start with those without afterId
      const withNoAfter = visibleSubstages.filter(s => !s.afterId);

      // Sort those by timestamp DESCENDING (latest first)
      withNoAfter.sort((a, b) => {
        const tsA = parseInt(a.id.split('-').pop(), 10);
        const tsB = parseInt(b.id.split('-').pop(), 10);
        return tsB - tsA; // DESCENDING
      });

      const ordered = [...withNoAfter];

      // Group substages by their afterId
      const withAfter = visibleSubstages.filter(s => s.afterId);
      const byAfterId = {};
      withAfter.forEach(s => {
        if (!byAfterId[s.afterId]) {
          byAfterId[s.afterId] = [];
        }
        byAfterId[s.afterId].push(s);
      });

      // Sort each group by timestamp DESCENDING (newest first)
      Object.keys(byAfterId).forEach(afterId => {
        byAfterId[afterId].sort((a, b) => {
          const tsA = parseInt(a.id.split('-').pop(), 10);
          const tsB = parseInt(b.id.split('-').pop(), 10);
          return tsB - tsA; // DESCENDING - latest timestamp first
        });
      });

      // Now iteratively place substages based on afterId
      const placed = new Set(ordered.map(s => s.id));
      let guard = 0;
      const maxIterations = 100;

      while (Object.keys(byAfterId).length > 0 && guard < maxIterations) {
        let madeProgress = false;

        for (const afterId of Object.keys(byAfterId)) {
          const group = byAfterId[afterId];
          if (group.length === 0) {
            delete byAfterId[afterId];
            continue;
          }

          const afterIdVisible = visibleIds.has(afterId);

          if (afterIdVisible && placed.has(afterId)) {
            // Place all substages in this group right after afterId
            const idx = ordered.findIndex(item => item.id === afterId);
            if (idx !== -1) {
              // Insert all items from this group (already sorted by timestamp desc)
              ordered.splice(idx + 1, 0, ...group);
              group.forEach(s => placed.add(s.id));
              delete byAfterId[afterId];
              madeProgress = true;
            }
          } else if (!afterIdVisible) {
            // afterId is not visible (filtered out), place at end
            ordered.push(...group);
            group.forEach(s => placed.add(s.id));
            delete byAfterId[afterId];
            madeProgress = true;
          }
        }

        if (!madeProgress) break;
        guard++;
      }

      return ordered;
    };

    // Build list of available substages in EXACT workflow order - STOP AT HIRED!
    const getAvailableSteps = () => {
      const currentDept = selectedJobData?.data?.department || selectedJobData?.department || null;
      const steps = [];

      // 1. All Applicants (static - always first)
      steps.push({ id: 'all-applicants-1', name: translateStaticStepName('all-applicants-1') || 'All Applicants', stageId: 'stage-1', type: 'static' });

      // 2. Filter Applicants (if exists)
      const filterSubstage = dynamicSubstages?.['stage-1']?.find(s => s.id === 'filter-applicants');
      if (filterSubstage) {
        const name = filterSubstage.title || filterSubstage.name || translateStaticStepName(filterSubstage.id) || filterSubstage.id;
        steps.push({ id: filterSubstage.id, name, stageId: 'stage-1', type: filterSubstage.type, afterId: filterSubstage.afterId });
      }

      // 3. ALL user-added dynamic substages in EXACT workflow order
      const stage1Filtered = filterSubstagesByDepartment(
        dynamicSubstages?.['stage-1']?.filter(s => s.id !== 'filter-applicants') || [],
        currentDept
      );
      const stage1Ordered = sortSubstagesByWorkflowOrder(stage1Filtered);
      stage1Ordered.forEach(s => {
        const name = s.title || s.name || translateStaticStepName(s.id) || s.id;
        steps.push({ id: s.id, name, stageId: 'stage-1', type: s.type, afterId: s.afterId, ...s });
      });

      // 4. Offer Sent (static)
      steps.push({ id: 'offer-sent', name: translateStaticStepName('offer-sent') || 'Offer Sent', stageId: 'stage-1', type: 'static' });

      // 5. Hired (static) - STOP HERE!
      steps.push({ id: 'hired-applicants', name: translateStaticStepName('hired-applicants') || 'Hired', stageId: 'stage-1', type: 'static' });

      // DO NOT include "Move to Onboarding", Stage 2, or Stage 3 steps in dropdown!

      return steps;
    };

    const handleStepChange = async (e) => {
      const selectedStepId = e.target.value;

      if (!selectedStepId) return;

      const steps = getAvailableSteps();
      const targetStep = steps.find(s => s.id === selectedStepId);

      if (!targetStep) {
        console.error('Target step not found:', selectedStepId);
        return;
      }

      // Check if skipping steps
      const currentStepIndex = steps.findIndex(s => s.id === applicant.substageId);
      const targetStepIndex = steps.findIndex(s => s.id === selectedStepId);

      if (targetStepIndex > currentStepIndex + 1) {
        const skippedSteps = steps.slice(currentStepIndex + 1, targetStepIndex);
        const skippedStepNames = skippedSteps.map(s => s.name);

        const confirmMessage = t('workspace.skipStepsConfirm', {
          name: applicant.name || applicant.username || t('workspace.candidate'),
          steps: skippedStepNames.join('\n• ')
        });

        if (!window.confirm(confirmMessage)) {
          setSelectedStep('');
          return;
        }
      }

      // Status-aware warnings before move (list view dropdown)
      const statusForListApplicant = applicantStatusMap[applicant.id] || {};
      const isListApplicantNeverOpened = !applicant.firstOpenedAt;
      const curSubstageForList = dynamicSubstages[applicant.stageId]?.find(s => s.id === applicant.substageId);
      const curSubstageTypeForList = curSubstageForList?.type || 'static';
      const isListAssessmentStep = isAssessmentStepType(curSubstageTypeForList);
      const listAssessmentStepStatus = isListAssessmentStep && curSubstageForList?.link
        ? (statusForListApplicant.assessmentsByStep || {})[curSubstageForList.link]
        : null;
      if (isListAssessmentStep && listAssessmentStepStatus !== 'available') {
        if (!window.confirm(t('cellStatus.warnMoveAwaitingResults'))) { setSelectedStep(''); return; }
      } else if (curSubstageTypeForList === 'interview' && statusForListApplicant.interviewScheduled) {
        const dl = statusForListApplicant.interviewScheduled.date || '';
        if (!window.confirm(t('cellStatus.warnMoveInterviewScheduled', { date: dl }))) { setSelectedStep(''); return; }
      } else if (isListApplicantNeverOpened) {
        if (!window.confirm(t('cellStatus.warnMoveNeverOpened'))) { setSelectedStep(''); return; }
      }

      // Move the applicant
      await onMoveApplicant(applicant, targetStep.stageId, targetStep.id, targetStep);

      // Reset dropdown
      setSelectedStep('');
    };

    const availableSteps = getAvailableSteps();

    // Handle viewing resume - FIXED to fetch CV data like ApplicantDetailsPopup.js
    const handleViewResume = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('🔍 View Resume clicked for:', applicant.name, applicant);

      try {
        // Determine connection key and applicant type
        const baseApplicantId = applicant.id.split('_copy_')[0].replace(/^imported_/, '');
        let connectionKey = applicant.connectionKey;

        if (!connectionKey) {
          // Construct connection key based on applicant ID pattern
          if (applicant.id.startsWith('temp_') || applicant.id.startsWith('imported_temp_')) {
            connectionKey = `${userUID}_imported_${baseApplicantId}`;
          } else {
            connectionKey = `${userUID}_${applicant.id}`;
          }
        }

        // Get connection data to determine applicant type
        const connectionRef = ref(database, `connections/${connectionKey}`);
        const connectionSnapshot = await get(connectionRef);

        if (!connectionSnapshot.exists()) {
          console.error('❌ Connection not found:', connectionKey);
          alert(uiText('Unable to load resume. Connection not found.'));
          return;
        }

        const connectionData = connectionSnapshot.val();
        const applicantType = connectionData.applicantType || (connectionData.tempProfileId ? 'imported' : 'authenticated');

        // Load person data to get CV link
        let personData = null;
        if (applicantType === 'authenticated') {
          const personRef = ref(database, `people/${applicant.id}`);
          const personSnapshot = await get(personRef);
          personData = personSnapshot.val();
          console.log('✅ Loaded authenticated user data for CV');
        } else {
          // For imported applicants, use tempProfileId from connection data
          const tempId = connectionData.tempProfileId || baseApplicantId;
          // Try NEW path first
          let profileRef = ref(database, `imported_profiles/${userUID}/${tempId}`);
          let profileSnapshot = await get(profileRef);

          // Fallback to legacy path
          if (!profileSnapshot.exists()) {
            profileRef = ref(database, `imported_profiles/${tempId}`);
            profileSnapshot = await get(profileRef);
          }

          personData = profileSnapshot.val();
          console.log('✅ Loaded imported profile data for CV');
        }

        // Get CV link from person data
        const resumeUrl = (personData?.cv && personData.cv.Link) ? personData.cv.Link : null;

        if (resumeUrl) {
          console.log('✅ Opening resume:', resumeUrl);
          window.open(resumeUrl, '_blank');
        } else {
          console.warn('⚠️ No resume URL found in person data');
          alert(uiText('No resume available for this candidate'));
        }
      } catch (error) {
        console.error('❌ Error loading resume:', error);
        alert(uiText('Failed to load resume. Please try again.'));
      }
    };

    const handleNameClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Name clicked for:', applicant.name, applicant);
      onOpenApplicantPopup(applicant);
    };

    const isSelected = listSelectedApplicantIds.includes(applicant.id);

    return (
      <tr className={`list-view-row ${isSelected ? 'list-view-row-selected' : ''}`}>
        <td className="compare-select-cell" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="compare-select-trigger"
            role="checkbox"
            aria-checked={isSelected}
            onMouseDown={(e) => handleApplicantSelectionMouseDown(e, applicant.id, isSelected)}
            onMouseEnter={() => handleApplicantSelectionDragEnter(applicant.id)}
            onKeyDown={(e) => handleApplicantSelectionKeyDown(e, applicant.id, isSelected)}
            title={t('workspace.selectionToggleHint')}
          >
            <span className={`compare-select-checkbox ${isSelected ? 'compare-select-checkbox-active' : ''}`}>
              {isSelected ? <i className="fa-solid fa-check"></i> : null}
            </span>
          </button>
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          <span
            className="list-view-name"
            onClick={handleNameClick}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            {applicant.starred && (
              <i className="fa-solid fa-star" style={{ color: 'var(--app-tone-ffc107, #ffc107)', fontSize: '14px' }}></i>
            )}
            {applicant.name || applicant.username || uiText("Unknown")}
            {isOfferApprover && pendingApprovals && pendingApprovals.some(p => p.type === 'offer_letter' && p.entityId === applicant.id) && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: 'rgba(235, 141, 64, 0.15)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  border: '1px solid rgba(235, 141, 64, 0.3)',
                  marginLeft: '8px'
                }}
                title={t('workspace.offerAwaitingApproval', 'Offer awaiting approval')}
              >
                <i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--app-tone-eb8d40, #EB8D40)', fontSize: '11px' }}></i>
                <span style={{ color: 'var(--app-tone-eb8d40, #EB8D40)', fontSize: '10px', fontWeight: 600 }}>
                  {t('workspace.offerPendingShort', 'Offer Pending')}
                </span>
              </div>
            )}
          </span>
        </td>
        <td onClick={(e) => e.stopPropagation()}>{currentStageName}</td>
        <td onClick={(e) => e.stopPropagation()}>
          {(() => {
            const createdAt = applicant.createdAt || 0;
            if (!createdAt) return '—';
            const date = new Date(createdAt);
            return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
          })()}
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          <select
            value={selectedStep}
            onChange={handleStepChange}
            className="list-view-dropdown"
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">{t('workspace.selectStep')}</option>
            {availableSteps.map(step => (
              <option key={step.id} value={step.id}>
                {step.name}
              </option>
            ))}
          </select>
        </td>
        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <button
            className="list-view-action-btn"
            onClick={handleViewResume}
            title={t('details.viewResume')}
            type="button"
          >
            <i className="fa-solid fa-file-alt"></i>
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div
      className="app-all-posts"
      data-tutorial="ats-workspace"
      style={{
        marginBottom: '25px',
        fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
        height: '100%',
        paddingTop: '50px',
        paddingBottom: '40px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
      {/* Header Section */}
      <div
        data-tutorial="ats-toolbar"
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '80%',
          margin: '0 10%',
          flexShrink: 0
        }}>
        {/* Redesigned Job Dropdown */}
        <div
          style={{ position: 'relative', display: 'inline-block' }}
          data-tutorial="job-selector"
        >
          <button
            style={{
              backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
            type="button"
            aria-expanded={isDropdownOpen}
            onClick={() => setIsDropdownOpen(prev => !prev)}
            data-light-control={"quiet"}>
            <span style={{
              fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
              fontWeight: '500',
              fontSize: '24px',
              lineHeight: '29px',
              color: 'var(--app-text, #FFFFFF)',
              maxWidth: '520px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {(!selectedJobName || selectedJobName === 'Select a job post') ? t('workspace.selectJob') : selectedJobName}
            </span>
            <i className="fa-solid fa-circle-chevron-down" style={{
              color: '#568ded',
              fontSize: '20px'
            }}></i>
          </button>
          <div className="dropdown-content" style={{ display: isDropdownOpen ? 'block' : 'none' }}>
            {isJobOptionsLoading ? (
              <div className="dropdown-item">
                <div className="dropdown-item-empty-state">{t('workspace.loadingJobs')}</div>
              </div>
            ) : visibleJobOptions.length === 0 ? (
              <div className="dropdown-item">
                <div className="dropdown-item-empty-state">
                  {teamAccess.isScoped ? uiText("No jobs are available in your assigned departments.") : t('workspace.noJobsAvailable')}
                </div>
              </div>
            ) : (
              visibleJobOptions.map((option, index) => (
                <div key={index} className="dropdown-item">
                  <a onClick={() => handleJobSelect(option.title)}>
                    <div className="dropdown-item-title">{uiText(option.title)}</div>
                    <div className="dropdown-item-subtitle">{formatOptionSubtitle(option)}</div>
                  </a>
                  {option.type === 'custom_list' && (
                    <button
                      className="dropdown-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCustomList(option);
                      }}
                      title={t('workspace.editList')}
                    >
                      <i className="fa-solid fa-edit"></i>
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {!canManageApplicants && (
          <div style={{ marginInlineStart: '20px' }}>
            <div style={{ display: 'flex' }}>
              <i className="fa-solid fa-lock" style={{ color: '#568ded' }}></i>
              <p style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', color: 'var(--app-text, #FFFFFF)', fontSize: '16px', marginInlineStart: '5px', marginBottom: '0' }}>{uiText("Applicant management is disabled for your account.")}</p>
            </div>
          </div>
        )}

        {/* Filter Job Area */}
        {isFilterJobArea && canManageApplicants && (
          <div style={{ marginInlineStart: '20px' }}>
            <div style={{ display: 'flex' }}>
              <i className="fa-solid fa-warning" style={{ color: 'var(--app-tone-eed202, #eed202)' }}></i>
              <p style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', color: 'var(--app-text, #FFFFFF)', fontSize: '16px', marginInlineStart: '5px', marginBottom: '0' }}>{t('workspace.filteringTurnedOff')}</p>
            </div>
            <p style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', color: 'var(--app-text, #FFFFFF)', fontSize: '16px', marginBottom: '0' }}>
              {t('workspace.forThisJob')} <a onClick={onAddFilteringToJob} style={{ color: '#568ded', cursor: 'pointer' }}>{t('workspace.turnItOn')}</a>
            </p>
          </div>
        )}

        {/* Global Search Icon with Tooltip */}
        <span className="search-tooltip-container" style={{ marginInlineStart: 'auto', marginInlineEnd: '15px' }}>
          <i
            className="fa-solid fa-magnifying-glass"
            style={{
              color: '#568ded',
              fontSize: '24px',
              cursor: 'pointer'
            }}
            onClick={onOpenGlobalSearch}
          />
          <span className="search-tooltip">{t('workspace.searchApplicants')}</span>
        </span>

	        {/* CV Upload Icon */}
	        <span className="search-tooltip-container" style={{ marginInlineEnd: '15px' }}>
	          <i
	            className="fa-solid fa-upload"
            style={{
              color: '#568ded',
              fontSize: '24px',
              cursor: 'pointer'
            }}
            onClick={onOpenCVUpload}
	          />
	          <span className="search-tooltip">{t('workspace.uploadCVs')}</span>
	        </span>

	        {/* Offers Hub Icon */}
	        {canAccessOffersHub && (
	          <span className="search-tooltip-container notifications-button-container" style={{ marginInlineEnd: '15px' }}>
	            <i
	              className="fa-solid fa-file-signature"
	              style={{
	                color: '#568ded',
	                fontSize: '24px',
	                cursor: 'pointer'
	              }}
	              onClick={() => setShowOffersHub(true)}
	            />
	            {visibleOffersHubItems.length > 0 && (
	              <div className="notification-badge">
	                {visibleOffersHubItems.length > 99 ? '99+' : visibleOffersHubItems.length}
	              </div>
	            )}
	            <span className="search-tooltip">{uiText("Offers (")}{visibleOffersHubItems.length})</span>
	          </span>
	        )}

	        {/* Notifications Icon */}
	        <span className="search-tooltip-container notifications-button-container">
          <i
            className="fa-solid fa-bell"
            style={{
              color: '#568ded',
              fontSize: '24px',
              cursor: 'pointer'
            }}
            onClick={onOpenNotifications}
          />
          {notificationCount > 0 && (
            <div className="notification-badge">
              {notificationCount > 99 ? '99+' : notificationCount}
            </div>
          )}
          <span className="search-tooltip">{t('notifications.title')} ({notificationCount})</span>
        </span>
      </div>

      {/* View Mode Toggle & Controls - Sticky Container */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        paddingTop: '20px',
        paddingBottom: '15px',
        width: '80%',
        margin: '0 10%'
      }}>
        {/* View Mode Toggle */}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          marginBottom: viewMode === 'gallery' || viewMode === 'list' ? '12px' : '0'
        }}>
          <button
            type="button"
            className={`view-toggle-chip ${viewMode === 'board' ? 'view-toggle-chip-active' : ''}`}
            onClick={() => setViewMode('board')}
          >
            <i className="fa-solid fa-table-columns" style={{ marginInlineEnd: '8px' }}></i>
            {t('workspace.boardView')}
          </button>
          <button
            type="button"
            className={`view-toggle-chip ${viewMode === 'list' ? 'view-toggle-chip-active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <i className="fa-solid fa-list" style={{ marginInlineEnd: '8px' }}></i>
            {t('workspace.listView')}
          </button>
          <button
            type="button"
            className={`view-toggle-chip ${viewMode === 'gallery' ? 'view-toggle-chip-active' : ''}`}
            onClick={() => setViewMode('gallery')}
          >
            <i className="fa-solid fa-grip" style={{ marginInlineEnd: '8px' }}></i>
            {t('workspace.galleryView')}
          </button>
        </div>

        {/* Filter Controls - Show for all views */}
        {(viewMode === 'gallery' || viewMode === 'list' || viewMode === 'board') && (
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            marginTop: viewMode === 'board' ? '12px' : '0'
          }}>
            {/* Filter dropdown */}
            <select
              className="gallery-sort-compact-dropdown"
              value={galleryFilterBy}
              onChange={(e) => setGalleryFilterBy(e.target.value)}
            >
              <option value="">{t('workspace.filterBy')}</option>
              <option value="starred">{t('workspace.showStarredOnly')}</option>
              <option value="applied_last_7">{t('workspace.appliedThisWeek')}</option>
              <option value="applied_over_7">{t('workspace.appliedOverWeek')}</option>
              <option value="step">{t('workspace.specificStep')}</option>
            </select>

            {galleryFilterBy === 'step' && (
              <select
                className="gallery-sort-compact-dropdown"
                value={galleryFilterStep}
                onChange={(e) => setGalleryFilterStep(e.target.value)}
              >
                <option value="">{t('workspace.chooseStep')}</option>
                {/* Static steps */}
                <option value="all-applicants-1">{t('substages.allApplicants')}</option>
                <option value="filter-applicants">{t('substages.fitApplicants')}</option>
                {/* Dynamic steps */}
                {(dynamicSubstages['stage-1'] || []).filter(s => s.id !== 'filter-applicants').map(s => (
                  <option key={s.id} value={s.id}>{s.title || s.name}</option>
                ))}
                <option value="offer-sent">{t('substages.offerSent')}</option>
                <option value="hired-applicants">{t('substages.hired')}</option>
              </select>
            )}

            {/* Sort and Group controls - Only for gallery view */}
            {viewMode === 'gallery' && (
              <>
                {/* Sort By Dropdown */}
                <select
                  className="gallery-sort-compact-dropdown"
                  value={gallerySortBy}
                  onChange={(e) => setGallerySortBy(e.target.value)}
                >
                  <option value="">{t('workspace.sortBy')}</option>
                  <option value="date">{t('workspace.sortByDateApplied')}</option>
                  <option value="updated">{t('workspace.sortByLastUpdated')}</option>
                  <option value="step">{t('workspace.sortByWorkflowStep')}</option>
                </select>

                {/* Sort Order Toggle - Only show if sorting is not by step */}
                {gallerySortBy && gallerySortBy !== 'step' && (
                  <button
                    className="gallery-sort-order-btn"
                    onClick={() => setGallerySortOrder(gallerySortOrder === 'asc' ? 'desc' : 'asc')}
                    title={gallerySortOrder === 'asc' ? t('workspace.ascending') : t('workspace.descending')}
                  >
                    <i className={`fa-solid fa-arrow-${gallerySortOrder === 'asc' ? 'up' : 'down'}-1-9`}></i>
                    <span>
                      {gallerySortOrder === 'asc' ? t('workspace.oldestFirst') : t('workspace.newestFirst')}
                    </span>
                  </button>
                )}

                {/* Group By Step Toggle */}
                <button
                  className={`gallery-group-toggle ${galleryGroupByStep ? 'gallery-group-toggle-active' : ''}`}
                  onClick={() => {
                    setGalleryGroupByStep(!galleryGroupByStep);
                    // When enabling grouping, expand all stages by default
                    if (!galleryGroupByStep) {
                      const allStages = {};
                      ['all-applicants-1', 'filter-applicants', 'offer-sent', 'hired-applicants'].forEach(id => {
                        allStages[id] = true;
                      });
                      // Also expand dynamic substages
                      if (dynamicSubstages['stage-1']) {
                        dynamicSubstages['stage-1'].forEach(sub => {
                          allStages[sub.id] = true;
                        });
                      }
                      setExpandedStages(allStages);
                    }
                  }}
                >
                  <i className="fa-solid fa-layer-group" style={{ marginInlineEnd: '8px' }}></i>
                  {t('workspace.groupByStep')}
                  {galleryGroupByStep && (
                    <i className="fa-solid fa-xmark" style={{ marginInlineStart: '8px' }}></i>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Applicants Workspace Section - Board View */}
      {viewMode === 'board' && (() => {
        // Apply filters for board view
        let filteredApplicants = [...applicantsData];
        if (galleryFilterBy === 'starred') {
          filteredApplicants = filteredApplicants.filter(a => a.starred === true);
        } else if (galleryFilterBy === 'applied_last_7') {
          const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
          filteredApplicants = filteredApplicants.filter(a => (a.createdAt || 0) >= cutoff);
        } else if (galleryFilterBy === 'applied_over_7') {
          const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
          filteredApplicants = filteredApplicants.filter(a => (a.createdAt || 0) < cutoff);
        } else if (galleryFilterBy === 'step' && galleryFilterStep) {
          filteredApplicants = filteredApplicants.filter(a => a.substageId === galleryFilterStep);
        }

        return (
          <div
            ref={boardRef}
            data-tutorial="workflow-board"
            style={{
              margin: '25px 10%',
              marginTop: '0',
              display: 'flex',
              width: '80%',
              overflowX: 'auto',
              overflowY: 'hidden',
              flex: 1,
              position: 'relative',
              zIndex: 1
            }}
          >

            {/* Stage 1 - Hiring Process */}
            <StageSection
              stageId="stage-1"
              isActive={currActiveStage === 'stage-1'}
              applicantsData={filteredApplicants}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onOpenApplicantPopup={onOpenApplicantPopup}
              onOpenFilteringSubstagePopup={onOpenFilteringSubstagePopup}
              userUID={userUID}
              dynamicSubstages={dynamicSubstages?.['stage-1'] || []}
              onRemoveFilterSubstage={onRemoveFilterSubstage}
              onOpenSubstagePopup={onOpenSubstagePopup}
              selectedApplicants={selectedApplicants}
              currentJobDepartment={selectedJobData?.data?.department || selectedJobData?.department || null}
              isOfferApprover={canReviewOffer}
              pendingApprovals={pendingApprovals}
              applicantStatusMap={applicantStatusMap}
              jobId={jobId}
              onSubstageUpdated={onSubstageUpdated}
              onSubstageDeleted={onSubstageDeleted}
              onMultipleApplicantsRemoved={onMultipleApplicantsRemoved}
            />

            {/* Stage 2 - Pre-Onboarding */}
            <StageSection
              stageId="stage-2"
              isActive={currActiveStage === 'stage-2'}
              applicantsData={filteredApplicants}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onOpenApplicantPopup={onOpenApplicantPopup}
              userUID={userUID}
              dynamicSubstages={dynamicSubstages?.['stage-2'] || []}
              onOpenSubstagePopup={onOpenSubstagePopup}
              selectedApplicants={selectedApplicants}
              currentJobDepartment={selectedJobData?.data?.department || selectedJobData?.department || null}
              isOfferApprover={canReviewOffer}
              pendingApprovals={pendingApprovals}
              applicantStatusMap={applicantStatusMap}
              jobId={jobId}
              onSubstageUpdated={onSubstageUpdated}
              onSubstageDeleted={onSubstageDeleted}
              onMultipleApplicantsRemoved={onMultipleApplicantsRemoved}
            />

            {/* Stage 3 - Onboarding */}
            <StageSection
              stageId="stage-3"
              isActive={currActiveStage === 'stage-3'}
              applicantsData={filteredApplicants}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onOpenApplicantPopup={onOpenApplicantPopup}
              userUID={userUID}
              dynamicSubstages={dynamicSubstages?.['stage-3'] || []}
              onOpenSubstagePopup={onOpenSubstagePopup}
              selectedApplicants={selectedApplicants}
              currentJobDepartment={selectedJobData?.data?.department || selectedJobData?.department || null}
              isOfferApprover={canReviewOffer}
              pendingApprovals={pendingApprovals}
              applicantStatusMap={applicantStatusMap}
              jobId={jobId}
              onSubstageUpdated={onSubstageUpdated}
              onSubstageDeleted={onSubstageDeleted}
              onMultipleApplicantsRemoved={onMultipleApplicantsRemoved}
            />

            {/* Add Substage Area */}
            <AddSubstageArea
              currentStage={currActiveStage}
              onAddSubstage={onOpenAddSubstagePopup}
              onOpenFilteringSubstagePopup={onOpenFilteringSubstagePopup}
            />
          </div>
        );
      })()}

      {/* List View */}
      {viewMode === 'list' && (() => {
        return (
          <div
            style={{
              margin: '0 10%',
              width: '80%',
              overflowY: 'auto',
              flex: 1,
              paddingBottom: '40px'
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <div className="list-view-container">
              <table className="applicants-list-table">
                <thead>
                  <tr>
                    <th style={{ width: '10%', textAlign: 'center' }}>{t('workspace.selectShort')}</th>
                    <th style={{ width: '27%' }}>{t('applicantDetails.applicantName')}</th>
                    <th style={{ width: '23%' }}>{t('workspace.step')}</th>
                    <th style={{ width: '18%' }}>{t('workspace.dateApplied')}</th>
                    <th style={{ width: '16%' }}>{t('actions.moveToStage')}</th>
                    <th style={{ width: '8%', textAlign: 'center' }}>{t('actions.viewProfile')}</th>
                  </tr>
                </thead>
                <tbody>
                  {listViewApplicants.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)' }}>
                        {t('noApplicants')}
                      </td>
                    </tr>
                  ) : (
                    listViewApplicants.map((applicant) => (
                      <ListViewRow
                        key={applicant.id}
                        applicant={applicant}
                        currentStageName={getApplicantStageName(applicant)}
                        dynamicSubstages={dynamicSubstages}
                        selectedJobData={selectedJobData}
                        onOpenApplicantPopup={onOpenApplicantPopup}
                        onMoveApplicant={handleListViewMove}
                        userUID={userUID}
                        isOfferApprover={canReviewOffer}
                        pendingApprovals={pendingApprovals}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Gallery View */}
      {viewMode === 'gallery' && (
        <div
          style={{
            margin: '0 10%',
            width: '80%',
            overflowY: 'auto',
            flex: 1,
            paddingBottom: '40px'
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseMove={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          {!galleryGroupByStep ? (
            /* Standard Gallery View - Single Grid */
            <div className="gallery-view-container">
              {applicantsData.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)',
                  fontFamily: 'Montserrat'
                }}>
                  <i className="fa-solid fa-users" style={{ fontSize: '48px', marginBottom: '20px', opacity: 0.3 }}></i>
                  <p style={{ fontSize: '18px', margin: 0 }}>
                    {t('workspace.noApplicantsForJob')}
                  </p>
                </div>
              ) : (
                (() => {
                  // Helper function to get step order
                  const getStepOrder = (substageId, stageId) => {
                    const stepIds = [
                      'all-applicants-1',
                      'filter-applicants',
                      'offer-sent',
                      'hired-applicants'
                    ];

                    const baseIndex = stepIds.indexOf(substageId);
                    if (baseIndex !== -1) return baseIndex;

                    // For dynamic substages, place between filter and offer
                    if (stageId === 'stage-1') return 1.5;
                    return 999;
                  };

                  // Apply optional FILTER first
                  let visibleApplicants = [...applicantsData];
                  if (galleryFilterBy === 'starred') {
                    visibleApplicants = visibleApplicants.filter(a => a.starred === true);
                  } else if (galleryFilterBy === 'applied_last_7') {
                    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
                    visibleApplicants = visibleApplicants.filter(a => (a.createdAt || 0) >= cutoff);
                  } else if (galleryFilterBy === 'applied_over_7') {
                    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
                    visibleApplicants = visibleApplicants.filter(a => (a.createdAt || 0) < cutoff);
                  } else if (galleryFilterBy === 'step' && galleryFilterStep) {
                    visibleApplicants = visibleApplicants.filter(a => a.substageId === galleryFilterStep);
                  }

                  // Sort applicants based on user selection
                  const sortedApplicants = !gallerySortBy ? visibleApplicants : visibleApplicants.sort((a, b) => {
                    let comparison = 0;

                    if (gallerySortBy === 'date') {
                      // Sort by date applied (createdAt) - original application date
                      const dateA = a.createdAt || 0;
                      const dateB = b.createdAt || 0;
                      comparison = dateA - dateB;
                    } else if (gallerySortBy === 'updated') {
                      // Sort by last updated (lastUpdated) - most recent activity
                      const updatedA = a.lastUpdated || a.createdAt || 0;
                      const updatedB = b.lastUpdated || b.createdAt || 0;
                      comparison = updatedA - updatedB;
                    } else if (gallerySortBy === 'step') {
                      // Sort by workflow step order
                      const orderA = getStepOrder(a.substageId, a.stageId);
                      const orderB = getStepOrder(b.substageId, b.stageId);
                      comparison = orderA - orderB;
                    }

                    // Apply sort order (ascending or descending)
                    return gallerySortOrder === 'asc' ? comparison : -comparison;
                  });

                  return sortedApplicants.map((applicant) => {
                    // Get stage name
                    const getSubstageName = (substageId, stageId) => {
                      const staticStages = {
                        'all-applicants-1': t('substages.allApplicants'),
                        'filter-applicants': t('substages.fitApplicants'),
                        'offer-sent': t('substages.offerSent'),
                        'hired-applicants': t('substages.hired'),
                        'move-to-onboarding': t('substages.moveToOnboarding')
                      };

                      if (staticStages[substageId]) return staticStages[substageId];

                      const allSubstages = dynamicSubstages[stageId] || [];
                      const substage = allSubstages.find(s => s.id === substageId);
                      return substage ? (substage.title || substage.name) : uiText("Unknown Stage");
                    };

                    const currentStageName = getSubstageName(applicant.substageId, applicant.stageId);

                    return (
                      <GalleryViewCard
                        key={applicant.id}
                        applicant={applicant}
                        currentStageName={currentStageName}
                        dynamicSubstages={dynamicSubstages}
                        selectedJobData={selectedJobData}
                        onOpenApplicantPopup={onOpenApplicantPopup}
                        onMoveApplicant={handleListViewMove}
                        userUID={userUID}
                      />
                    );
                  });
                })()
              )}
            </div>
          ) : (
            /* Grouped Gallery View - By Workflow Step */
            <div>
              {(() => {
                // Apply filters first
                let filteredApplicants = [...applicantsData];
                if (galleryFilterBy === 'starred') {
                  filteredApplicants = filteredApplicants.filter(a => a.starred === true);
                } else if (galleryFilterBy === 'applied_last_7') {
                  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
                  filteredApplicants = filteredApplicants.filter(a => (a.createdAt || 0) >= cutoff);
                } else if (galleryFilterBy === 'applied_over_7') {
                  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
                  filteredApplicants = filteredApplicants.filter(a => (a.createdAt || 0) < cutoff);
                } else if (galleryFilterBy === 'step' && galleryFilterStep) {
                  filteredApplicants = filteredApplicants.filter(a => a.substageId === galleryFilterStep);
                }

                // Group applicants by substage
                const groupedApplicants = {};

                filteredApplicants.forEach(applicant => {
                  const substageId = applicant.substageId || 'unknown';
                  if (!groupedApplicants[substageId]) {
                    groupedApplicants[substageId] = [];
                  }
                  groupedApplicants[substageId].push(applicant);
                });

                // Get helper functions
                const getSubstageName = (substageId) => {
                  const staticStages = {
                    'all-applicants-1': t('substages.allApplicants'),
                    'filter-applicants': t('substages.fitApplicants'),
                    'offer-sent': t('substages.offerSent'),
                    'hired-applicants': t('substages.hired'),
                    'move-to-onboarding': t('substages.moveToOnboarding')
                  };

                  if (staticStages[substageId]) return staticStages[substageId];

                  const allSubstages = Object.values(dynamicSubstages).flat();
                  const substage = allSubstages.find(s => s.id === substageId);
                  return substage ? (substage.title || substage.name) : uiText("Unknown Stage");
                };

                const getStepIconClass = (substageId) => {
                  // Check if it's a dynamic substage with a type
                  const allSubstages = Object.values(dynamicSubstages).flat();
                  const substage = allSubstages.find(s => s.id === substageId);

                  if (substage && substage.type) {
                    if (substage.type === 'ai-interview' && substage.deliveryType === 'two-way-voice') {
                      return 'fa-solid fa-robot';
                    }
                    const typeIconMap = {
                      'interview': 'fa-solid fa-calendar-day',
                      'assessment': 'fa-solid fa-gears',
                      'ai-assessment': 'fa-solid fa-robot',
                      'ai-interview': 'fa-solid fa-video',
                      'paperwork': 'fa-solid fa-file-signature',
                      'contact-info': 'fa-solid fa-address-card',
                      'logistics': 'fa-solid fa-boxes-packing',
                      'filtering': 'fa-solid fa-filter'
                    };
                    if (typeIconMap[substage.type]) return typeIconMap[substage.type];
                  }

                  // Static step icons
                  const staticIcons = {
                    'all-applicants-1': 'fa-solid fa-users',
                    'filter-applicants': 'fa-solid fa-filter',
                    'offer-sent': 'fa-solid fa-envelope-open-text',
                    'hired-applicants': 'fa-solid fa-user-check',
                  };

                  return staticIcons[substageId] || 'fa-solid fa-people-group';
                };

                // Define workflow order
                const workflowOrder = [
                  'all-applicants-1',
                  'filter-applicants',
                  ...((dynamicSubstages['stage-1'] || [])
                    .filter(s => s.id !== 'filter-applicants')
                    .map(s => s.id)),
                  'offer-sent',
                  'hired-applicants'
                ];

                // Sort groups by workflow order
                const sortedSubstageIds = Object.keys(groupedApplicants).sort((a, b) => {
                  const indexA = workflowOrder.indexOf(a);
                  const indexB = workflowOrder.indexOf(b);
                  return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                });

                return sortedSubstageIds.map(substageId => {
                  const applicantsInStage = groupedApplicants[substageId];
                  const stageName = getSubstageName(substageId);
                  const iconClass = getStepIconClass(substageId);
                  const isExpanded = expandedStages[substageId] !== false; // Default expanded

                  return (
                    <div key={substageId} className="gallery-stage-group">
                      {/* Stage Header */}
                      <div
                        className="gallery-stage-header"
                        onClick={() => setExpandedStages(prev => ({
                          ...prev,
                          [substageId]: !isExpanded
                        }))}
                      >
                        <div className="gallery-stage-header-left">
                          <i className={iconClass}></i>
                          <span className="gallery-stage-title">{stageName}</span>
                          <span className="gallery-stage-count">({applicantsInStage.length})</span>
                        </div>
                        <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                      </div>

                      {/* Stage Content - Gallery Grid */}
                      {isExpanded && (
                        <div className="gallery-view-container">
                          {applicantsInStage.map(applicant => (
                            <GalleryViewCard
                              key={applicant.id}
                              applicant={applicant}
                              currentStageName={stageName}
                              dynamicSubstages={dynamicSubstages}
                              selectedJobData={selectedJobData}
                              onOpenApplicantPopup={onOpenApplicantPopup}
                              onMoveApplicant={handleListViewMove}
                              userUID={userUID}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}

      {!showCompareModal && selectionActionMenuPosition && activeActionSelectionIds.length > 0 && (
        <div
          className={`selection-action-menu${showCompareSelectionPicker && activeActionSelectionIds.length !== MAX_COMPARE_CANDIDATES ? ' selection-action-menu--picker' : ''}`}
          style={{
            top: `${selectionActionMenuPosition.top}px`,
            left: `${selectionActionMenuPosition.left}px`
          }}
        >
          {/* Page 1 — main actions */}
          {!(showCompareSelectionPicker && activeActionSelectionIds.length !== MAX_COMPARE_CANDIDATES) && (
            <>
              <div className="selection-action-menu-header">
                <span className="selection-action-count">
                  {t('workspace.selectedCount', { count: activeActionSelectionIds.length })}
                </span>
              </div>
              <div className="selection-action-buttons">
                <button
                  type="button"
                  className="selection-action-btn selection-action-btn-danger"
                  onClick={handleRejectSelectedCandidates}
                  disabled={isSelectionActionLoading}
                >
                  <i className="fa-solid fa-xmark"></i>
                  {t('popup.reject')}
                </button>
                <button
                  type="button"
                  className="selection-action-btn selection-action-btn-primary"
                  onClick={handleOpenCompareFromSelection}
                  disabled={isSelectionActionLoading}
                  title={
                    activeActionSelectionIds.length === MAX_COMPARE_CANDIDATES
                      ? t('workspace.compareNow')
                      : t('workspace.compareChooseTwo')
                  }
                >
                  <i className="fa-solid fa-scale-balanced"></i>
                  {t('workspace.compareShort')}
                </button>
              </div>
            </>
          )}

          {/* Page 2 — compare picker (only when >2 selected) */}
          {showCompareSelectionPicker && activeActionSelectionIds.length !== MAX_COMPARE_CANDIDATES && comparePickerApplicants.length > 0 && (
            <div className="selection-compare-picker">
              {/* Sticky header */}
              <div className="selection-compare-picker-header">
                <button
                  type="button"
                  className="selection-compare-picker-back"
                  onClick={() => setShowCompareSelectionPicker(false)}
                >
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
                <div className="selection-compare-picker-search-wrap">
                  <i className="fa-solid fa-magnifying-glass selection-compare-picker-search-icon"></i>
                  <input
                    type="text"
                    className="selection-compare-picker-search"
                    placeholder={t('workspace.compareSearchPlaceholder')}
                    value={comparePickerSearch}
                    onChange={e => setComparePickerSearch(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              {/* Scrollable list */}
              <div className="selection-compare-picker-list">
                {comparePickerApplicants
                  .filter(c => {
                    if (!comparePickerSearch.trim()) return true;
                    const name = (c.name || c.username || '').toLowerCase();
                    return name.includes(comparePickerSearch.trim().toLowerCase());
                  })
                  .map((candidate) => {
                    const isChecked = compareDraftSelectionIds.includes(candidate.id);
                    const candidateName = candidate.name || candidate.username || t('workspace.candidate');
                    const candidateStage = getApplicantStageName(candidate);
                    return (
                      <button
                        key={candidate.id}
                        type="button"
                        className={`selection-compare-picker-row ${isChecked ? 'selection-compare-picker-row-active' : ''}`}
                        onClick={() => toggleCompareDraftSelection(candidate.id)}
                      >
                        <span className={`selection-compare-picker-check ${isChecked ? 'selection-compare-picker-check-active' : ''}`}>
                          {isChecked ? <i className="fa-solid fa-check"></i> : null}
                        </span>
                        <span className="selection-compare-picker-text">
                          <span className="selection-compare-picker-name">{candidateName}</span>
                          <span className="selection-compare-picker-meta">{candidateStage}</span>
                        </span>
                      </button>
                    );
                  })}
              </div>

              {/* Sticky footer */}
              <div className="selection-compare-picker-footer">
                <button
                  type="button"
                  className="selection-compare-proceed"
                  onClick={handleProceedCompareSelection}
                  disabled={compareDraftSelectionIds.length !== MAX_COMPARE_CANDIDATES}
                >
                  {t('workspace.compareProceed')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showCompareModal && (
        <div className="compare-modal-overlay" onClick={() => setShowCompareModal(false)}>
          <div className={`compare-modal-card${isCompareAILoading || isCompareLoading ? ' compare-modal-card--loading' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="compare-modal-header">
              <div className="compare-modal-title-block">
                <h3>{t('workspace.compareModalTitle')}</h3>
                <p className="compare-modal-subtitle">{t('workspace.compareModalSubtitle')}</p>
              </div>
              <button type="button" className="compare-modal-close" onClick={() => setShowCompareModal(false)}>&times;</button>
            </div>

            {(isCompareLoading || isCompareAILoading) ? (
              <div className="compare-ai-loader">
                <div className="compare-ai-dots">
                  <span /><span /><span />
                </div>
                <p>{t('workspace.compareAILoading')}</p>
              </div>
            ) : (
            <>
            {compareInsightCards.length > 0 && (
              <section className="compare-decision-band" aria-label={t('workspace.compareHiringRecommendation')}>
                {compareInsightCards.map((card) => (
                  <article key={card.key} className={`compare-insight-card ${card.tone}`}>
                    <span>{uiText(card.label)}</span>
                    <strong>{uiText(card.title)}</strong>
                    <p>{card.body}</p>
                  </article>
                ))}
              </section>
            )}

            <div className="compare-workspace">
              <CompareDecisionMatrix
                candidates={comparedApplicantInsights}
                rows={compareDecisionRows}
                t={t}
              />

              <section className="compare-candidate-card-grid">
                {comparedApplicantInsights.map((candidate) => (
                  <CompareCandidateScoreCard key={candidate.id} candidate={candidate} t={t} />
                ))}
              </section>

              <section className="compare-section-card">
                <div className="compare-section-heading">
                  <span>{t('workspace.compareEvidenceReviewHeader')}</span>
                  <h4>{t('workspace.compareCandidateReadHeader')}</h4>
                  <p>{t('workspace.compareEvidenceReviewSubtitle')}</p>
                </div>
                <div className="compare-evidence-grid">
                  {comparedApplicantInsights.map((candidate) => (
                    <CompareEvidenceCard key={candidate.id} candidate={candidate} t={t} />
                  ))}
                </div>
              </section>
            </div>

            </>
            )}
          </div>
        </div>
      )}

      {/* Custom List Edit Modal */}
      {showEditCustomList && (
        <div className="cv-upload-overlay">
          <div className="cv-upload-modal" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <div className="modal-header">
              <h2>{t('workspace.editCustomListTitle')}</h2>
              <button className="close-btn" onClick={() => setShowEditCustomList(false)}>&times;</button>
            </div>

            <div className="upload-content">
              <div className="form-group">
                <label>{t('workspace.editCustomList.listTitle')}</label>
                <input
                  type="text"
                  value={editListTitle}
                  onChange={(e) => setEditListTitle(e.target.value)}
                  placeholder={t('workspace.editCustomList.listTitlePlaceholder')}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('workspace.editCustomList.description')}</label>
                <input
                  type="text"
                  value={editListDescription}
                  onChange={(e) => setEditListDescription(e.target.value)}
                  placeholder={t('workspace.editCustomList.descriptionPlaceholder')}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('workspace.editCustomList.targetHireDate', 'Target hire date')}</label>
                <input
                  type="date"
                  value={editListTargetHireDate}
                  onChange={(e) => setEditListTargetHireDate(e.target.value)}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('workspace.editCustomList.jobDescription')}</label>
                <textarea
                  value={editListJobDescription}
                  onChange={(e) => setEditListJobDescription(e.target.value)}
                  placeholder={t('workspace.editCustomList.jobDescriptionPlaceholder')}
                  className="text-area"
                  rows="4"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-danger" onClick={handleDeleteCustomList}>
                {t('workspace.editCustomList.delete')}
              </button>
              <div>
                <button className="btn-secondary" onClick={() => setShowEditCustomList(false)}>
                  {t('workspace.cancel')}
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSaveCustomListChanges}
                  disabled={!editListTitle.trim()}
                >
                  {t('workspace.editCustomList.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Manager & Employee ID Modal */}
      {showAssignManager && (
        <div className="cv-upload-overlay">
          <div className="cv-upload-modal">
            <div className="modal-header">
              <h2>{t('workspace.assignManagerModal.title')}</h2>
              <button className="close-btn" onClick={closeAssignManagerModal}>&times;</button>
            </div>
            <div className="upload-content">
              <div className="form-group">
                <label>{t('workspace.assignManagerModal.employee')}</label>
                <input type="text" value={assignEmployeeName} disabled className="text-input" />
              </div>
              <div className="form-group">
                <label>{t('workspace.assignManagerModal.manager')}</label>
                {/* Searchable dropdown (integrated) */}
                <div ref={managerDropdownRef} style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder={t('workspace.assignManagerModal.managerSearchPlaceholder')}
                    value={managerSearch}
                    onFocus={() => setManagerDropdownOpen(true)}
                    onChange={(e) => {
                      setManagerSearch(e.target.value);
                      setManagerDropdownOpen(true);
                    }}
                    className="text-input"
                  />
                  {managerDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      maxHeight: '220px',
                      overflowY: 'auto',
                      zIndex: 999
                    }}>
                      <div
                        onMouseDown={() => {
                          setSelectedManagerId('');
                          // Keep the localized display label in the existing search
                          // state without changing the stable empty manager ID value.
                          setManagerSearch(t('workspace.assignManagerModal.none'));
                          setManagerDropdownOpen(false);
                        }}
                        style={{ padding: '8px 10px', cursor: 'pointer' }}
                      >
                        {t('workspace.assignManagerModal.none')}
                      </div>
                      {(() => {
                        const q = managerSearch.trim().toLowerCase();
                        const filtered = !q ? managerOptions : managerOptions.filter((m) => {
                          const hay = `${m.name || ''} ${m.employeeId || ''} ${m.id}`.toLowerCase();
                          return hay.includes(q);
                        });
                        return filtered.map(m => (
                          <div
                            key={m.id}
                            onMouseDown={() => {
                              setSelectedManagerId(m.id);
                              setManagerSearch(`${m.name}${m.employeeId ? ` (${m.employeeId})` : ''}`);
                              setManagerDropdownOpen(false);
                            }}
                            style={{ padding: '8px 10px', cursor: 'pointer' }}
                          >
                            {m.name}{m.employeeId ? ` (${m.employeeId})` : ''}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>{t('workspace.assignManagerModal.employeeId')}</label>
                <input
                  type="text"
                  value={enteredEmployeeId}
                  onChange={(e) => setEnteredEmployeeId(e.target.value)}
                  placeholder={t('workspace.assignManagerModal.employeeIdPlaceholder')}
                  className="text-input"
                />
              </div>
              <div className="form-group">
                <label>{t('workspace.assignManagerModal.startDate')}</label>
                <input
                  type="date"
                  value={assignStartDate}
                  onChange={(e) => setAssignStartDate(e.target.value)}
                  className="text-input"
                />
              </div>
              <div className="form-group">
                <label>{t('workspace.assignManagerModal.initialPtoBalance')}</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={assignPtoBalance}
                  onChange={(e) => setAssignPtoBalance(e.target.value)}
                  placeholder={t('workspace.assignManagerModal.ptoBalancePlaceholder')}
                  className="text-input"
                />
                <small style={{ color: '#666' }}>
                  {t('workspace.assignManagerModal.ptoHelperText')}
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeAssignManagerModal}>
                {t('workspace.assignManagerModal.skip')}
              </button>
              <button className="btn-primary" onClick={saveAssignManager}>
                {t('workspace.assignManagerModal.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Interview Scheduler */}
      {showBulkInterviewScheduler && bulkInterviewApplicants.length > 0 && (
        <BulkInterviewScheduler
          applicants={bulkInterviewApplicants}
          userUID={userUID}
          onClose={() => {
            setShowBulkInterviewScheduler(false);
            setBulkInterviewApplicants([]);
            setBulkInterviewSubstageData(null);
            if (onClearSelection) {
              onClearSelection();
            }
          }}
          onScheduleSuccess={handleBulkInterviewScheduleSuccess}
          jobTitle={currActiveJob}
          companyName=""
          substageTitle={bulkInterviewSubstageData?.title || 'Interview'}
          substageData={bulkInterviewSubstageData}
        />
      )}

      <LiveInterviewQuestionGateModal
        gate={liveQuestionGate}
        onCancel={() => setLiveQuestionGate(null)}
        onGenerate={generatePendingLiveQuestions}
        onConfirmMove={confirmLiveQuestionsAndMove}
        onEdit={editPendingLiveQuestions}
        onSaveEdit={savePendingLiveQuestionEdits}
        onCancelEdit={cancelPendingLiveQuestionEdits}
        onQuestionChange={updatePendingLiveQuestion}
      />

      {/* Legacy assessment generation remains separate from live interviews. */}
      <GeneratingAssessmentModal
        isOpen={showGeneratingModal}
        jobTitle={generatingJobTitle}
        onClose={() => setShowGeneratingModal(false)}
      />

      {/* Undo Toast */}
      {showUndoToast && undoData && (
        <div className="undo-toast">
          <div className="undo-toast-content">
            <i className="fa-solid fa-check-circle" style={{ color: 'var(--app-tone-4ade80, #4ade80)', fontSize: '20px' }}></i>
            <span className="undo-toast-message">
              {undoData.count === 1
                ? t('workspace.toast.movedOne')
                : t('workspace.toast.movedMany', { count: undoData.count })}
            </span>
            <button className="undo-toast-btn" onClick={handleUndo}>
              <i className="fa-solid fa-rotate-left"></i>
              {t('workspace.toast.undo')}
            </button>
            <button className="undo-toast-close" onClick={hideUndoToast}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* General Toast Notification */}
      {showToast && (
        <div className="undo-toast">
          <div className="undo-toast-content">
            <i className="fa-solid fa-exclamation-circle" style={{ color: 'var(--app-tone-f59e0b, #f59e0b)', fontSize: '20px' }}></i>
            <span className="undo-toast-message">
              {uiText(toastMessage)}
            </span>
            <button className="undo-toast-close" onClick={hideToast}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Bulk Assessment Sending Toast */}
      {isSendingBulkAssessments && (
        <div className="undo-toast">
          <div className="undo-toast-content">
            <i className="fa-solid fa-spinner fa-spin" style={{ color: '#568ded', fontSize: '20px' }}></i>
            <span className="undo-toast-message">
              {t('workspace.sendingAssessments', { count: bulkAssessmentCount })}
            </span>
          </div>
        </div>
      )}

      <OffersHub
	        actionBusyKey={offersHubActionBusy}
	        canDraftOffer={canDraftOffer}
	        canSendDraftToCandidate={canSendOfferToCandidate}
	        canSubmitDraftForApproval={mustSubmitForApproval}
	        isOfferApprover={canReviewOffer}
	        isOpen={showOffersHub}
	        loading={offersHubLoading}
	        offers={visibleOffersHubItems}
	        pendingApprovalByOfferId={pendingApprovalByOfferId}
	        onApproveOffer={handleApproveOfferFromHub}
	        onClose={() => setShowOffersHub(false)}
	        onOpenApplicant={handleOpenOfferApplicant}
	        onRejectOffer={handleRejectOfferFromHub}
	        onReviewOffer={handleReviewOfferFromHub}
	        onSaveDraftOffer={handleSaveDraftOfferFromHub}
	        onSendDraftOffer={handleSendDraftOfferFromHub}
	        onSubmitDraftForApproval={handleSubmitDraftOfferFromHub}
	      />

      {/* Offer Email Modal */}
      {showOfferEmailModal && (
        <div className="cv-upload-overlay">
          <div className={`cv-upload-modal offer-email-modal${offerFieldsReadOnly ? ' read-only' : ''}`} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
	            <div className="modal-header">
	              <h2>{offerReviewMode ? uiText("Review Offer") : t('workspace.offerModal.title')}</h2>
	              <button className="close-btn" onClick={() => {
	                setShowOfferEmailModal(false);
	                setOfferReviewMode(false);
	                setReviewApprovalData(null);
	                setActiveOfferId('');
	                setOfferContextListedSalary('');
	                setOfferRejectionReason('');
	              }}>&times;</button>
	            </div>

            <div className="upload-content">
              {!offerReviewMode && (
                <p className="offer-modal-intro">
                  {t('workspace.offerModal.description')}
                </p>
              )}

              {offerFieldsReadOnly && (
                <div className="offer-permission-notice">
                  <i className="fa-solid fa-lock" />
                  {offerReviewMode
                    ? t('workspace.offerModal.reviewPermissionNotice', 'You can view this offer, but you do not have permission to approve or reject it.')
                    : t('workspace.offerModal.draftPermissionNotice', 'You can view this offer, but you do not have permission to draft or send offers.')}
                </div>
              )}

              <fieldset className="offer-modal-fields" disabled={offerFieldsReadOnly}>
              <div className="offer-top-grid">
                <div className="offer-field">
                  <label>{t('workspace.offerModal.to')}</label>
                  <div className="offer-recipient-list">
                    {offerModalApplicants.map((applicant) => (
                      <span key={applicant.id} className="offer-recipient-pill">
                        <i className="fa-solid fa-user" />
                        {applicant.name || applicant.username || uiText("Candidate")}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="offer-field">
                  <label>{t('workspace.offerModal.subject')}</label>
                  <input
                    type="text"
                    value={offerSubject}
                    onChange={(e) => setOfferSubject(e.target.value)}
                    placeholder={t('workspace.offerModal.subjectPlaceholder')}
                    className="text-input"
                  />
                </div>
              </div>

              <section className="offer-section">
                <div className="offer-section-title">
                  <i className="fa-solid fa-money-check-dollar" />
                  <div>
                    <h3>{t('workspace.offerModal.compensationTitle', 'Compensation')}</h3>
                    <span>{t('workspace.offerModal.compensationHint', 'Keep the primary pay terms together.')}</span>
                  </div>
                </div>
                <div className="offer-field-grid">
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.baseSalary', 'Base salary')}</label>
                    <input
                      type="text"
                      value={offerTerms.baseSalary}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, baseSalary: e.target.value }))}
                      placeholder={t('workspace.offerModal.baseSalaryPlaceholder', 'e.g., 120,000')}
                      className="text-input"
                    />
                    {offerContextListedSalary && (
                      <p className="offer-listed-salary-hint">
                        {t('workspace.offerModal.listedSalaryHint', {
                          salary: offerContextListedSalary,
                          defaultValue: 'Listed salary on job post: {{salary}}'
                        })}
                      </p>
                    )}
                  </div>
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.currency', 'Currency')}</label>
                    <select
                      value={offerTerms.currency}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, currency: e.target.value }))}
                      className="text-input"
                    >
                      <option value="USD">USD</option>
                      <option value="CAD">CAD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="AED">AED</option>
                      <option value="SAR">SAR</option>
                    </select>
                  </div>
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.payFrequency', 'Pay frequency')}</label>
                    <select
                      value={offerTerms.payFrequency}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, payFrequency: e.target.value }))}
                      className="text-input"
                    >
                      <option value="annual">{t('workspace.offerModal.annual', 'Annual')}</option>
                      <option value="monthly">{t('workspace.offerModal.monthly', 'Monthly')}</option>
                      <option value="hourly">{t('workspace.offerModal.hourly', 'Hourly')}</option>
                    </select>
                  </div>
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.bonus', 'Bonus')}</label>
                    <input
                      type="text"
                      value={offerTerms.bonus}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, bonus: e.target.value }))}
                      placeholder={t('workspace.offerModal.optionalAmount', 'Optional amount')}
                      className="text-input"
                    />
                  </div>
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.signOnBonus', 'Sign-on bonus')}</label>
                    <input
                      type="text"
                      value={offerTerms.signOnBonus}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, signOnBonus: e.target.value }))}
                      placeholder={t('workspace.offerModal.optionalAmount', 'Optional amount')}
                      className="text-input"
                    />
                  </div>
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.commission', 'Commission')}</label>
                    <input
                      type="text"
                      value={offerTerms.commission}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, commission: e.target.value }))}
                      placeholder={t('workspace.offerModal.optionalAmount', 'Optional amount')}
                      className="text-input"
                    />
                  </div>
                  <div className="offer-field offer-field-full">
                    <label>{t('workspace.offerModal.equity', 'Equity')}</label>
                    <input
                      type="text"
                      value={offerTerms.equity}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, equity: e.target.value }))}
                      placeholder={t('workspace.offerModal.equityPlaceholder', 'Equity, options, or grant details')}
                      className="text-input"
                    />
                  </div>
                </div>
              </section>

              <section className="offer-section">
                <div className="offer-section-title">
                  <i className="fa-solid fa-briefcase" />
                  <div>
                    <h3>{t('workspace.offerModal.workSetupTitle', 'Work setup')}</h3>
                    <span>{t('workspace.offerModal.workSetupHint', 'Employment and location expectations.')}</span>
                  </div>
                </div>
                <div className="offer-field-grid">
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.employmentType', 'Employment type')}</label>
                    <select
                      value={offerTerms.employmentType}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, employmentType: e.target.value }))}
                      className="text-input"
                    >
                      <option value="Full-time">{t('workspace.offerModal.fullTime', 'Full-time')}</option>
                      <option value="Part-time">{t('workspace.offerModal.partTime', 'Part-time')}</option>
                      <option value="Contract">{t('workspace.offerModal.contract', 'Contract')}</option>
                      <option value="Internship">{t('workspace.offerModal.internship', 'Internship')}</option>
                    </select>
                  </div>
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.workMode', 'Work mode')}</label>
                    <select
                      value={offerTerms.workMode}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, workMode: e.target.value }))}
                      className="text-input"
                    >
                      <option value="On-site">{t('workspace.offerModal.onSite', 'On-site')}</option>
                      <option value="Hybrid">{t('workspace.offerModal.hybrid', 'Hybrid')}</option>
                      <option value="Remote">{t('workspace.offerModal.remote', 'Remote')}</option>
                    </select>
                  </div>
                </div>
              </section>

              <section className={`offer-section ${offerTimelineError ? 'has-warning' : ''}`}>
                <div className="offer-section-title">
                  <i className="fa-solid fa-calendar-check" />
                  <div>
                    <h3>{t('workspace.offerModal.timelineTitle', 'Offer timeline')}</h3>
                    <span>{t('workspace.offerModal.timelineHint', 'Candidate response comes before the first day.')}</span>
                  </div>
                </div>
                <div className="offer-field-grid">
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.expiresAt', 'Offer expires')}</label>
                    <input
                      type="date"
                      value={offerTerms.expiresAt}
                      max={offerTerms.startDate || undefined}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, expiresAt: e.target.value }))}
                      className="text-input"
                    />
                  </div>
                  <div className="offer-field">
                    <label>{t('workspace.offerModal.startDate', 'Start date')}</label>
                    <input
                      type="date"
                      value={offerTerms.startDate}
                      min={offerTerms.expiresAt || undefined}
                      onChange={(e) => setOfferTerms((prev) => ({ ...prev, startDate: e.target.value }))}
                      className="text-input"
                    />
                  </div>
                </div>
                {offerTimelineError && (
                  <div className="offer-timeline-warning">
                    <i className="fa-solid fa-triangle-exclamation" />
                    {offerTimelineError}
                  </div>
                )}
              </section>

              <div className="form-group">
                <div className="offer-template-row">
                  <label>{t('workspace.offerModal.message')}</label>
                  {!offerReviewMode && (
                    <div className="offer-template-actions">
                      <button type="button" className="btn-secondary" onClick={applySavedOfferTemplate}>
                        {t('workspace.offerModal.applyTemplate', 'Apply template')}
                      </button>
                      <button type="button" className="btn-secondary" onClick={saveCurrentOfferTemplate}>
                        {t('workspace.offerModal.saveTemplate', 'Save as template')}
                      </button>
                    </div>
                  )}
                </div>
                <div ref={offerEditorRef} className="offer-message-editor" />
              </div>

              {(mustSubmitForApproval || offerReviewMode) && (
                <div className="form-group">
                  <label>{t('workspace.offerModal.approvalNote', 'Approval note')}</label>
                  <textarea
                    value={offerApprovalNote}
                    onChange={(e) => setOfferApprovalNote(e.target.value)}
                    placeholder={t('workspace.offerModal.approvalNotePlaceholder', 'Add compensation rationale, exceptions, or context for the approver')}
                    className="text-input offer-textarea"
                  />
                </div>
              )}

              <label className="offer-followup-toggle">
                <input
                  type="checkbox"
                  checked={offerFollowUpsEnabled}
                  onChange={(e) => setOfferFollowUpsEnabled(e.target.checked)}
                />
                <span>{t('workspace.offerModal.followUps', 'Send automated follow-ups until the candidate responds')}</span>
              </label>

              {offerFollowUpsEnabled && (
                <section className="offer-section offer-followup-settings">
                  <div className="offer-followup-list">
                    {offerFollowUps.map((followUp, index) => (
                      <div className="offer-followup-item" key={followUp.id}>
                        <div className="offer-followup-item-header">
                          <div className="offer-followup-day-control">
                            <label>{t('workspace.offerModal.followUpSendAfter', 'Send after')}</label>
                            <div>
                              <button
                                type="button"
                                className="offer-stepper-btn"
                                onClick={() => updateOfferFollowUp(followUp.id, {
                                  daysAfterSend: Math.max(1, (Number.parseInt(followUp.daysAfterSend, 10) || 1) - 1)
                                })}
                              >
                                <i className="fa-solid fa-minus" />
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={followUp.daysAfterSend}
                                onChange={(e) => updateOfferFollowUp(followUp.id, {
                                  daysAfterSend: Math.max(1, Number.parseInt(e.target.value, 10) || 1)
                                })}
                                className="text-input"
                              />
                              <button
                                type="button"
                                className="offer-stepper-btn"
                                onClick={() => updateOfferFollowUp(followUp.id, {
                                  daysAfterSend: (Number.parseInt(followUp.daysAfterSend, 10) || 0) + 1
                                })}
                              >
                                <i className="fa-solid fa-plus" />
                              </button>
                              <span>{t('workspace.offerModal.daysAfterSend', 'days')}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => removeOfferFollowUp(followUp.id)}
                            disabled={offerFollowUps.length <= 1}
                          >
                            {t('workspace.offerModal.remove')}
                          </button>
                        </div>
                        <div className="offer-field">
                          <label>{t('workspace.offerModal.followUpSubjectNumbered', 'Reminder {{number}} subject', { number: index + 1 })}</label>
                          <input
                            type="text"
                            value={followUp.subject}
                            onChange={(e) => updateOfferFollowUp(followUp.id, { subject: e.target.value })}
                            className="text-input"
                          />
                        </div>
                        <div className="offer-field">
                          <label>{t('workspace.offerModal.followUpMessageNumbered', 'Reminder {{number}} email', { number: index + 1 })}</label>
                          <div
                            ref={(node) => {
                              if (node) {
                                offerFollowUpEditorRefs.current[followUp.id] = node;
                              }
                            }}
                            className="offer-followup-editor"
                          />
                        </div>
                      </div>
                    ))}
                    <button type="button" className="btn-secondary offer-add-followup" onClick={addOfferFollowUp}>
                      <i className="fa-solid fa-plus" />
                      {t('workspace.offerModal.addReminder', 'Add reminder')}
                    </button>
                  </div>
                </section>
              )}

              {offerReviewMode && (
                <div className="form-group">
                  <label>{t('workspace.offerModal.rejectionReason', 'Rejection reason')}</label>
                  <textarea
                    value={offerRejectionReason}
                    onChange={(e) => setOfferRejectionReason(e.target.value)}
                    placeholder={t('workspace.offerModal.rejectionReasonPlaceholder', 'Required if rejecting this offer')}
                    className="text-input offer-textarea"
                  />
                </div>
              )}

              <div className="form-group">
                <label>{t('workspace.offerModal.attachments')}</label>
                <input className="offer-file-input" type="file" multiple onChange={handleOfferAttachmentChange} />

                {offerAttachments.length > 0 && (
                  <div className="offer-attachment-list">
                    {offerAttachments.map((att, idx) => (
                      <div key={`${att.name}-${idx}`} className="offer-attachment-chip">
                        <i className="fa-solid fa-paperclip" />
                        <span>{att.name}</span>
                        <button
                          className="btn-secondary"
                          onClick={() => handleRemoveOfferAttachment(idx)}
                        >
                          {t('workspace.offerModal.remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* PDF Inline Preview with Object tag fallback */}
                {offerReviewMode && offerAttachments.map((att, idx) => {
                  const isPDF = att.type?.includes('pdf') || att.name?.toLowerCase().endsWith('.pdf');
                  if (isPDF && att.content) {
                    return (
                      <div key={`preview-${idx}`} style={{ marginTop: '16px', height: '600px', border: '1px solid #e1e4e8', borderRadius: '8px', overflow: 'hidden' }}>
                        <object
                          data={`data:application/pdf;base64,${att.content}`}
                          type="application/pdf"
                          width="100%"
                          height="100%"
                          style={{ border: 'none', display: 'block' }}
                        >
                          <div style={{ padding: '20px', textAlign: 'center', background: '#f8f9fa' }}>
                            <p style={{ marginBottom: '10px' }}>{uiText("Preview not directly available.")}</p>
                            <a
                              href={`data:application/pdf;base64,${att.content}`}
                              download={att.name || 'document.pdf'}
                              style={{ color: '#568ded', textDecoration: 'underline', fontWeight: 600 }}
                            >{uiText("Download PDF")}</a>
                          </div>
                        </object>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
              </fieldset>
            </div>

            <div className="modal-footer">
              {offerReviewMode ? (
                <div className="offer-review-footer">
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setShowOfferEmailModal(false);
                      setOfferReviewMode(false);
                      setReviewApprovalData(null);
                      setActiveOfferId('');
                      setOfferRejectionReason('');
                    }}
                  >{uiText("Close")}</button>
                  {canReviewOffer && (
                    <div className="offer-modal-actions">
                      <button
                        className="btn-danger"
                        onClick={handleRejectOffer}
                        disabled={offerBusy}
                      >
                        {offerActionMode === 'rejecting' && <i className="fa-solid fa-spinner fa-spin" style={{ marginInlineEnd: '8px' }}></i>}
                        {offerReviewRejectLabel}
                      </button>
                      <button
                        className="btn-primary"
                        onClick={handleApproveOffer}
                        disabled={offerBusy}
                      >
                        {offerActionMode === 'approving' ? <i className="fa-solid fa-spinner fa-spin" style={{ marginInlineEnd: '8px' }}></i> : <i className="fa-solid fa-check" style={{ marginInlineEnd: '8px' }}></i>}
                        {offerReviewApproveLabel}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="offer-footer-stack">
                  <div className="offer-review-footer">
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setShowOfferEmailModal(false);
                        setActiveOfferId('');
                        setOfferRejectionReason('');
                      }}
                      disabled={offerBusy}
                    >
                      {t('workspace.offerModal.cancel')}
                    </button>
                    <div className="offer-modal-actions">
                      <button
                        className="btn-secondary"
                        onClick={handleSaveOfferDraft}
                        disabled={offerBusy || !canDraftOffer}
                      >
                        {offerActionMode === 'saving' && <i className="fa-solid fa-spinner fa-spin" style={{ marginInlineEnd: '8px' }}></i>}
                        {offerActionMode === 'saving' ? t('workspace.offerModal.saving', 'Saving...') : t('workspace.offerModal.saveDraft', 'Save draft')}
                      </button>
                      <button
                        className="btn-primary"
                        onClick={handleSendOfferEmail}
                        disabled={offerBusy || !canDraftOffer}
                      >
                        {offerActionMode === 'submitting' || offerActionMode === 'sending' ? (
                          <i className="fa-solid fa-spinner fa-spin" style={{ marginInlineEnd: '8px' }}></i>
                        ) : mustSubmitForApproval ? (
                          <i className="fa-solid fa-file-contract" style={{ marginInlineEnd: '8px' }}></i>
                        ) : (
                          <i className="fa-solid fa-paper-plane" style={{ marginInlineEnd: '8px' }}></i>
                        )}
                        {offerPrimaryLabel}
                      </button>
                    </div>
                  </div>
                  {!offerFieldsReadOnly && (
                    <p className="offer-footer-helper">{offerFooterHelper}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Onboarding Transition Modal */}
      {showOnboardingTransition && (
        <div className="cv-upload-overlay">
          <div className="cv-upload-modal" data-tutorial="onboarding-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>{t('workspace.onboardingModal.title')}</h2>
              <button className="close-btn" onClick={() => {
                setShowOnboardingTransition(false);
                setOnboardingOfferContext(null);
              }}>&times;</button>
            </div>

            <div className="upload-content">
	              <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
	                {t('workspace.onboardingModal.description')}
	              </p>

	              <div style={{
	                marginBottom: '16px',
	                padding: '12px',
	                borderRadius: '8px',
	                background: onboardingOfferContext?.acceptedOfferAt ? '#f0fdf4' : '#fff7ed',
	                border: `1px solid ${onboardingOfferContext?.acceptedOfferAt ? '#bbf7d0' : '#fed7aa'}`,
	                color: onboardingOfferContext?.acceptedOfferAt ? 'var(--app-tone-166534, #166534)' : 'var(--app-tone-9a3412, #9a3412)',
	                fontSize: '13px',
	                lineHeight: 1.45
	              }}>
		                {onboardingOfferContext ? (
		                  <>
		                    <strong>{t('workspace.onboardingModal.linkedOffer')}</strong> {onboardingOfferContext.offerId}
		                    <br />
		                    {onboardingOfferContext.acceptedOfferAt
		                      ? t('workspace.onboardingModal.acceptedAt', {
		                        date: new Date(onboardingOfferContext.acceptedOfferAt).toLocaleString(isRTL ? 'ar' : 'en-US')
		                      })
		                      : t('workspace.onboardingModal.acceptanceTimestampMissing')}
		                  </>
		                ) : (
		                  <>
		                    <strong>{t('workspace.onboardingModal.noLinkedOfferTitle')}</strong>{' '}
		                    {t('workspace.onboardingModal.noLinkedOfferMessage')}
		                  </>
		                )}
	              </div>

              <div className="form-group">
                <label>{t('workspace.onboardingModal.name')}</label>
                <input
                  type="text"
                  value={onboardingForm.name}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, name: e.target.value })}
                  placeholder={t('workspace.onboardingModal.namePlaceholder')}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('workspace.onboardingModal.email')}</label>
                <input
                  type="email"
                  value={onboardingForm.email}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, email: e.target.value })}
                  placeholder={t('workspace.onboardingModal.emailPlaceholder')}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('workspace.onboardingModal.role')}</label>
                <input
                  type="text"
                  value={onboardingForm.role}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, role: e.target.value })}
                  placeholder={t('workspace.onboardingModal.rolePlaceholder')}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('workspace.onboardingModal.department')}</label>
                <select
                  value={onboardingForm.department}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, department: e.target.value })}
                  className="text-input"
                >
                  <option value="">{t('workspace.onboardingModal.departmentPlaceholder')}</option>
                  {ONBOARDING_DEPARTMENT_OPTIONS.map(([value, translationKey]) => (
                    <option key={value} value={value}>
                      {t(`workspace.onboardingModal.departments.${translationKey}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('workspace.onboardingModal.startDate')}</label>
                <input
                  type="date"
                  value={onboardingForm.startDate}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, startDate: e.target.value })}
                  className="text-input"
                />
              </div>

              {onboardingEstablishments.length > 1 && (
                <div className="form-group">
                  <label>{t('workspace.onboardingModal.legalEstablishment')}</label>
                  <select
                    value={onboardingEstablishmentId}
                    onChange={(event) => setOnboardingEstablishmentId(event.target.value)}
                    className="text-input"
                    required
                  >
                    <option value="">{t('workspace.onboardingModal.selectLegalEstablishment')}</option>
                    {onboardingEstablishments.map((establishment) => (
                      <option key={establishment.id} value={establishment.id}>
                        {establishment.name || establishment.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>{t('workspace.onboardingModal.manager')}</label>
                <input
                  type="text"
                  value={onboardingForm.manager}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, manager: e.target.value })}
                  placeholder={t('workspace.onboardingModal.managerPlaceholder')}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>{t('workspace.onboardingModal.buddy')}</label>
                <input
                  type="text"
                  value={onboardingForm.buddyMentor}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, buddyMentor: e.target.value })}
                  placeholder={t('workspace.onboardingModal.buddyPlaceholder')}
                  className="text-input"
                />
              </div>
            </div>

            <div className="modal-footer">
	              <button
	                className="btn-secondary"
	                onClick={() => {
	                  setShowOnboardingTransition(false);
	                  setOnboardingOfferContext(null);
	                }}
	              >
                {t('workspace.onboardingModal.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmitOnboarding}
              >
                <i className="fa-solid fa-check" style={{ marginInlineEnd: '8px' }}></i>
                {t('workspace.onboardingModal.add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ApplicantsWorkspace; 
